import {
  useCallback,
  useEffect,
  useLayoutEffect,
  memo,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type FormEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowLeft,
  AtSign,
  BarChart2,
  Bell,
  Bookmark,
  Bold,
  Check,
  ChevronDown,
  ChevronRight,
  Code,
  Copy,
  Edit3,
  FileCode,
  FileText,
  Hash,
  Image,
  Info,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Lock,
  LogOut,
  MessageCircle,
  Mic,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Pause,
  Pin,
  Play,
  Plus,
  Quote,
  Search,
  Send,
  Settings,
  Square,
  Smile,
  SmilePlus,
  Strikethrough,
  Trash2,
  Underline,
  UploadCloud,
  UserMinus,
  Users,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/common/Avatar';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { useSocket } from '@/providers/SocketProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/features/settings/hooks/useMe';
import { useFullscreenShortcut } from '@/hooks/useFullscreenShortcut';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import {
  channelKeys,
  useChannelMembers,
  useChannelCreationMembers,
  useChannelMessages,
  useChannelMessagesAround,
  useChannels,
  useChatPeople,
  useCreateChannel,
  useCreateDirectChannel,
  useDeleteChannelMessage,
  useEditChannelMessage,
  useEligibleChannelMembers,
  useMarkChannelRead,
  useRemoveChannelMember,
  useSendChannelMessage,
  useTogglePin,
  useToggleReaction,
  useUpdateChannel,
  useUpdateChannelMember,
  useVoteChannelPoll,
  useArchiveChannel,
  useRestoreChannel,
  usePresence,
  applyPresenceUpdate,
  useChangeMemberRole,
  useRemoveMemberFromChannel,
  useAddChannelMember,
  useMentionGroups,
  useChannelAudiences,
  useToggleSaved,
  applySavedChange,
} from '../hooks/useChannels';
import type {
  ChannelData,
  ChannelMemberData,
  ChannelMessageData,
  ChatUser,
  MentionGroup,
  MessageReactionData,
  MessageSearchResult,
  PresenceStatus,
  SavedMessage,
} from '@/api/services/channels.service';
import { getPinnedMessages } from '@/api/services/channels.service';
import { getSearchShortcutLabel } from '@/lib/keyboard';
import { MessageSearchDialog } from './MessageSearchDialog';
import { SavedMessagesDialog } from './SavedMessagesDialog';

// ─── Constants ────────────────────────────────────────────────────────────────
const REACTION_CHOICES = ['👍', '❤️', '😂', '🎉', '🔥', '👏', '✅', '👀', '🤔', '😄', '🙏', '🚀'];
const MESSAGE_EDIT_WINDOW_MS = 10 * 60 * 1000;
const MAX_CHANNEL_FILE_SIZE = 20 * 1024 * 1024;
const GROUP_GAP_MS = 5 * 60 * 1000;
const VOICE_PLAYBACK_WAVEFORM_HEIGHTS = [
  7, 11, 16, 9, 20, 13, 8, 17, 23, 12, 18, 10, 15, 21, 8, 14, 19, 11, 16, 24, 9, 13, 18, 7,
];
const EMPTY_RECORDING_WAVEFORM = Array.from({ length: 20 }, () => 3);

function buildVoiceWaveform(amplitudes: number[]): number[] {
  const barCount = 28;
  if (!amplitudes.length)
    return VOICE_PLAYBACK_WAVEFORM_HEIGHTS.map((height) => Math.round(height * 0.42));

  return Array.from({ length: barCount }, (_, index) => {
    const start = Math.floor((index / barCount) * amplitudes.length);
    const end = Math.max(start + 1, Math.floor(((index + 1) / barCount) * amplitudes.length));
    const peak = Math.max(...amplitudes.slice(start, end), 0);
    return Math.max(3, Math.min(18, Math.round(3 + Math.min(1, peak / 24) * 15)));
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function personOf(sender: ChannelMessageData['senderId']): ChatUser | null {
  return typeof sender === 'string' ? null : sender;
}

function displayChannelGroupLabel(channel: Pick<ChannelData, 'groupLabel' | 'groupType'>): string {
  const label = channel.groupLabel?.trim();
  // Channels created before the terminology update keep this stored label, and
  // organization-wide channels are stored as "All Members" by the API.
  // Normalize both at render time so the sidebar section reads "Channels".
  if (!label) return 'Channels';
  const normalized = label.toLowerCase();
  if (normalized === 'company channels' || normalized === 'all members') return 'Channels';
  return label;
}

/**
 * Centres a rendered message in its scroll container and flashes it. Returns
 * false when the message is not in the DOM (e.g. outside the loaded window).
 */
function revealMessage(container: HTMLElement, messageId: string): boolean {
  const target = container.querySelector<HTMLElement>(
    `[data-message-id="${CSS.escape(messageId)}"]`
  );
  if (!target) return false;
  const offset =
    target.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;
  container.scrollTop = Math.max(0, offset - container.clientHeight / 2 + target.offsetHeight / 2);
  target.classList.remove('chat-message-flash');
  // Force a reflow so jumping to the same message twice restarts the animation.
  void target.offsetWidth;
  target.classList.add('chat-message-flash');
  window.setTimeout(() => target.classList.remove('chat-message-flash'), 2600);
  return true;
}

function personName(sender: ChannelMessageData['senderId']): string {
  const p = personOf(sender);
  if (!p) return 'Former member';
  return `${p.firstName} ${p.lastName}`.trim() || p.email;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
    new Date(value)
  );
}

const PRESENCE_LABEL: Record<PresenceStatus, string> = {
  online: 'Online',
  idle: 'Away',
  offline: 'Offline',
};

/**
 * Status dot pinned to an avatar's corner. Offline shows nothing, so a dot
 * always means "around". `ringClassName` matches the surface behind it.
 */
function PresenceDot({
  status,
  ringClassName = 'ring-white dark:ring-gray-900',
}: {
  status?: PresenceStatus;
  ringClassName?: string;
}): React.ReactElement | null {
  if (!status || status === 'offline') return null;
  return (
    <span
      role="img"
      aria-label={PRESENCE_LABEL[status]}
      title={PRESENCE_LABEL[status]}
      className={cn(
        'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2',
        status === 'online' ? 'bg-emerald-500' : 'bg-amber-400',
        ringClassName
      )}
    />
  );
}

function formatDateDivider(value: string): string {
  const d = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

function fileIcon(mimeType: string): React.ReactElement {
  return mimeType.startsWith('image/') ? (
    <Image className="h-4 w-4" />
  ) : (
    <FileText className="h-4 w-4" />
  );
}

function formatAudioDuration(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0:00';
  const seconds = Math.floor(value);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function resolvedAudioDuration(audio: HTMLAudioElement): number {
  if (Number.isFinite(audio.duration) && audio.duration > 0) return audio.duration;
  if (audio.seekable.length > 0) {
    const seekableEnd = audio.seekable.end(audio.seekable.length - 1);
    if (Number.isFinite(seekableEnd) && seekableEnd > 0) return seekableEnd;
  }
  return 0;
}

function VoiceNotePlayer({
  src,
  compact = false,
  initialDuration,
  initialWaveform,
  onDiscard,
  onSend,
}: {
  src: string;
  compact?: boolean;
  initialDuration?: number;
  initialWaveform?: number[];
  onDiscard?: () => void;
  onSend?: () => void;
}): React.ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playbackFrameRef = useRef<number | null>(null);
  const durationProbeTimerRef = useRef<number | null>(null);
  const durationProbeStartedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration ?? 0);
  const waveform = initialWaveform?.length ? initialWaveform : VOICE_PLAYBACK_WAVEFORM_HEIGHTS;
  const canSeek = Number.isFinite(duration) && duration > 0;
  const progress = canSeek ? Math.min(100, (currentTime / duration) * 100) : 0;
  const completedBars = Math.round((progress / 100) * waveform.length);

  useEffect(() => {
    if (!isPlaying) return;
    const syncPlayback = () => {
      const audio = audioRef.current;
      if (!audio) return;
      setCurrentTime(audio.currentTime);
      const nextDuration = resolvedAudioDuration(audio);
      // The recorder duration is an immediate fallback. Once the browser has
      // parsed the encoded audio, its media duration is authoritative (WebM
      // can include a short encoder tail after the microphone is stopped).
      if (nextDuration > 0) setDuration(nextDuration);
      playbackFrameRef.current = window.requestAnimationFrame(syncPlayback);
    };
    syncPlayback();
    return () => {
      if (playbackFrameRef.current !== null) {
        window.cancelAnimationFrame(playbackFrameRef.current);
        playbackFrameRef.current = null;
      }
    };
  }, [initialDuration, isPlaying]);

  const updateDurationFromMetadata = (audio: HTMLAudioElement) => {
    const nextDuration = resolvedAudioDuration(audio);
    if (nextDuration > 0) {
      setDuration(nextDuration);
      return;
    }
    // Some WebM recordings omit a usable duration index. For legacy notes
    // without our persisted duration, probing the media seek range lets the
    // browser resolve the true end without requiring the user to press play.
    if (initialDuration || durationProbeStartedRef.current || !audio.paused) return;
    durationProbeStartedRef.current = true;
    const originalTime = audio.currentTime;
    let probeComplete = false;
    const finishProbe = () => {
      if (probeComplete) return;
      probeComplete = true;
      audio.removeEventListener('timeupdate', finishProbe);
      const probed = resolvedAudioDuration(audio) || audio.currentTime;
      if (Number.isFinite(probed) && probed > 0) setDuration(probed);
      if (audio.paused) audio.currentTime = originalTime;
      if (durationProbeTimerRef.current !== null) {
        window.clearTimeout(durationProbeTimerRef.current);
        durationProbeTimerRef.current = null;
      }
    };
    audio.addEventListener('timeupdate', finishProbe, { once: true });
    durationProbeTimerRef.current = window.setTimeout(finishProbe, 1_500);
    try {
      audio.currentTime = Number.MAX_SAFE_INTEGER;
    } catch {
      finishProbe();
    }
  };

  useEffect(
    () => () => {
      if (durationProbeTimerRef.current !== null) {
        window.clearTimeout(durationProbeTimerRef.current);
      }
    },
    []
  );

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        toast.error('Voice note could not be played. Please try again.');
      }
      return;
    }
    audio.pause();
  };

  const seekFromPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canSeek) return;
    const audio = audioRef.current;
    if (!audio) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-[14px] border border-slate-200/70 bg-slate-50/70 px-2.5 py-1.5 dark:border-white/[0.07] dark:bg-white/[0.035]',
        compact ? 'max-w-[16rem]' : 'max-w-[21rem]'
      )}
    >
      <audio
        ref={audioRef}
        preload="metadata"
        src={src}
        onLoadedMetadata={(event) => {
          updateDurationFromMetadata(event.currentTarget);
        }}
        onDurationChange={(event) => {
          updateDurationFromMetadata(event.currentTarget);
        }}
        onCanPlay={(event) => {
          updateDurationFromMetadata(event.currentTarget);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onError={() => toast.error('Voice note could not be played. Please try again.')}
      />
      <button
        type="button"
        onClick={() => void togglePlayback()}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-600 text-white transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 dark:bg-white dark:text-gray-900"
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <Pause className="h-3 w-3 fill-current" />
        ) : (
          <Play className="ml-0.5 h-3 w-3 fill-current" />
        )}
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div
          role="slider"
          tabIndex={canSeek ? 0 : -1}
          aria-label="Voice note progress"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(currentTime)}
          onPointerDown={(event) => {
            if (!canSeek) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            seekFromPointer(event);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) seekFromPointer(event);
          }}
          onKeyDown={(event) => {
            if (!canSeek) return;
            const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
            if (!direction) return;
            event.preventDefault();
            const audio = audioRef.current;
            if (!audio) return;
            const nextTime = Math.max(0, Math.min(duration, audio.currentTime + direction * 5));
            audio.currentTime = nextTime;
            setCurrentTime(nextTime);
          }}
          className={cn(
            'relative flex h-5 min-w-0 flex-1 items-center gap-[2px] rounded-sm outline-none',
            canSeek
              ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-slate-400/50'
              : 'cursor-default'
          )}
        >
          {waveform.map((height, index) => (
            <span
              key={`${height}-${index}`}
              className={cn(
                'min-w-[2px] flex-1 rounded-full transition-colors duration-150',
                index < completedBars
                  ? 'bg-slate-500 dark:bg-white/85'
                  : 'bg-slate-400/80 dark:bg-white/35'
              )}
              style={{
                height: initialWaveform?.length ? height : Math.max(3, Math.round(height * 0.42)),
              }}
            />
          ))}
          {canSeek && (
            <>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-1/2 z-10 h-px -translate-y-1/2 bg-slate-500/45 dark:bg-white/55"
                style={{ width: `${progress}%` }}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 z-20 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-600 shadow-[0_0_0_1px_rgba(255,255,255,0.9)] dark:bg-white dark:shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                style={{ left: `${progress}%` }}
              />
            </>
          )}
        </div>
        <span className="shrink-0 text-[10px] font-medium tabular-nums text-slate-400 dark:text-gray-500">
          {formatAudioDuration(currentTime)} / {formatAudioDuration(duration)}
        </span>
      </div>
      {onDiscard && (
        <button
          type="button"
          onClick={onDiscard}
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white/70 hover:text-danger dark:hover:bg-white/10"
          title="Discard voice note"
          aria-label="Discard voice note"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {onSend && (
        <button
          type="button"
          onClick={onSend}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-600 text-white transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 dark:bg-white dark:text-gray-900"
          title="Send voice note"
          aria-label="Send voice note"
        >
          <Send className="ml-[-1px] h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function MessageAttachment({
  attachment,
  compact = false,
}: {
  attachment: ChannelMessageData['attachments'][number];
  compact?: boolean;
}): React.ReactElement {
  if (attachment.mimeType.startsWith('audio/')) {
    return (
      <VoiceNotePlayer
        src={attachment.url}
        compact={compact}
        initialDuration={attachment.durationSeconds}
        initialWaveform={attachment.waveform}
      />
    );
  }
  if (attachment.mimeType.startsWith('image/')) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        aria-label="Open shared image"
        className={cn(
          'relative block overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm transition-all hover:border-primary/45 hover:shadow-md dark:border-white/10 dark:bg-white/[0.06]',
          compact ? 'max-w-[12rem]' : 'max-w-[22rem]'
        )}
      >
        <img
          src={attachment.url}
          alt={attachment.name || 'Shared image'}
          loading="lazy"
          className={cn('block h-auto w-full object-contain', compact ? 'max-h-44' : 'max-h-80')}
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'flex items-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-primary/35 hover:text-primary dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300',
        compact
          ? 'max-w-[15rem] gap-1.5 px-2 py-1.5 text-[11px]'
          : 'max-w-[18rem] gap-2 px-2.5 py-2 text-xs'
      )}
    >
      {fileIcon(attachment.mimeType)}
      <span className="truncate">{attachment.name}</span>
    </a>
  );
}

// ─── Poll Data Types & Parsing ────────────────────────────────────────────────
interface PollOption {
  id: string;
  text: string;
  votes: string[];
}

interface PollData {
  id: string;
  question: string;
  allowMultiple?: boolean;
  options: PollOption[];
}

function parsePoll(content: string): { poll: PollData | null; remaining: string } {
  if (!content) return { poll: null, remaining: '' };
  const prefix = '[POLL:';
  const startIdx = content.indexOf(prefix);
  if (startIdx === -1) return { poll: null, remaining: content };

  const endIdx = content.lastIndexOf(']');
  if (endIdx === -1 || endIdx <= startIdx + prefix.length) {
    return { poll: null, remaining: content };
  }

  const jsonStr = content.slice(startIdx + prefix.length, endIdx).trim();
  try {
    const poll = JSON.parse(jsonStr) as PollData;
    const remaining = (content.slice(0, startIdx) + content.slice(endIdx + 1)).trim();
    return { poll, remaining };
  } catch {
    return { poll: null, remaining: content };
  }
}

function PollCard({
  poll,
  myId,
  people = [],
  onVote,
}: {
  poll: PollData;
  myId?: string;
  people?: ChatUser[];
  onVote?: (optionId: string, updatedPoll: PollData) => void;
}): React.ReactElement {
  const totalVotes = poll.options.reduce((sum, opt) => sum + (opt.votes?.length || 0), 0);
  const isMultiple = Boolean(poll.allowMultiple);

  const handleToggleVote = (optionId: string) => {
    if (!myId || !onVote) return;
    let updatedOptions: PollOption[];

    if (!isMultiple) {
      // Single choice mode: clicking active vote removes it; clicking another option switches the vote!
      const currentVotedOpt = poll.options.find((opt) => opt.votes?.includes(myId));
      if (currentVotedOpt?.id === optionId) {
        // Unvote
        updatedOptions = poll.options.map((opt) => ({
          ...opt,
          votes: (opt.votes || []).filter((id) => id !== myId),
        }));
      } else {
        // Switch vote to this option
        updatedOptions = poll.options.map((opt) => ({
          ...opt,
          votes:
            opt.id === optionId
              ? [...(opt.votes || []).filter((id) => id !== myId), myId]
              : (opt.votes || []).filter((id) => id !== myId),
        }));
      }
    } else {
      // Multiple choice mode
      updatedOptions = poll.options.map((opt) => {
        const hasVoted = opt.votes?.includes(myId);
        const newVotes = hasVoted
          ? (opt.votes || []).filter((id) => id !== myId)
          : [...(opt.votes || []).filter((id) => id !== myId), myId];
        return { ...opt, votes: newVotes };
      });
    }

    onVote(optionId, { ...poll, options: updatedOptions });
  };

  // Find max votes to highlight leading option
  const maxVotes = Math.max(...poll.options.map((o) => o.votes?.length || 0), 0);

  return (
    <div className="my-3 max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-800/90">
      {/* Header bar */}
      <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-3 dark:border-white/5 dark:bg-white/[0.02]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-xs dark:bg-primary-light/20 dark:text-primary-light">
              <BarChart2 className="h-4 w-4" />
            </div>
            <div>
              <h4 className="text-[14px] font-bold leading-snug text-slate-900 dark:text-white">
                {poll.question}
              </h4>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-semibold',
                    isMultiple
                      ? 'bg-amber-100/80 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
                      : 'bg-primary/15 text-primary dark:bg-primary-light/15 dark:text-primary-light'
                  )}
                >
                  {isMultiple ? 'Multiple choices' : 'Single choice only'}
                </span>
                <span className="text-slate-400">•</span>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  {totalVotes} {totalVotes === 1 ? 'vote cast' : 'votes cast'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Options List */}
      <div className="space-y-3 p-4">
        {poll.options.map((opt) => {
          const count = opt.votes?.length || 0;
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const isVoted = myId ? opt.votes?.includes(myId) : false;
          const isLeading = count > 0 && count === maxVotes;

          // Resolve voter names and avatars
          const voters = (opt.votes || []).map((voterId) => {
            const isMe = myId && String(voterId) === String(myId);
            const person = people.find((p) => String(p._id) === String(voterId));
            const name = isMe
              ? 'You'
              : person
                ? `${person.firstName} ${person.lastName}`.trim() || person.email
                : 'Member';
            return { id: voterId, name, isMe, avatar: person?.avatar };
          });

          return (
            <div
              key={opt.id}
              className="group/opt rounded-xl border border-slate-200/80 bg-slate-50/40 p-1.5 transition-all dark:border-white/10 dark:bg-white/[0.02]"
            >
              <button
                type="button"
                onClick={() => handleToggleVote(opt.id)}
                className={cn(
                  'relative flex w-full items-center justify-between overflow-hidden rounded-lg px-3.5 py-2.5 text-left text-xs transition-all duration-200',
                  isVoted
                    ? 'bg-primary/10 font-semibold text-primary dark:bg-primary/20'
                    : 'bg-white hover:bg-slate-100/70 dark:bg-gray-800/80 dark:hover:bg-white/[0.06]'
                )}
              >
                {/* Animated Progress Bar fill */}
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 transition-all duration-500 ease-out',
                    isVoted
                      ? 'bg-primary/15 dark:bg-primary/25'
                      : isLeading
                        ? 'bg-amber-500/10 dark:bg-amber-500/20'
                        : 'bg-slate-200/50 dark:bg-white/5'
                  )}
                  style={{ width: `${pct}%` }}
                />

                {/* Left: Indicator + Option title */}
                <div className="relative z-10 flex min-w-0 items-center gap-2.5">
                  {isMultiple ? (
                    // Checkbox indicator for multiple choice
                    <div
                      className={cn(
                        'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border transition-all duration-150',
                        isVoted
                          ? 'border-primary bg-primary text-white shadow-xs'
                          : 'border-slate-300 bg-white group-hover/opt:border-primary/50 dark:border-white/20 dark:bg-gray-800'
                      )}
                    >
                      {isVoted && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                  ) : (
                    // Radio circle indicator for single choice
                    <div
                      className={cn(
                        'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border transition-all duration-150',
                        isVoted
                          ? 'border-primary bg-primary text-white shadow-xs'
                          : 'border-slate-300 bg-white group-hover/opt:border-primary/50 dark:border-white/20 dark:bg-gray-800'
                      )}
                    >
                      {isVoted && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </div>
                  )}
                  <span
                    className={cn(
                      'truncate text-[13px] font-medium leading-normal',
                      isVoted
                        ? 'text-primary font-semibold dark:text-white'
                        : 'text-slate-800 dark:text-gray-200'
                    )}
                  >
                    {opt.text}
                  </span>
                </div>

                {/* Right: Vote count badge */}
                <div className="relative z-10 shrink-0 pl-2 text-right">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold transition-colors',
                      isVoted
                        ? 'bg-primary text-white shadow-2xs'
                        : count > 0
                          ? 'bg-slate-200/80 text-slate-700 dark:bg-white/10 dark:text-gray-200'
                          : 'text-slate-400 dark:text-gray-500'
                    )}
                  >
                    {count} {count === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
              </button>

              {/* Professional Voters Display Ribbon */}
              {voters.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 px-2 pb-1 pt-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-400">
                    Voted:
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {voters.map((voter) => (
                      <span
                        key={voter.id}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-1 pr-2 text-[11px] transition-colors',
                          voter.isMe
                            ? 'border-primary/30 bg-primary/10 font-semibold text-primary dark:bg-primary/20 dark:text-white'
                            : 'border-slate-200 bg-white font-medium text-slate-700 shadow-2xs dark:border-white/10 dark:bg-gray-800 dark:text-gray-200'
                        )}
                      >
                        <Avatar
                          name={voter.name}
                          src={voter.avatar}
                          size="xs"
                          className="h-4 w-4 rounded-full text-[8px]"
                        />
                        <span>{voter.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-4 py-2 text-[11px] text-slate-400 dark:border-white/5 dark:bg-white/[0.01] dark:text-gray-400">
        <span>Click option to vote or switch</span>
        <span className="font-medium text-slate-500 dark:text-gray-400">Kaamco Polls</span>
      </div>
    </div>
  );
}

// ─── Markdown Inline & Block Formatting ───────────────────────────────────────
function renderInlineFormatting(text: string): React.ReactNode[] {
  const regex =
    /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|`[^`]+`|\*[^*]+\*|_[^_]+_|\[[^\]]+\]\([^)]+\)|@[A-Za-z0-9_.-]+(?:\s[A-Za-z0-9_.-]+)?)/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (!part) return null;

    // **Bold**
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return (
        <strong key={i} className="font-semibold text-slate-900 dark:text-white">
          {renderInlineFormatting(part.slice(2, -2))}
        </strong>
      );
    }
    // __Underline__
    if (part.startsWith('__') && part.endsWith('__') && part.length >= 4) {
      return (
        <span key={i} className="underline underline-offset-2">
          {renderInlineFormatting(part.slice(2, -2))}
        </span>
      );
    }
    // ~~Strikethrough~~
    if (part.startsWith('~~') && part.endsWith('~~') && part.length >= 4) {
      return (
        <del key={i} className="line-through opacity-70">
          {renderInlineFormatting(part.slice(2, -2))}
        </del>
      );
    }
    // `Inline code`
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return (
        <code
          key={i}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-pink-600 dark:bg-white/10 dark:text-pink-400"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    // *Italic* or _Italic_
    if (
      (part.startsWith('*') && part.endsWith('*') && part.length >= 2) ||
      (part.startsWith('_') && part.endsWith('_') && part.length >= 2)
    ) {
      return (
        <em key={i} className="italic">
          {renderInlineFormatting(part.slice(1, -1))}
        </em>
      );
    }
    // [link text](url)
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline hover:text-primary/80"
        >
          {linkMatch[1]}
        </a>
      );
    }
    // @Mention
    if (part.startsWith('@')) {
      return (
        <span
          key={i}
          className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.2 font-medium text-primary dark:bg-primary/20"
        >
          {part}
        </span>
      );
    }

    return part;
  });
}

function MessageMarkdownContent({
  content,
  myId,
  people = [],
  onVote,
}: {
  content: string;
  myId?: string;
  people?: ChatUser[];
  onVote?: (optionId: string, poll: PollData) => void;
}): React.ReactElement {
  const { poll, remaining } = parsePoll(content);
  const codeBlockRegex = /(```[\s\S]*?```)/g;
  const chunks = remaining.split(codeBlockRegex);

  return (
    <div className="text-[13px] leading-[1.5] text-slate-800 dark:text-gray-200">
      {poll && <PollCard poll={poll} myId={myId} people={people} onVote={onVote} />}

      {chunks.map((chunk, idx) => {
        if (!chunk) return null;

        if (chunk.startsWith('```') && chunk.endsWith('```')) {
          const inner = chunk.slice(3, -3);
          const firstLineBreak = inner.indexOf('\n');
          const language = firstLineBreak !== -1 ? inner.slice(0, firstLineBreak).trim() : '';
          const codeContent = firstLineBreak !== -1 ? inner.slice(firstLineBreak + 1) : inner;

          return (
            <div
              key={idx}
              className="group relative my-2 overflow-hidden rounded-xl border border-slate-300 bg-slate-900 text-slate-100 dark:border-white/10 dark:bg-black/90"
            >
              <div className="flex items-center justify-between border-b border-slate-800 bg-slate-950/60 px-3 py-1 text-[11px] text-slate-400">
                <span className="font-mono uppercase">{language || 'code'}</span>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(codeContent.trim())
                      .then(() => toast.success('Code copied!'));
                  }}
                  className="rounded px-1.5 py-0.5 text-[10px] hover:bg-white/10 hover:text-white"
                >
                  Copy
                </button>
              </div>
              <pre className="overflow-x-auto p-3 font-mono text-[12px] leading-relaxed">
                <code>{codeContent.trim()}</code>
              </pre>
            </div>
          );
        }

        const lines = chunk.split('\n');
        const lineElements: React.ReactNode[] = [];
        let inQuote = false;
        let quoteLines: string[] = [];
        let listType: 'ordered' | 'unordered' | null = null;
        let listLines: string[] = [];

        const flushList = (key: string) => {
          if (!listType || listLines.length === 0) return;
          const items = listLines.map((item, itemIdx) => (
            <li key={`${key}-${itemIdx}`} className="pl-0.5">
              {renderInlineFormatting(item)}
            </li>
          ));
          lineElements.push(
            listType === 'ordered' ? (
              <ol key={key} className="my-1 list-decimal space-y-0.5 pl-5">
                {items}
              </ol>
            ) : (
              <ul key={key} className="my-1 list-disc space-y-0.5 pl-5">
                {items}
              </ul>
            )
          );
          listType = null;
          listLines = [];
        };

        const flushQuote = (key: string) => {
          if (quoteLines.length > 0) {
            lineElements.push(
              <blockquote
                key={key}
                className="my-1.5 border-l-2 border-primary/70 bg-primary/5 px-3 py-1 italic text-slate-700 dark:border-primary dark:bg-primary/10 dark:text-gray-300"
              >
                {quoteLines.map((ql, qidx) => (
                  <div key={qidx}>{renderInlineFormatting(ql)}</div>
                ))}
              </blockquote>
            );
            quoteLines = [];
            inQuote = false;
          }
        };

        lines.forEach((line, lineIdx) => {
          if (line.startsWith('> ')) {
            flushList(`list-${idx}-${lineIdx}`);
            inQuote = true;
            quoteLines.push(line.slice(2));
          } else {
            if (inQuote) flushQuote(`quote-${idx}-${lineIdx}`);
            const unorderedItem = line.match(/^\s*[-*+]\s+(.+)$/);
            const orderedItem = line.match(/^\s*\d+\.\s+(.+)$/);
            const nextListType = unorderedItem ? 'unordered' : orderedItem ? 'ordered' : null;
            const listContent = unorderedItem?.[1] ?? orderedItem?.[1];
            if (nextListType && listContent !== undefined) {
              if (listType && listType !== nextListType) {
                flushList(`list-${idx}-${lineIdx}`);
              }
              listType = nextListType;
              listLines.push(listContent);
            } else if (line.trim() === '') {
              flushList(`list-${idx}-${lineIdx}`);
              lineElements.push(<div key={`br-${idx}-${lineIdx}`} className="h-2" />);
            } else {
              flushList(`list-${idx}-${lineIdx}`);
              lineElements.push(
                <p key={`line-${idx}-${lineIdx}`} className="whitespace-pre-wrap break-words">
                  {renderInlineFormatting(line)}
                </p>
              );
            }
          }
        });
        if (inQuote) flushQuote(`quote-${idx}-end`);
        flushList(`list-${idx}-end`);

        return <div key={idx}>{lineElements}</div>;
      })}
    </div>
  );
}

function shouldGroup(a: ChannelMessageData, b: ChannelMessageData): boolean {
  const senderA = personOf(a.senderId)?._id ?? a.senderId;
  const senderB = personOf(b.senderId)?._id ?? b.senderId;
  if (senderA !== senderB) return false;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() < GROUP_GAP_MS;
}

// ─── Action Button (theme-aware) ───────────────────────────────────────────────
function ActionBtn({
  label,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }): React.ReactElement {
  return (
    <button
      {...props}
      title={label}
      aria-label={label}
      type="button"
      className={cn(
        'relative flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors',
        'hover:bg-slate-100 hover:text-slate-900',
        'dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className
      )}
    >
      {children}
    </button>
  );
}

// ─── Dropdown Menu Item ────────────────────────────────────────────────────────
function MenuItem({
  children,
  danger,
  onClick,
}: {
  children: React.ReactNode;
  danger?: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[12px] transition-colors',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/15'
          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white'
      )}
    >
      {children}
    </button>
  );
}

// ─── Message Hover Toolbar ─────────────────────────────────────────────────────
interface ToolbarProps {
  message: ChannelMessageData;
  canEdit: boolean;
  canDelete: boolean;
  canPin: boolean;
  onReact: (emoji: string) => void;
  onThread: () => void;
  onPin: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Omitted where saving is not offered. */
  onSave?: () => void;
}

function MessageToolbar({
  message,
  canEdit,
  canDelete,
  canPin,
  onReact,
  onSave,
  onThread,
  onPin,
  onEdit,
  onDelete,
}: ToolbarProps): React.ReactElement {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !pickerOpen) return;
    const close = (e: MouseEvent) => {
      if (menuOpen && !menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
      if (pickerOpen && !pickerRef.current?.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen, pickerOpen]);

  return (
    <div className="message-toolbar pointer-events-none absolute -top-4 right-3 z-20 flex translate-y-1 items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 opacity-0 shadow-md transition-all duration-100 group-hover/msg:pointer-events-auto group-hover/msg:translate-y-0 group-hover/msg:opacity-100 dark:border-white/10 dark:bg-gray-800">
      {/* Emoji picker */}
      <div className="relative" ref={pickerRef}>
        <ActionBtn label="Add reaction" onClick={() => setPickerOpen((o) => !o)}>
          <SmilePlus className="h-3.5 w-3.5" />
        </ActionBtn>
        {pickerOpen && (
          <div className="absolute right-0 top-[calc(100%+6px)] z-50 grid w-44 grid-cols-4 gap-0.5 rounded-xl border border-slate-200 bg-white p-2 shadow-xl animate-in fade-in zoom-in-95 duration-100 dark:border-white/10 dark:bg-gray-800">
            {REACTION_CHOICES.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onReact(emoji);
                  setPickerOpen(false);
                }}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                title={`React ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      <ActionBtn label="Reply in thread" onClick={onThread}>
        <MessageCircle className="h-3.5 w-3.5" />
      </ActionBtn>

      {onSave && (
        <ActionBtn label={message.saved ? 'Remove from saved' : 'Save message'} onClick={onSave}>
          <Bookmark
            className={cn(
              'h-3.5 w-3.5',
              message.saved && 'fill-current text-primary dark:text-primary-light'
            )}
          />
        </ActionBtn>
      )}

      {/* More actions */}
      <div className="relative" ref={menuRef}>
        <ActionBtn label="More actions" onClick={() => setMenuOpen((o) => !o)}>
          <MoreHorizontal className="h-3.5 w-3.5" />
        </ActionBtn>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-[calc(100%+6px)] z-50 w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-xl animate-in fade-in zoom-in-95 duration-100 dark:border-white/10 dark:bg-gray-800"
          >
            <MenuItem
              onClick={() => {
                void navigator.clipboard
                  .writeText(message.content)
                  .then(() => toast.success('Copied'));
                setMenuOpen(false);
              }}
            >
              Copy text
            </MenuItem>
            {canPin && (
              <MenuItem
                onClick={() => {
                  onPin();
                  setMenuOpen(false);
                }}
              >
                <Pin className="h-3.5 w-3.5" /> Pin message
              </MenuItem>
            )}
            {(canEdit || canDelete) && (
              <>
                <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
                {canEdit && (
                  <MenuItem
                    onClick={() => {
                      onEdit();
                      setMenuOpen(false);
                    }}
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit message
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem
                    danger
                    onClick={() => {
                      onDelete();
                      setMenuOpen(false);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete message
                  </MenuItem>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Single Message Bubble ────────────────────────────────────────────────────
interface BubbleProps {
  message: ChannelMessageData;
  isFirst: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPin: boolean;
  myId?: string;
  people?: ChatUser[];
  /** Who can be @mentioned when editing: the channel's current members. */
  mentionPeople?: ChatUser[];
  /** Archived channels: history only, no message actions. */
  readOnly?: boolean;
  onSave?: (message: ChannelMessageData) => void;
  onThread: (m: ChannelMessageData) => void;
  onReact: (messageId: string, emoji: string) => void;
  onPin: (messageId: string) => void;
  onEdit: (messageId: string, content: string, mentions?: EditMentions) => void;
  onDelete: (messageId: string) => void;
  onVote?: (messageId: string, optionId: string, poll: PollData) => void;
}

const MessageBubble = memo(function MessageBubble({
  message,
  isFirst,
  canEdit,
  canDelete,
  canPin,
  readOnly = false,
  onSave,
  myId,
  people = [],
  mentionPeople = people,
  onThread,
  onReact,
  onPin,
  onEdit,
  onDelete,
  onVote,
}: BubbleProps): React.ReactElement {
  const sender = personOf(message.senderId);
  const isDeleted = Boolean(message.deletedAt);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const isToday = new Date(message.createdAt).toDateString() === new Date().toDateString();

  // ── @mentions while editing (the edit box is plain text) ──
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  // The "@query" being typed: where it starts and ends in the draft.
  const [editMention, setEditMention] = useState<{
    query: string;
    start: number;
    end: number;
  } | null>(null);
  const [editMentionIndex, setEditMentionIndex] = useState(0);
  // Labels inserted from the popover ("@Ali Khan") and who/what they point to.
  const pickedMentionsRef = useRef(new Map<string, { kind: 'user' | 'role'; id: string }>());
  const { data: editGroups = [] } = useMentionGroups(
    editing ? message.channelId : null,
    Boolean(editMention)
  );
  const editQuery = editMention?.query.toLowerCase() ?? '';
  const editGroupOptions = editMention ? filterMentionGroups(editGroups, editQuery) : [];
  const editPeopleOptions = editMention
    ? mentionPeople.filter(
        (p) =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(editQuery) ||
          p.email.toLowerCase().includes(editQuery)
      )
    : [];
  const editOptionCount = editGroupOptions.length + editPeopleOptions.length;

  const detectEditMention = (value: string, caret: number) => {
    const match = value.slice(0, caret).match(/(?:^|\s)@([a-zA-Z0-9_.-]{0,20})$/);
    if (!match) {
      setEditMention(null);
      return;
    }
    setEditMention({ query: match[1], start: caret - match[1].length - 1, end: caret });
    setEditMentionIndex(0);
  };

  const insertEditMention = (label: string, kind: 'user' | 'role', id: string) => {
    if (!editMention) return;
    const text = `${label} `;
    const next = draft.slice(0, editMention.start) + text + draft.slice(editMention.end);
    const caret = editMention.start + text.length;
    pickedMentionsRef.current.set(label, { kind, id });
    setDraft(next);
    setEditMention(null);
    requestAnimationFrame(() => {
      editInputRef.current?.focus();
      editInputRef.current?.setSelectionRange(caret, caret);
    });
  };
  const pickEditPerson = (person: ChatUser) =>
    insertEditMention(
      `@${`${person.firstName} ${person.lastName}`.trim() || person.email}`,
      'user',
      person._id
    );
  const pickEditGroup = (group: MentionGroup) =>
    insertEditMention(`@${group.name}`, 'role', group.id);

  const stopEditing = () => {
    setDraft(message.content);
    setEditMention(null);
    pickedMentionsRef.current.clear();
    setEditing(false);
  };

  /** Keeps existing mentions and adds the ones picked here whose text survived. */
  const saveEdit = () => {
    const content = draft.trim();
    if (!content) return;
    const added = [...pickedMentionsRef.current].filter(([label]) => content.includes(label));
    const addedUsers = added.filter(([, pick]) => pick.kind === 'user').map(([, p]) => p.id);
    const addedRoles = added.filter(([, pick]) => pick.kind === 'role').map(([, p]) => p.id);
    onEdit(
      message._id,
      content,
      added.length
        ? {
            mentions: [...new Set([...(message.mentions ?? []), ...addedUsers])],
            mentionRoles: [...new Set([...(message.mentionRoles ?? []), ...addedRoles])],
          }
        : undefined
    );
    setEditMention(null);
    pickedMentionsRef.current.clear();
    setEditing(false);
  };

  // WhatsApp / Slack style centered system notice for join / leave / remove events
  const isSystemNotice =
    !isDeleted &&
    Boolean(message.content) &&
    message.content.startsWith('_') &&
    message.content.endsWith('_') &&
    (message.content.toLowerCase().includes('left the channel') ||
      message.content.toLowerCase().includes('removed from the channel') ||
      message.content.toLowerCase().includes('joined the channel'));

  if (isSystemNotice) {
    const cleanText = message.content.replace(/^_+|_+$/g, '').trim();
    return (
      <div
        data-is-today={isToday ? 'true' : undefined}
        data-message-sequence={message.sequence}
        data-message-id={message._id}
        className="my-3 flex items-center justify-center px-4"
      >
        <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-slate-100/90 px-3.5 py-1 text-[11px] font-medium text-slate-600 shadow-2xs dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400">
          <LogOut className="h-3 w-3 text-slate-400 dark:text-gray-500" />
          <span>{cleanText}</span>
          <span className="text-slate-300 dark:text-gray-600">•</span>
          <time className="text-[10px] text-slate-400 dark:text-gray-500">
            {formatTime(message.createdAt)}
          </time>
        </div>
      </div>
    );
  }

  return (
    <div
      data-is-today={isToday ? 'true' : undefined}
      data-message-sequence={message.sequence}
      data-message-id={message._id}
      className="group/msg relative flex gap-3 px-4 py-0.5 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.025]"
    >
      {/* Left gutter */}
      <div className="w-10 shrink-0 pt-0.5">
        {isFirst && <Avatar name={personName(message.senderId)} src={sender?.avatar} size="md" />}
        {!isFirst && message.saved && !isDeleted && (
          <span className="flex justify-end pr-1 pt-1" title="Saved">
            <Bookmark className="h-3 w-3 fill-current text-primary dark:text-primary-light" />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {isFirst && (
          <div className="mb-0.5 flex items-baseline gap-2">
            <span className="cursor-pointer text-[13px] font-semibold text-slate-900 hover:underline dark:text-gray-100">
              {personName(message.senderId)}
            </span>
            <time className="text-[11px] font-normal text-slate-400 dark:text-gray-500">
              {formatTime(message.createdAt)}
            </time>
            {message.editedAt && !isDeleted && (
              <span className="text-[10px] text-slate-400 dark:text-gray-500">(edited)</span>
            )}
            {message.saved && !isDeleted && (
              <span
                role="img"
                aria-label="Saved"
                title="Saved"
                className="inline-flex items-center self-center text-primary dark:text-primary-light"
              >
                <Bookmark className="h-3 w-3 fill-current" />
              </span>
            )}
            {message.deliveryState === 'sending' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 dark:text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Sending
              </span>
            )}
          </div>
        )}

        {isDeleted ? (
          <p className="text-[13px] italic text-slate-400 dark:text-gray-500">
            This message was deleted.
          </p>
        ) : editing ? (
          <div className="relative mt-1">
            <textarea
              ref={editInputRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                detectEditMention(e.target.value, e.target.selectionStart);
              }}
              rows={3}
              className="w-full resize-none rounded-lg border border-primary/40 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none ring-2 ring-primary/10 dark:bg-gray-800 dark:text-gray-100"
              onKeyDown={(e) => {
                // The @ popover gets the keys first: groups, then people.
                if (editMention && editOptionCount > 0) {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const step = e.key === 'ArrowDown' ? 1 : -1;
                    setEditMentionIndex((i) => (i + step + editOptionCount) % editOptionCount);
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    const index = editMentionIndex < editOptionCount ? editMentionIndex : 0;
                    if (index < editGroupOptions.length) pickEditGroup(editGroupOptions[index]);
                    else pickEditPerson(editPeopleOptions[index - editGroupOptions.length]);
                    return;
                  }
                }
                if (editMention && e.key === 'Escape') {
                  e.preventDefault();
                  setEditMention(null);
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveEdit();
                }
                if (e.key === 'Escape') stopEditing();
              }}
            />
            <MentionPopover
              open={Boolean(editMention)}
              people={mentionPeople}
              groups={editGroups}
              search={editMention?.query ?? ''}
              selectedIndex={editMentionIndex}
              onSelect={pickEditPerson}
              onSelectGroup={pickEditGroup}
              onClose={() => setEditMention(null)}
            />
            <div className="mt-1.5 flex gap-1.5 text-[11px] text-slate-500">
              escape to{' '}
              <button type="button" className="text-primary hover:underline" onClick={stopEditing}>
                cancel
              </button>{' '}
              • enter to{' '}
              <button type="button" className="text-primary hover:underline" onClick={saveEdit}>
                save
              </button>{' '}
              • @ to mention
            </div>
          </div>
        ) : (
          <>
            {message.content && (
              <MessageMarkdownContent
                content={message.content}
                myId={myId}
                people={people}
                onVote={(optionId, poll) => onVote?.(message._id, optionId, poll)}
              />
            )}
            {message.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.attachments.map((att) => (
                  <MessageAttachment key={att.key} attachment={att} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Reactions */}
        {!isDeleted && message.reactions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => onReact(message._id, r.emoji)}
                className={cn(
                  'inline-flex h-6 items-center gap-1 rounded-md border px-1.5 text-[11px] transition-all',
                  r.reacted
                    ? 'border-primary/35 bg-primary/10 text-primary'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300'
                )}
              >
                <span>{r.emoji}</span>
                <span className="font-medium">{r.count}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => onReact(message._id, '👍')}
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 bg-transparent text-slate-400 transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary dark:border-white/10 dark:text-gray-500"
              title="Add reaction"
            >
              <SmilePlus className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Thread reply button */}
        {!isDeleted && message.replyCount > 0 && (
          <button
            type="button"
            onClick={() => onThread(message)}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-primary transition-colors hover:border-primary/40 hover:bg-primary/5 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-primary/10"
          >
            <MessageCircle className="h-3.5 w-3.5 shrink-0" />
            <span>
              {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
            </span>
          </button>
        )}
      </div>

      {/* Hover toolbar */}
      {!isDeleted && !readOnly && (
        <MessageToolbar
          message={message}
          canEdit={canEdit}
          canDelete={canDelete}
          canPin={canPin}
          onReact={(emoji) => onReact(message._id, emoji)}
          onSave={onSave && message.deliveryState !== 'sending' ? () => onSave(message) : undefined}
          onThread={() => onThread(message)}
          onPin={() => onPin(message._id)}
          onEdit={() => setEditing(true)}
          onDelete={() => onDelete(message._id)}
        />
      )}
    </div>
  );
});

// ─── Date Divider ─────────────────────────────────────────────────────────────
function DateDivider({ date, isToday }: { date: string; isToday?: boolean }): React.ReactElement {
  const label = formatDateDivider(date);
  const todayLabel = isToday || label === 'Today';

  return (
    <div
      id={todayLabel ? 'channel-today-divider' : undefined}
      className="relative mx-4 my-4 flex items-center gap-3"
    >
      <div
        className={cn(
          'h-px flex-1',
          todayLabel ? 'bg-primary/25 dark:bg-primary/30' : 'bg-slate-200 dark:bg-white/10'
        )}
      />
      <span
        className={cn(
          'shrink-0 rounded-full border px-3 py-0.5 text-[11px] font-semibold transition-colors',
          todayLabel
            ? 'border-primary/30 bg-primary/10 text-primary shadow-xs dark:bg-primary/20 dark:text-primary-light'
            : 'border-slate-200 bg-white text-slate-500 dark:border-white/10 dark:bg-gray-800 dark:text-gray-400'
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          'h-px flex-1',
          todayLabel ? 'bg-primary/25 dark:bg-primary/30' : 'bg-slate-200 dark:bg-white/10'
        )}
      />
    </div>
  );
}

// ─── Message List with grouping ────────────────────────────────────────────────
interface MessageListProps {
  messages: ChannelMessageData[];
  myId?: string;
  people?: ChatUser[];
  /** Who can be @mentioned when editing: the channel's current members. */
  mentionPeople?: ChatUser[];
  canPin: boolean;
  canModerate: boolean;
  readOnly?: boolean;
  onSave?: (message: ChannelMessageData) => void;
  onThread: (m: ChannelMessageData) => void;
  onReact: (messageId: string, emoji: string) => void;
  onPin: (messageId: string) => void;
  onEdit: (messageId: string, content: string, mentions?: EditMentions) => void;
  onDelete: (messageId: string) => void;
  onVote?: (messageId: string, optionId: string, poll: PollData) => void;
}

function MessageList({
  messages,
  myId,
  people = [],
  mentionPeople,
  canPin,
  canModerate,
  readOnly = false,
  onSave,
  onThread,
  onReact,
  onPin,
  onEdit,
  onDelete,
  onVote,
}: MessageListProps): React.ReactElement {
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  // Re-render exactly when the next editable message expires, so the Edit
  // action disappears without relying on an unrelated message update.
  useEffect(() => {
    const nextExpiry = messages
      .filter(
        (message) =>
          (typeof message.senderId === 'string' ? message.senderId : message.senderId._id) ===
            myId && new Date(message.createdAt).getTime() + MESSAGE_EDIT_WINDOW_MS > currentTime
      )
      .map((message) => new Date(message.createdAt).getTime() + MESSAGE_EDIT_WINDOW_MS)
      .sort((a, b) => a - b)[0];
    if (!nextExpiry) return;
    const timeout = window.setTimeout(
      () => setCurrentTime(Date.now()),
      Math.max(0, nextExpiry - currentTime) + 1
    );
    return () => window.clearTimeout(timeout);
  }, [currentTime, messages, myId]);

  const isOwnMessage = (m: ChannelMessageData) =>
    (typeof m.senderId === 'string' ? m.senderId : m.senderId._id) === myId;
  const canEdit = (m: ChannelMessageData) =>
    isOwnMessage(m) && currentTime - new Date(m.createdAt).getTime() <= MESSAGE_EDIT_WINDOW_MS;

  const items: React.ReactNode[] = [];
  let lastDate = '';

  messages.forEach((msg, i) => {
    const msgDate = new Date(msg.createdAt).toDateString();
    const isToday = msgDate === new Date().toDateString();
    if (msgDate !== lastDate) {
      lastDate = msgDate;
      items.push(<DateDivider key={`date-${msg._id}`} date={msg.createdAt} isToday={isToday} />);
    }
    const isFirst = i === 0 || !shouldGroup(messages[i - 1], msg);
    items.push(
      <MessageBubble
        key={msg._id}
        message={msg}
        isFirst={isFirst}
        canEdit={canEdit(msg)}
        canDelete={isOwnMessage(msg) || canModerate}
        canPin={canPin}
        readOnly={readOnly}
        onSave={onSave}
        myId={myId}
        people={people}
        mentionPeople={mentionPeople}
        onThread={onThread}
        onReact={onReact}
        onPin={onPin}
        onEdit={onEdit}
        onDelete={onDelete}
        onVote={onVote}
      />
    );
  });

  return <div className="flex flex-col pb-2">{items}</div>;
}

// ─── Typing Indicator ─────────────────────────────────────────────────────────
function TypingIndicator({
  names,
  recordingNames = [],
}: {
  names: string[];
  recordingNames?: string[];
}): React.ReactElement | null {
  const isRecording = recordingNames.length > 0;
  const activeNames = isRecording ? recordingNames : names;
  if (activeNames.length === 0) return null;
  const label = isRecording
    ? activeNames.length === 1
      ? `${activeNames[0]} is recording a voice message`
      : activeNames.length <= 3
        ? `${activeNames.slice(0, -1).join(', ')} and ${activeNames.at(-1)} are recording voice messages`
        : `${activeNames.length} people are recording voice messages`
    : activeNames.length === 1
      ? `${activeNames[0]} is typing`
      : activeNames.length <= 3
        ? `${activeNames.slice(0, -1).join(', ')} and ${activeNames.at(-1)} are typing`
        : `${activeNames.length} people are typing`;
  return (
    <div className="flex h-6 items-center gap-1.5 px-5 pb-1">
      {isRecording ? (
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-50 text-red-500 animate-pulse dark:bg-red-500/15 dark:text-red-300">
          <Mic className="h-2.5 w-2.5" />
        </span>
      ) : (
        <span className="flex items-end gap-[3px] pb-0.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 dark:bg-gray-500"
              style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
            />
          ))}
        </span>
      )}
      <span className="text-[11px] italic text-slate-400 dark:text-gray-500">{label}…</span>
    </div>
  );
}

// ─── Mention Popover ─────────────────────────────────────────────────────────
// ─── Mention Popover ─────────────────────────────────────────────────────────
/** Mentions sent with an edit; omitted fields keep the message's existing ones. */
type EditMentions = { mentions?: string[]; mentionRoles?: string[] };

/** Shared by the popover and the composer's keyboard handling, so both agree on order. */
function filterMentionGroups(groups: MentionGroup[], search: string): MentionGroup[] {
  const q = search.toLowerCase();
  return groups.filter(
    (group) => group.name.toLowerCase().includes(q) || group.slug.toLowerCase().includes(q)
  );
}

function MentionPopover({
  open,
  people,
  groups = [],
  search = '',
  selectedIndex = 0,
  onSelect,
  onSelectGroup,
  onClose,
}: {
  open: boolean;
  people: ChatUser[];
  /** Roles mentionable here; listed before people. */
  groups?: MentionGroup[];
  search?: string;
  /** Index into groups-then-people, matching the composer's keyboard handling. */
  selectedIndex?: number;
  onSelect: (user: ChatUser) => void;
  onSelectGroup?: (group: MentionGroup) => void;
  onClose: () => void;
}): React.ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = people.filter((p) => {
    const full = `${p.firstName} ${p.lastName}`.toLowerCase();
    return (
      full.includes(search.toLowerCase()) || p.email.toLowerCase().includes(search.toLowerCase())
    );
  });
  const filteredGroups = onSelectGroup ? filterMentionGroups(groups, search) : [];

  return (
    <div
      ref={ref}
      className="absolute bottom-[calc(100%+8px)] left-4 z-50 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800"
    >
      <div className="mb-1.5 flex items-center justify-between px-2 py-1 border-b border-slate-100 dark:border-white/5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-400">
          {filteredGroups.length > 0 ? 'Mention a group or person' : 'Members in this channel'}
        </span>
        {search && (
          <span className="rounded bg-primary/10 px-1.5 py-0.2 text-[10px] font-medium text-primary dark:bg-primary/20">
            @{search}
          </span>
        )}
      </div>

      <div className="max-h-52 overflow-y-auto space-y-0.5 chat-scroll">
        {filteredGroups.length > 0 && (
          <>
            <p className="px-2.5 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-500">
              Groups
            </p>
            {filteredGroups.map((group, idx) => (
              <button
                key={group.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelectGroup?.(group);
                  onClose();
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs transition-colors',
                  idx === selectedIndex
                    ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10'
                )}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-gray-300">
                  <Users className="h-3 w-3" />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium">@{group.name}</span>
                  <span className="block truncate text-[10px] text-slate-400 dark:text-gray-400">
                    {group.memberCount} {group.memberCount === 1 ? 'person' : 'people'} in this
                    channel will be notified
                  </span>
                </div>
              </button>
            ))}
            {filtered.length > 0 && (
              <p className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-500">
                People
              </p>
            )}
          </>
        )}
        {filtered.length === 0
          ? filteredGroups.length === 0 && (
              <p className="py-4 text-center text-xs text-slate-400">
                No members matching &quot;@{search}&quot;
              </p>
            )
          : filtered.map((person, idx) => {
              const name = `${person.firstName} ${person.lastName}`.trim() || person.email;
              const isSelected = idx + filteredGroups.length === selectedIndex;
              return (
                <button
                  key={person._id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(person);
                    onClose();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs transition-colors',
                    isSelected
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-white'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10'
                  )}
                >
                  <Avatar name={name} src={person.avatar} size="xs" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="block truncate font-medium">{name}</span>
                    </div>
                    <span className="block truncate text-[10px] text-slate-400 dark:text-gray-400">
                      {person.employment?.department ?? person.email}
                    </span>
                  </div>
                </button>
              );
            })}
      </div>
    </div>
  );
}

// ─── Create Poll Modal ───────────────────────────────────────────────────────
interface CreatePollModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (pollContent: string) => void;
}

function CreatePollModal({ open, onClose, onCreated }: CreatePollModalProps): React.ReactElement {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);

  const handleAddOption = () => {
    if (options.length < 6) {
      setOptions((cur) => [...cur, '']);
    }
  };

  const handleOptionChange = (idx: number, val: string) => {
    setOptions((cur) => cur.map((opt, i) => (i === idx ? val : opt)));
  };

  const handleRemoveOption = (idx: number) => {
    if (options.length > 2) {
      setOptions((cur) => cur.filter((_, i) => i !== idx));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validOptions = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || validOptions.length < 2) {
      toast.error('Enter a question and at least 2 options');
      return;
    }
    const pollData: PollData = {
      id: crypto.randomUUID(),
      question: question.trim(),
      allowMultiple,
      options: validOptions.map((text, i) => ({ id: String(i + 1), text, votes: [] })),
    };
    const pollContent = `[POLL:${JSON.stringify(pollData)}]`;
    onCreated(pollContent);
    setQuestion('');
    setOptions(['', '']);
    setAllowMultiple(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a poll"
      className="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-poll-form">
            Create Poll
          </Button>
        </>
      }
    >
      <form id="create-poll-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-gray-200">
            Question
          </label>
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question..."
            className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-gray-200">
            Options
          </label>
          <div className="mt-1.5 space-y-2">
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={opt}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  className="h-9 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-xs outline-none focus:border-primary dark:border-white/15 dark:bg-white/[0.04]"
                />
                {options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(idx)}
                    className="p-1 text-slate-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {options.length < 6 && (
            <button
              type="button"
              onClick={handleAddOption}
              className="mt-2 text-xs font-semibold text-primary hover:underline"
            >
              + Add option
            </button>
          )}
        </div>

        {/* Single Choice / Multiple Choice Configuration Checkbox */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <label className="group flex cursor-pointer select-none items-center gap-2.5">
            <div
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all duration-150',
                !allowMultiple
                  ? 'border-primary bg-primary text-white shadow-xs'
                  : 'border-slate-300 bg-white group-hover:border-primary/50 dark:border-white/20 dark:bg-gray-800'
              )}
            >
              {!allowMultiple && <Check className="h-3 w-3 stroke-[3]" />}
            </div>
            <input
              type="checkbox"
              checked={!allowMultiple}
              onChange={(e) => setAllowMultiple(!e.target.checked)}
              className="sr-only"
            />
            <div>
              <span className="block text-xs font-semibold text-slate-800 dark:text-gray-100">
                Allow only 1 vote per person (Single Choice)
              </span>
              <span className="block text-[11px] text-slate-400 dark:text-gray-400">
                Members can select only one option
              </span>
            </div>
          </label>
        </div>
      </form>
    </Modal>
  );
}

// ─── Insert Link Modal ───────────────────────────────────────────────────────
interface InsertLinkModalProps {
  open: boolean;
  initialText?: string;
  onClose: () => void;
  onInsert: (text: string, url: string) => void;
}

function InsertLinkModal({
  open,
  initialText = '',
  onClose,
  onInsert,
}: InsertLinkModalProps): React.ReactElement {
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let formattedUrl = url.trim();
    if (!formattedUrl) {
      toast.error('Please enter a valid URL');
      return;
    }
    if (!/^https?:\/\//i.test(formattedUrl) && !/^mailto:/i.test(formattedUrl)) {
      formattedUrl = `https://${formattedUrl}`;
    }
    onInsert(text.trim() || formattedUrl, formattedUrl);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Link"
      className="max-w-sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="insert-link-form">
            Add Link
          </Button>
        </>
      }
    >
      <form id="insert-link-form" onSubmit={handleSubmit} className="space-y-3.5">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-gray-200">
            Text <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Display text"
            className="mt-1.5 h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-gray-200">
            Link URL <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="mt-1.5 h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </div>
      </form>
    </Modal>
  );
}

// ─── HTML to Markdown Converter ───────────────────────────────────────────────
function htmlToMarkdown(html: string): string {
  if (!html || !html.trim()) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;

  function nodeToMd(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    let inner = '';
    el.childNodes.forEach((child) => {
      inner += nodeToMd(child);
    });

    switch (tag) {
      case 'b':
      case 'strong':
        return inner.trim() ? `**${inner}**` : '';
      case 'i':
      case 'em':
        return inner.trim() ? `*${inner}*` : '';
      case 'u':
        return inner.trim() ? `__${inner}__` : '';
      case 's':
      case 'strike':
      case 'del':
        return inner.trim() ? `~~${inner}~~` : '';
      case 'code':
        if (el.parentElement?.tagName.toLowerCase() === 'pre') return inner;
        return inner.trim() ? `\`${inner}\`` : '';
      case 'pre':
        return `\n\`\`\`\n${inner.trim()}\n\`\`\`\n`;
      case 'blockquote':
        return (
          '\n' +
          inner
            .split('\n')
            .filter(Boolean)
            .map((l) => `> ${l}`)
            .join('\n') +
          '\n'
        );
      case 'a': {
        const href = el.getAttribute('href') || '';
        return `[${inner || href}](${href})`;
      }
      case 'li': {
        const parent = el.parentElement?.tagName.toLowerCase();
        if (parent === 'ol') {
          const idx = Array.from(el.parentElement?.children || []).indexOf(el) + 1;
          return `${idx}. ${inner}\n`;
        }
        return `- ${inner}\n`;
      }
      case 'ul':
      case 'ol':
        return `\n${inner}\n`;
      case 'div':
      case 'p':
        return inner ? `${inner}\n` : '';
      case 'br':
        return '\n';
      default:
        return inner;
    }
  }

  let result = '';
  temp.childNodes.forEach((child) => {
    result += nodeToMd(child);
  });
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

// ─── Composer ─────────────────────────────────────────────────────────────────
interface ComposerProps {
  channel: ChannelData;
  people?: ChatUser[];
  threadRootId?: string;
  onSent?: () => void;
  allowVoiceNotes?: boolean;
}

type VoiceNoteDraft = {
  file: File;
  previewUrl: string;
  duration: number;
  waveform: number[];
};

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PendingAttachmentCard({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}): React.ReactElement {
  const [previewUrl, setPreviewUrl] = useState<string>();
  const isImage = file.type.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    const frame = window.requestAnimationFrame(() => setPreviewUrl(url));
    return () => {
      window.cancelAnimationFrame(frame);
      URL.revokeObjectURL(url);
    };
  }, [file, isImage]);

  return (
    <div className="group relative flex w-[11.5rem] shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white p-1.5 pr-7 shadow-sm dark:border-white/10 dark:bg-gray-800">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 dark:bg-white/[0.06]">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
        ) : isImage ? (
          <Image className="h-5 w-5 text-slate-400" aria-hidden />
        ) : (
          <FileText className="h-5 w-5 text-slate-500 dark:text-gray-400" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-medium text-slate-700 dark:text-gray-200">
          {file.name}
        </p>
        <p className="mt-0.5 text-[10px] text-slate-400 dark:text-gray-500">
          {formatAttachmentSize(file.size)}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
        aria-label={`Remove ${file.name}`}
        title="Remove attachment"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Composer({
  channel,
  people = [],
  threadRootId,
  onSent,
  allowVoiceNotes = false,
}: ComposerProps): React.ReactElement {
  const { socket, isConnected } = useSocket();
  const sendMessage = useSendChannelMessage();
  const [files, setFiles] = useState<File[]>([]);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [pollModalOpen, setPollModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkInitialText, setLinkInitialText] = useState('');
  const [moreFormatOpen, setMoreFormatOpen] = useState(false);
  const [alsoSendChannel, setAlsoSendChannel] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isVoiceRecordingPaused, setIsVoiceRecordingPaused] = useState(false);
  const [isFinalizingVoice, setIsFinalizingVoice] = useState(false);
  const [voiceRecordingSeconds, setVoiceRecordingSeconds] = useState(0);
  const [voiceRecordingWaveform, setVoiceRecordingWaveform] =
    useState<number[]>(EMPTY_RECORDING_WAVEFORM);
  const [voiceNoteDraft, setVoiceNoteDraft] = useState<VoiceNoteDraft | null>(null);

  const typingAt = useRef(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);
  const mentionRangeRef = useRef<{ node: Node; startOffset: number; endOffset: number } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const moreFormatRef = useRef<HTMLDivElement>(null);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceRecordingStartedAtRef = useRef<number | null>(null);
  const voiceRecordingPausedAtRef = useRef<number | null>(null);
  const voiceRecordingPausedMillisecondsRef = useRef(0);
  const voiceRecordingTimerRef = useRef<number | null>(null);
  const voiceWaveformFrameRef = useRef<number | null>(null);
  const voiceWaveformSamplesRef = useRef<number[]>([]);
  const voiceWaveformLastSampleAtRef = useRef(0);
  const voiceWaveformLastRenderAtRef = useRef(0);
  const voiceAudioContextRef = useRef<AudioContext | null>(null);
  const voiceRecordingDurationRef = useRef(0);
  const discardVoiceRecordingRef = useRef(false);
  const sendVoiceRecordingRef = useRef(false);

  useEffect(
    () => () => {
      if (voiceRecordingTimerRef.current !== null) {
        window.clearInterval(voiceRecordingTimerRef.current);
      }
      if (voiceWaveformFrameRef.current !== null) {
        window.cancelAnimationFrame(voiceWaveformFrameRef.current);
      }
      void voiceAudioContextRef.current?.close();
      voiceRecorderRef.current?.stop();
      voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
    },
    []
  );

  const stopVoiceWaveform = useCallback(() => {
    if (voiceWaveformFrameRef.current !== null) {
      window.cancelAnimationFrame(voiceWaveformFrameRef.current);
      voiceWaveformFrameRef.current = null;
    }
    const context = voiceAudioContextRef.current;
    voiceAudioContextRef.current = null;
    if (context && context.state !== 'closed') void context.close();
  }, []);

  useEffect(() => {
    if (!isRecordingVoice) return;
    const updateDuration = () => {
      const startedAt = voiceRecordingStartedAtRef.current;
      if (startedAt !== null) {
        const endedAt = voiceRecordingPausedAtRef.current ?? Date.now();
        const seconds = Math.floor(
          (endedAt - startedAt - voiceRecordingPausedMillisecondsRef.current) / 1000
        );
        voiceRecordingDurationRef.current = seconds;
        setVoiceRecordingSeconds(seconds);
      }
    };
    updateDuration();
    voiceRecordingTimerRef.current = window.setInterval(updateDuration, 250);
    return () => {
      if (voiceRecordingTimerRef.current !== null) {
        window.clearInterval(voiceRecordingTimerRef.current);
        voiceRecordingTimerRef.current = null;
      }
    };
  }, [isRecordingVoice]);

  useEffect(() => {
    if (!isRecordingVoice || !socket || !isConnected) return;
    const broadcastRecording = () =>
      socket.emit('channel.typing', {
        channelId: channel._id,
        typing: true,
        activity: 'recording',
      });
    socket.emit('channel.typing', { channelId: channel._id, typing: false });
    broadcastRecording();
    const heartbeat = window.setInterval(broadcastRecording, 3_000);
    return () => {
      window.clearInterval(heartbeat);
      socket.emit('channel.typing', {
        channelId: channel._id,
        typing: false,
        activity: 'recording',
      });
    };
  }, [channel._id, isConnected, isRecordingVoice, socket]);

  useEffect(
    () => () => {
      if (voiceNoteDraft) URL.revokeObjectURL(voiceNoteDraft.previewUrl);
    },
    [voiceNoteDraft]
  );

  const isDirectConversation = channel.kind === 'direct' || channel.kind === 'group_dm';
  const isModerator =
    !isDirectConversation &&
    (channel.currentUserRole === 'owner' || channel.currentUserRole === 'moderator');
  const ownerOnlyPosting =
    !isDirectConversation &&
    channel.messagePermission === 'owner_only' &&
    channel.currentUserRole !== 'owner';
  const readOnly =
    !isDirectConversation &&
    (ownerOnlyPosting || ((channel.isReadOnly || channel.kind === 'announcement') && !isModerator));

  // If the connection drops while the editor is already in use, the normal
  // input event will not fire again after reconnect. Restore the ephemeral
  // typing state so the other participant does not see an intermittent gap.
  useEffect(() => {
    if (!socket || !isConnected || readOnly) return;
    if (editorRef.current?.textContent?.trim()) {
      socket.emit('channel.typing', { channelId: channel._id, typing: true });
    }
  }, [channel._id, isConnected, readOnly, socket]);

  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    orderedList: false,
    unorderedList: false,
    quote: false,
    link: false,
    inlineCode: false,
    codeBlock: false,
  });

  const refreshActiveFormats = useCallback(() => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current?.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    const element =
      range.startContainer.nodeType === Node.ELEMENT_NODE
        ? (range.startContainer as Element)
        : range.startContainer.parentElement;
    const isInside = (selector: string) => Boolean(element?.closest(selector));
    const formatBlock = String(document.queryCommandValue('formatBlock')).toLowerCase();
    const next = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strike: document.queryCommandState('strikeThrough'),
      orderedList: document.queryCommandState('insertOrderedList'),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      quote: formatBlock === 'blockquote' || isInside('blockquote'),
      link: isInside('a'),
      inlineCode: isInside('code'),
      codeBlock: isInside('pre'),
    };
    setActiveFormats((current) =>
      Object.keys(next).every(
        (key) => current[key as keyof typeof current] === next[key as keyof typeof next]
      )
        ? current
        : next
    );
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActiveFormats);
    return () => document.removeEventListener('selectionchange', refreshActiveFormats);
  }, [refreshActiveFormats]);

  const filteredPeople = people.filter((p) => {
    const full = `${p.firstName} ${p.lastName}`.toLowerCase();
    return (
      full.includes(mentionSearch.toLowerCase()) ||
      p.email.toLowerCase().includes(mentionSearch.toLowerCase())
    );
  });
  // Role group mentions (@manager) exist in channels, not DMs.
  const isDirectConversationChannel = channel.kind === 'direct' || channel.kind === 'group_dm';
  const { data: mentionGroups = [] } = useMentionGroups(
    isDirectConversationChannel ? null : channel._id,
    mentionOpen
  );
  const filteredGroups = filterMentionGroups(mentionGroups, mentionSearch);
  const mentionOptionCount = filteredGroups.length + filteredPeople.length;

  useEffect(() => {
    if (!plusMenuOpen && !emojiOpen && !moreFormatOpen) return;
    const close = (e: MouseEvent) => {
      if (plusMenuOpen && !plusMenuRef.current?.contains(e.target as Node)) setPlusMenuOpen(false);
      if (emojiOpen && !emojiPickerRef.current?.contains(e.target as Node)) setEmojiOpen(false);
      if (moreFormatOpen && !moreFormatRef.current?.contains(e.target as Node))
        setMoreFormatOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [plusMenuOpen, emojiOpen, moreFormatOpen]);

  const exec = (command: string, value: string | undefined = undefined) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    requestAnimationFrame(refreshActiveFormats);
  };

  const handleFilesSelected = (selectedFiles: FileList | File[] | null) => {
    const candidates = Array.from(selectedFiles ?? []);
    const oversized = candidates.filter((file) => file.size > MAX_CHANNEL_FILE_SIZE);
    const accepted = candidates.filter((file) => file.size <= MAX_CHANNEL_FILE_SIZE);
    if (oversized.length) {
      toast.error(
        `${oversized.length === 1 ? oversized[0].name : `${oversized.length} files`} exceed the 20 MB per-file limit.`
      );
    }
    if (!accepted.length) return;
    setFiles((current) => {
      const remaining = Math.max(0, 10 - current.length - (voiceNoteDraft ? 1 : 0));
      if (accepted.length > remaining) toast.error('A message can include up to 10 files.');
      return [...current, ...accepted.slice(0, remaining)];
    });
    setPlusMenuOpen(false);
  };
  const handleFilesSelectedRef = useRef(handleFilesSelected);
  useEffect(() => {
    handleFilesSelectedRef.current = handleFilesSelected;
  });

  // Pasted screenshots all arrive as "image.png"; give each a distinct name.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = Array.from(e.clipboardData.files);
    if (!pasted.length) return;
    e.preventDefault();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    handleFilesSelected(
      pasted.map((file, index) =>
        /^image\.\w+$/i.test(file.name)
          ? new File(
              [file],
              `pasted-${stamp}${pasted.length > 1 ? `-${index + 1}` : ''}.${file.name.split('.').pop()}`,
              { type: file.type, lastModified: file.lastModified }
            )
          : file
      )
    );
  };

  // Files can be dropped anywhere on the surrounding conversation pane.
  const formRef = useRef<HTMLFormElement>(null);
  // The pane currently being dragged over, or null when no files are in flight.
  const [activeDropZone, setActiveDropZone] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const zone = formRef.current?.closest<HTMLElement>('[data-chat-drop-zone]') ?? formRef.current;
    if (!zone) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth += 1;
      setActiveDropZone(zone);
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setActiveDropZone(null);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setActiveDropZone(null);
      handleFilesSelectedRef.current(Array.from(e.dataTransfer?.files ?? []));
      editorRef.current?.focus();
    };
    zone.addEventListener('dragenter', onDragEnter);
    zone.addEventListener('dragover', onDragOver);
    zone.addEventListener('dragleave', onDragLeave);
    zone.addEventListener('drop', onDrop);
    return () => {
      zone.removeEventListener('dragenter', onDragEnter);
      zone.removeEventListener('dragover', onDragOver);
      zone.removeEventListener('dragleave', onDragLeave);
      zone.removeEventListener('drop', onDrop);
    };
  }, [readOnly]);

  const toggleVoiceRecording = async () => {
    if (isRecordingVoice) {
      const recorder = voiceRecorderRef.current;
      if (recorder?.state === 'recording' || recorder?.state === 'paused') {
        setIsFinalizingVoice(true);
        // Request a last chunk before stopping. Some browsers otherwise only
        // emit an empty final chunk for very short recordings.
        try {
          recorder.requestData();
        } catch {
          // A browser can transition to inactive between the state check and
          // requestData; onstop still receives the already captured chunks.
        }
        recorder.stop();
      }
      return;
    }
    if (isFinalizingVoice) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice notes are not supported by this browser.');
      return;
    }
    try {
      discardVoiceRecordingRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find(
        (type) => MediaRecorder.isTypeSupported(type)
      );
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        const finalMimeType = recorder.mimeType || 'audio/webm';
        const extension = finalMimeType.includes('ogg') ? 'ogg' : 'webm';
        const recording = new Blob(chunks, { type: finalMimeType });
        const file = new File([recording], `voice-note-${Date.now()}.${extension}`, {
          type: finalMimeType,
        });
        stream.getTracks().forEach((track) => track.stop());
        stopVoiceWaveform();
        voiceRecorderRef.current = null;
        voiceStreamRef.current = null;
        const startedAt = voiceRecordingStartedAtRef.current;
        const stoppedAt = voiceRecordingPausedAtRef.current ?? Date.now();
        // Calculate once more at stop time. This avoids a stale 0:00 value if
        // the recorder is stopped before React's interval has had a chance to run.
        const capturedDuration = Math.max(
          1,
          Math.ceil(
            startedAt === null
              ? voiceRecordingDurationRef.current
              : (stoppedAt - startedAt - voiceRecordingPausedMillisecondsRef.current) / 1000
          )
        );
        const capturedWaveform = buildVoiceWaveform(voiceWaveformSamplesRef.current);
        voiceWaveformSamplesRef.current = [];
        voiceRecordingStartedAtRef.current = null;
        voiceRecordingPausedAtRef.current = null;
        voiceRecordingPausedMillisecondsRef.current = 0;
        voiceRecordingDurationRef.current = 0;
        setIsRecordingVoice(false);
        setIsVoiceRecordingPaused(false);
        setIsFinalizingVoice(false);
        setVoiceRecordingSeconds(0);
        setVoiceRecordingWaveform(EMPTY_RECORDING_WAVEFORM);
        const sendImmediately = sendVoiceRecordingRef.current;
        sendVoiceRecordingRef.current = false;
        if (discardVoiceRecordingRef.current) {
          discardVoiceRecordingRef.current = false;
          return;
        }
        if (file.size === 0) {
          toast.error('No audio was captured. Please record again.');
          return;
        }
        if (file.size > MAX_CHANNEL_FILE_SIZE) {
          toast.error('Voice note exceeds the 20 MB limit.');
          return;
        }
        if (sendImmediately) {
          const payload = {
            ...(threadRootId && !alsoSendChannel ? { threadRootId } : {}),
            clientMessageId: crypto.randomUUID(),
            voiceDurationSeconds: capturedDuration,
            voiceWaveform: capturedWaveform,
          };
          if (alsoSendChannel) setAlsoSendChannel(false);
          onSent?.();
          socket?.emit('channel.typing', { channelId: channel._id, typing: false });
          sendMessage.mutate(
            { channelId: channel._id, payload, files: [file] },
            { onError: (error) => toast.error(getErrorMessage(error)) }
          );
          return;
        }
        if (files.length >= 10) {
          toast.error('Remove an attachment before adding a voice note.');
          return;
        }
        setVoiceNoteDraft({
          file,
          previewUrl: URL.createObjectURL(file),
          duration: capturedDuration,
          waveform: capturedWaveform,
        });
      };
      voiceRecorderRef.current = recorder;
      voiceStreamRef.current = stream;
      setVoiceRecordingWaveform(EMPTY_RECORDING_WAVEFORM);
      voiceWaveformSamplesRef.current = [];
      voiceWaveformLastSampleAtRef.current = 0;
      voiceWaveformLastRenderAtRef.current = 0;
      try {
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.72;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        voiceAudioContextRef.current = audioContext;
        const readAudioLevel = (now: number) => {
          if (voiceAudioContextRef.current !== audioContext) return;
          analyser.getByteTimeDomainData(samples);
          const averageAmplitude =
            samples.reduce((total, sample) => total + Math.abs(sample - 128), 0) / samples.length;
          const level = Math.max(4, Math.min(18, Math.round(4 + averageAmplitude * 0.95)));
          if (now - voiceWaveformLastRenderAtRef.current >= 145) {
            voiceWaveformLastRenderAtRef.current = now;
            setVoiceRecordingWaveform((current) => {
              const previousLevel = current.at(-1) ?? 4;
              const easedLevel = Math.round(previousLevel * 0.68 + level * 0.32);
              return [...current.slice(1), easedLevel];
            });
          }
          if (now - voiceWaveformLastSampleAtRef.current >= 50) {
            voiceWaveformSamplesRef.current.push(averageAmplitude);
            voiceWaveformLastSampleAtRef.current = now;
          }
          if (voiceAudioContextRef.current === audioContext) {
            voiceWaveformFrameRef.current = window.requestAnimationFrame(readAudioLevel);
          }
        };
        voiceWaveformFrameRef.current = window.requestAnimationFrame(readAudioLevel);
      } catch {
        // Recording still works in browsers that cannot expose an analyser.
      }
      // Periodic chunks keep the recording valid even if the browser flushes
      // the final data event late.
      recorder.start(250);
      voiceRecordingStartedAtRef.current = Date.now();
      voiceRecordingPausedAtRef.current = null;
      voiceRecordingPausedMillisecondsRef.current = 0;
      voiceRecordingDurationRef.current = 0;
      setVoiceRecordingSeconds(0);
      setIsVoiceRecordingPaused(false);
      setIsRecordingVoice(true);
    } catch {
      toast.error('Microphone access is required to record a voice note.');
    }
  };

  const cancelVoiceRecording = () => {
    const recorder = voiceRecorderRef.current;
    if (!isRecordingVoice || (recorder?.state !== 'recording' && recorder?.state !== 'paused'))
      return;
    discardVoiceRecordingRef.current = true;
    setIsFinalizingVoice(true);
    recorder.stop();
  };

  const toggleVoicePause = () => {
    const recorder = voiceRecorderRef.current;
    if (!isRecordingVoice || !recorder) return;
    if (recorder.state === 'recording') {
      voiceRecordingPausedAtRef.current = Date.now();
      recorder.pause();
      setIsVoiceRecordingPaused(true);
      return;
    }
    if (recorder.state === 'paused') {
      const pausedAt = voiceRecordingPausedAtRef.current;
      if (pausedAt !== null) {
        voiceRecordingPausedMillisecondsRef.current += Date.now() - pausedAt;
      }
      voiceRecordingPausedAtRef.current = null;
      recorder.resume();
      setIsVoiceRecordingPaused(false);
    }
  };

  const sendVoiceRecording = () => {
    const recorder = voiceRecorderRef.current;
    if (!isRecordingVoice || (recorder?.state !== 'recording' && recorder?.state !== 'paused'))
      return;
    sendVoiceRecordingRef.current = true;
    setIsFinalizingVoice(true);
    recorder.stop();
  };

  const handleInput = () => {
    if (!socket || !isConnected || readOnly) return;
    const now = Date.now();
    if (now - typingAt.current > 1500) {
      typingAt.current = now;
      socket.emit('channel.typing', { channelId: channel._id, typing: true });
    }

    // Live Slack/Discord @ detection
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const textBefore = node.textContent?.slice(0, range.startOffset) || '';
        const match = textBefore.match(/(?:^|\s)@([a-zA-Z0-9_.-]{0,20})$/);
        if (match) {
          const query = match[1];
          const atIdx = textBefore.lastIndexOf('@' + query);
          mentionRangeRef.current = {
            node,
            startOffset: atIdx,
            endOffset: range.startOffset,
          };
          setMentionSearch(query);
          setMentionSelectedIndex(0);
          setMentionOpen(true);
          return;
        }
      }
    }

    if (mentionOpen) {
      setMentionOpen(false);
      mentionRangeRef.current = null;
    }
  };

  const handleAddEmoji = (emoji: string) => {
    editorRef.current?.focus();
    document.execCommand('insertText', false, emoji);
    setEmojiOpen(false);
  };

  const handleMentionSelect = (user: ChatUser) => {
    const name = `${user.firstName} ${user.lastName}`.trim() || user.email;
    insertMentionChip(`@${name}`, 'data-mention-user', user._id);
  };

  const handleGroupMentionSelect = (group: MentionGroup) => {
    insertMentionChip(`@${group.name}`, 'data-mention-role', group.id);
  };

  /**
   * Replaces the typed "@query" with a chip. The data attribute carries the id
   * so the send path can tell the API who (or which role) was mentioned.
   */
  const insertMentionChip = (
    label: string,
    attribute: 'data-mention-user' | 'data-mention-role',
    id: string
  ) => {
    editorRef.current?.focus();

    if (mentionRangeRef.current && editorRef.current?.contains(mentionRangeRef.current.node)) {
      const { node, startOffset, endOffset } = mentionRangeRef.current;
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        try {
          range.setStart(node, startOffset);
          range.setEnd(node, endOffset);
          sel.removeAllRanges();
          sel.addRange(range);
        } catch {
          // ignore fallback
        }
      }
    }

    const chip = document.createElement('span');
    chip.className =
      'inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 font-semibold text-primary dark:bg-primary/20';
    chip.setAttribute(attribute, id);
    // textContent escapes names that contain markup characters.
    chip.textContent = label;
    document.execCommand('insertHTML', false, `${chip.outerHTML}&nbsp;`);
    setMentionOpen(false);
    setMentionSearch('');
    mentionRangeRef.current = null;
  };

  /** Ids of the people and roles whose chips are still in the editor. */
  const collectMentions = (): { mentions?: string[]; mentionRoles?: string[] } => {
    const editor = editorRef.current;
    if (!editor) return {};
    const idsOf = (attribute: string) => [
      ...new Set(
        [...editor.querySelectorAll<HTMLElement>(`[${attribute}]`)]
          .map((chip) => chip.getAttribute(attribute) ?? '')
          .filter(Boolean)
      ),
    ];
    const mentions = idsOf('data-mention-user');
    const mentionRoles = idsOf('data-mention-role');
    return {
      ...(mentions.length ? { mentions } : {}),
      ...(mentionRoles.length ? { mentionRoles } : {}),
    };
  };

  const handleOpenLinkModal = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
      setLinkInitialText(sel.toString());
    } else {
      savedSelectionRef.current = null;
      setLinkInitialText('');
    }
    setLinkModalOpen(true);
  };

  const handleLinkInsert = (linkText: string, linkUrl: string) => {
    editorRef.current?.focus();
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
      }
    }
    document.execCommand(
      'insertHTML',
      false,
      `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary-dark">${linkText}</a>&nbsp;`
    );
  };

  const handleInsertInlineCode = () => {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && selection.toString()) {
      const selectedText = selection.toString();
      document.execCommand(
        'insertHTML',
        false,
        `<code class="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-[12px] text-pink-600 dark:bg-white/10 dark:text-pink-400">${selectedText}</code>&nbsp;`
      );
    } else {
      document.execCommand(
        'insertHTML',
        false,
        `<code class="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-[12px] text-pink-600 dark:bg-white/10 dark:text-pink-400">code</code>&nbsp;`
      );
    }
  };

  const handleInsertSnippet = () => {
    editorRef.current?.focus();
    document.execCommand(
      'insertHTML',
      false,
      `<pre class="my-1.5 rounded-lg border border-slate-300 bg-slate-900 p-2.5 font-mono text-[12px] text-slate-100 dark:border-white/10 dark:bg-black/80"><code>// Your code here</code></pre><p><br></p>`
    );
    setPlusMenuOpen(false);
  };

  const getClosestListItem = (): HTMLLIElement | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    let node: Node | null = selection.anchorNode;
    while (node && node !== editorRef.current) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).tagName.toLowerCase() === 'li'
      ) {
        return node as HTMLLIElement;
      }
      node = node.parentNode;
    }
    return null;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Keyboard navigation when @ mention popup is open
    // Options are groups first, then people (same order as MentionPopover).
    if (mentionOpen && mentionOptionCount > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex((i) => (i + 1) % mentionOptionCount);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex((i) => (i - 1 + mentionOptionCount) % mentionOptionCount);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const index = mentionSelectedIndex < mentionOptionCount ? mentionSelectedIndex : 0;
        if (index < filteredGroups.length) handleGroupMentionSelect(filteredGroups[index]);
        else {
          const targetUser = filteredPeople[index - filteredGroups.length];
          if (targetUser) handleMentionSelect(targetUser);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }

    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        exec('bold');
        return;
      }
      if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        exec('italic');
        return;
      }
      if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        exec('underline');
        return;
      }
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        handleOpenLinkModal();
        return;
      }
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        handleInsertInlineCode();
        return;
      }
      if ((e.key === 'x' || e.key === 'X') && e.shiftKey) {
        e.preventDefault();
        exec('strikeThrough');
        return;
      }
    }

    if (e.key === 'Enter') {
      if (e.shiftKey) {
        e.preventDefault();
        const li = getClosestListItem();
        if (li) {
          const text = li.textContent?.replace(/\u200B/g, '').trim() || '';
          const list = li.parentElement; // <ol> or <ul>
          if (text === '') {
            // Double Shift+Enter on empty list item -> Exit the list back to normal text!
            if (list && editorRef.current?.contains(list)) {
              li.remove();
              const p = document.createElement('p');
              p.innerHTML = '<br>';
              if (list.nextSibling) {
                list.parentNode?.insertBefore(p, list.nextSibling);
              } else {
                list.parentNode?.appendChild(p);
              }
              if (list.children.length === 0) {
                list.remove();
              }
              const range = document.createRange();
              range.setStart(p, 0);
              range.collapse(true);
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(range);
            }
          } else {
            // Not empty -> continue to next item (2., 3., etc.)!
            document.execCommand('insertParagraph', false);
          }
        } else {
          // Outside of list -> normal newline
          document.execCommand('insertLineBreak', false);
        }
      } else if (!e.nativeEvent.isComposing) {
        e.preventDefault();
        void submit();
      }
    }
  };

  const handleCreatePoll = (pollPayload: string) => {
    if (readOnly) return;
    const payload = {
      content: pollPayload,
      ...(threadRootId && !alsoSendChannel ? { threadRootId } : {}),
      clientMessageId: crypto.randomUUID(),
    };

    sendMessage.mutate(
      { channelId: channel._id, payload, files: [] },
      { onError: (error) => toast.error(getErrorMessage(error)) }
    );
    onSent?.();
    if (alsoSendChannel) setAlsoSendChannel(false);
    toast.success('Poll published!');
  };

  const submit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (isRecordingVoice || isFinalizingVoice) {
      toast.info('Stop recording and wait for the voice note to finish preparing.');
      return;
    }
    const html = editorRef.current?.innerHTML || '';
    const body = htmlToMarkdown(html).trim();
    const filesToSend = voiceNoteDraft ? [...files, voiceNoteDraft.file] : files;
    if ((!body && filesToSend.length === 0) || readOnly) return;

    const payload = {
      content: body || undefined,
      ...(body ? collectMentions() : {}),
      ...(threadRootId && !alsoSendChannel ? { threadRootId } : {}),
      clientMessageId: crypto.randomUUID(),
      ...(voiceNoteDraft ? { voiceDurationSeconds: voiceNoteDraft.duration } : {}),
      ...(voiceNoteDraft ? { voiceWaveform: voiceNoteDraft.waveform } : {}),
    };

    if (editorRef.current) editorRef.current.innerHTML = '';
    setFiles([]);
    setVoiceNoteDraft(null);
    if (alsoSendChannel) setAlsoSendChannel(false);
    onSent?.();
    socket?.emit('channel.typing', { channelId: channel._id, typing: false });
    setTimeout(() => editorRef.current?.focus(), 0);
    sendMessage.mutate(
      { channelId: channel._id, payload, files: filesToSend },
      { onError: (error) => toast.error(getErrorMessage(error)) }
    );
  };

  if (readOnly) {
    return (
      <div className="border-t border-slate-300 bg-white px-5 py-3 text-center text-[12px] text-slate-500 dark:border-white/10 dark:bg-gray-900 dark:text-gray-400">
        <Volume2 className="mb-0.5 inline h-3.5 w-3.5 text-amber-500" />{' '}
        {ownerOnlyPosting
          ? 'Only the channel owner can post here.'
          : 'Only owners and moderators can post here.'}
      </div>
    );
  }

  const placeholderText = threadRootId ? 'Reply…' : `Message #${channel.name}`;
  const formatButtonClass = (active: boolean) =>
    cn(
      'flex h-6.5 w-6.5 items-center justify-center rounded transition-colors',
      active
        ? 'bg-primary/15 text-primary shadow-xs dark:bg-primary/25 dark:text-primary-light'
        : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white'
    );

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className="shrink-0 bg-white px-4 pb-4 pt-2 dark:bg-gray-900"
    >
      {activeDropZone &&
        createPortal(
          <div className="pointer-events-none absolute inset-2 z-40 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary bg-white/90 text-center dark:bg-gray-900/90">
            <UploadCloud className="h-8 w-8 text-primary" />
            <p className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">
              Drop files to attach
            </p>
            <p className="text-[11px] text-slate-500">Up to 10 files, 20 MB each</p>
          </div>,
          activeDropZone
        )}
      {/* A voice note remains its own preview, never a generic file attachment. */}
      {voiceNoteDraft && (
        <div className="mb-2">
          <VoiceNotePlayer
            src={voiceNoteDraft.previewUrl}
            initialDuration={voiceNoteDraft.duration}
            initialWaveform={voiceNoteDraft.waveform}
            onDiscard={() => setVoiceNoteDraft(null)}
            onSend={() => void submit()}
          />
        </div>
      )}

      {/* Attached files preview */}
      {files.length > 0 && (
        <div className="-mb-px rounded-t-xl border border-slate-300 bg-slate-50/90 px-2.5 py-2 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="chat-scroll flex gap-2 overflow-x-auto pb-0.5">
            {files.map((file, index) => (
              <PendingAttachmentCard
                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                file={file}
                onRemove={() => setFiles((current) => current.filter((item) => item !== file))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="sr-only"
        onChange={(e) => {
          handleFilesSelected(e.target.files);
          e.currentTarget.value = '';
        }}
      />

      {/* Main Composer Box */}
      <div
        className={cn(
          'flex flex-col rounded-xl border border-slate-300 bg-slate-50/70 outline-none transition-[border-color,box-shadow,background-color] duration-150 focus-within:border-slate-400/80 focus-within:bg-white focus-within:outline-none focus-within:ring-1 focus-within:ring-slate-200/80 dark:border-white/10 dark:bg-white/[0.035] dark:focus-within:border-white/20 dark:focus-within:bg-white/[0.045] dark:focus-within:ring-white/[0.04]',
          files.length > 0 && 'rounded-tl-none rounded-tr-none'
        )}
      >
        {/* Top Rich Text Formatting Toolbar (Seamless single-row layout without awkward gaps) */}
        <div
          className={cn(
            'flex items-center gap-1 overflow-x-hidden border-b border-slate-200 px-2.5 py-1.5 dark:border-white/10',
            isRecordingVoice && 'hidden'
          )}
        >
          {/* 1. Bold */}
          <button
            type="button"
            title="Bold (Ctrl+B)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('bold')}
            className={formatButtonClass(activeFormats.bold)}
            aria-pressed={activeFormats.bold}
          >
            <Bold className="h-3.5 w-3.5" />
          </button>

          {/* 2. Italic */}
          <button
            type="button"
            title="Italic (Ctrl+I)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('italic')}
            className={formatButtonClass(activeFormats.italic)}
            aria-pressed={activeFormats.italic}
          >
            <Italic className="h-3.5 w-3.5" />
          </button>

          {/* 3. Underline */}
          <button
            type="button"
            title="Underline (Ctrl+U)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('underline')}
            className={formatButtonClass(activeFormats.underline)}
            aria-pressed={activeFormats.underline}
          >
            <Underline className="h-3.5 w-3.5" />
          </button>

          {/* 4. Strikethrough */}
          <button
            type="button"
            title="Strikethrough (Ctrl+Shift+X)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('strikeThrough')}
            className={formatButtonClass(activeFormats.strike)}
            aria-pressed={activeFormats.strike}
          >
            <Strikethrough className="h-3.5 w-3.5" />
          </button>

          {/* 5. Link */}
          <button
            type="button"
            title="Link (Ctrl+K)"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleOpenLinkModal}
            className={formatButtonClass(activeFormats.link)}
            aria-pressed={activeFormats.link}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>

          {/* Divider */}
          <div className="mx-0.5 h-3.5 w-px bg-slate-300 dark:bg-white/15" />

          {/* 6. Ordered List */}
          <button
            type="button"
            title="Numbered list"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('insertOrderedList')}
            className={formatButtonClass(activeFormats.orderedList)}
            aria-pressed={activeFormats.orderedList}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </button>

          {/* 7. Bulleted List */}
          <button
            type="button"
            title="Bulleted list"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('insertUnorderedList')}
            className={formatButtonClass(activeFormats.unorderedList)}
            aria-pressed={activeFormats.unorderedList}
          >
            <List className="h-3.5 w-3.5" />
          </button>

          {/* 8. Blockquote */}
          <button
            type="button"
            title="Blockquote"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('formatBlock', 'blockquote')}
            className={formatButtonClass(activeFormats.quote)}
            aria-pressed={activeFormats.quote}
          >
            <Quote className="h-3.5 w-3.5" />
          </button>

          {/* Main Message Body: Show Inline Code & Code block directly */}
          {!threadRootId ? (
            <>
              {/* Divider */}
              <div className="mx-0.5 h-3.5 w-px bg-slate-300 dark:bg-white/15" />

              {/* Inline Code */}
              <button
                type="button"
                title="Inline code (Ctrl+E)"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleInsertInlineCode}
                className={formatButtonClass(activeFormats.inlineCode)}
                aria-pressed={activeFormats.inlineCode}
              >
                <Code className="h-3.5 w-3.5" />
              </button>

              {/* Code Block */}
              <button
                type="button"
                title="Code block"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleInsertSnippet}
                className={formatButtonClass(activeFormats.codeBlock)}
                aria-pressed={activeFormats.codeBlock}
              >
                <FileCode className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            /* Thread Panel: 3 Dots at the very end in a contiguous row */
            <div className="relative" ref={moreFormatRef}>
              <button
                type="button"
                title="More formatting options"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setMoreFormatOpen((o) => !o)}
                className={cn(
                  'flex h-6.5 w-6.5 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white',
                  moreFormatOpen && 'bg-slate-200 text-slate-900 dark:bg-white/15 dark:text-white'
                )}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>

              {moreFormatOpen && (
                <div className="absolute right-0 top-[calc(100%+4px)] z-50 flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800">
                  <button
                    type="button"
                    title="Inline code (Ctrl+E)"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      handleInsertInlineCode();
                      setMoreFormatOpen(false);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 hover:text-emerald-600 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-emerald-400"
                  >
                    <Code className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    title="Code block"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      handleInsertSnippet();
                      setMoreFormatOpen(false);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 hover:text-primary dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-primary-light"
                  >
                    <FileCode className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Live Visual WYSIWYG Editable Area */}
        <div
          ref={editorRef}
          contentEditable
          role="textbox"
          aria-multiline="true"
          spellCheck={false}
          data-placeholder={placeholderText}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={cn(
            'channel-message-editor max-h-36 min-h-[48px] w-full overflow-y-auto bg-transparent px-3 py-2 text-[13px] leading-relaxed text-slate-800 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 chat-scroll empty:before:pointer-events-none empty:before:select-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] dark:text-gray-100 dark:empty:before:text-gray-500 [&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-primary/60 [&_blockquote]:bg-primary/5 [&_blockquote]:px-2.5 [&_blockquote]:py-0.5 [&_blockquote]:italic [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
            isRecordingVoice && 'hidden'
          )}
        />

        {/* Thread options row (if in thread) */}
        {threadRootId && !isRecordingVoice && (
          <div className="border-t border-slate-200/80 px-2.5 py-1.5 dark:border-white/10">
            <label className="group inline-flex cursor-pointer select-none items-center gap-2">
              <div
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all duration-150',
                  alsoSendChannel
                    ? 'border-primary bg-primary text-white shadow-sm'
                    : 'border-slate-300 bg-white group-hover:border-primary/60 dark:border-white/25 dark:bg-gray-800'
                )}
              >
                {alsoSendChannel && <Check className="h-3 w-3 stroke-[3]" />}
              </div>
              <input
                type="checkbox"
                checked={alsoSendChannel}
                onChange={(e) => setAlsoSendChannel(e.target.checked)}
                className="sr-only"
              />
              <span className="text-xs text-slate-600 dark:text-gray-300">
                Send as channel message
              </span>
            </label>
          </div>
        )}

        {/* Bottom Action Bar */}
        {isRecordingVoice ? (
          <div className="flex min-h-[52px] items-center px-3 py-1.5">
            <div className="flex w-full max-w-[23rem] items-center gap-2.5 rounded-full bg-slate-100/80 px-3 py-2 dark:bg-white/[0.07]">
              <button
                type="button"
                onClick={cancelVoiceRecording}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                title="Discard recording"
                aria-label="Discard recording"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div
                className="flex min-w-0 flex-1 items-center gap-2"
                role="status"
                aria-live="polite"
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
                <span className="shrink-0 text-[12px] font-medium tabular-nums text-slate-600 dark:text-gray-300">
                  {formatAudioDuration(voiceRecordingSeconds)}
                </span>
                <div
                  className="flex h-5 min-w-0 flex-1 items-center gap-[2px] overflow-hidden"
                  aria-label="Live recording level"
                >
                  {voiceRecordingWaveform.map((height, index) => (
                    <span
                      key={`${height}-${index}`}
                      className="min-w-[2px] flex-1 rounded-full bg-slate-500/80 transition-[height] duration-[280ms] ease-out dark:bg-white/55"
                      style={{ height }}
                    />
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void toggleVoiceRecording()}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                title="Finish recording and preview"
                aria-label="Finish recording and preview"
              >
                <Square className="h-2.5 w-2.5 fill-current" />
              </button>
              <button
                type="button"
                onClick={toggleVoicePause}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                title={isVoiceRecordingPaused ? 'Resume recording' : 'Pause recording'}
                aria-label={isVoiceRecordingPaused ? 'Resume recording' : 'Pause recording'}
              >
                {isVoiceRecordingPaused ? (
                  <Play className="ml-0.5 h-3 w-3 fill-current" />
                ) : (
                  <Pause className="h-3 w-3 fill-current" />
                )}
              </button>
              <button
                type="button"
                onClick={sendVoiceRecording}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-600 text-white shadow-sm transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                title="Send voice note"
                aria-label="Send voice note"
              >
                <Send className="ml-[-1px] h-3 w-3" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-2 py-1.5">
            {/* Left Actions */}
            <div className="flex items-center gap-1.5">
              {/* + Button with multiple Slack/Discord options */}
              <div className="relative" ref={plusMenuRef}>
                <button
                  type="button"
                  onClick={() => setPlusMenuOpen((o) => !o)}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200/80 text-slate-700 transition-colors hover:bg-slate-300 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/20"
                  title="Add attachments, snippets, and polls"
                  aria-label="Add options"
                >
                  <Plus className="h-4 w-4" />
                </button>

                {plusMenuOpen && (
                  <div className="absolute bottom-[calc(100%+8px)] left-0 z-50 w-56 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-2xl animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      <UploadCloud className="h-4 w-4 text-primary" />
                      <div>
                        <span className="block font-medium">Upload from computer</span>
                        <span className="block text-[10px] text-slate-400">
                          Share files, images or documents · up to 20 MB each
                        </span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={handleInsertSnippet}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      <Code className="h-4 w-4 text-emerald-500" />
                      <div>
                        <span className="block font-medium">Create code snippet</span>
                        <span className="block text-[10px] text-slate-400">
                          Formatted syntax code block
                        </span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPlusMenuOpen(false);
                        setPollModalOpen(true);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      <BarChart2 className="h-4 w-4 text-amber-500" />
                      <div>
                        <span className="block font-medium">Create a poll</span>
                        <span className="block text-[10px] text-slate-400">
                          Ask a question to teammates
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {allowVoiceNotes && (
                <div className="flex items-center gap-1">
                  {isRecordingVoice && (
                    <span
                      className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-1 text-[10px] font-semibold tabular-nums text-red-600 dark:bg-red-500/15 dark:text-red-300"
                      role="status"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                      {String(Math.floor(voiceRecordingSeconds / 60)).padStart(2, '0')}:
                      {String(voiceRecordingSeconds % 60).padStart(2, '0')}
                    </span>
                  )}
                  {isFinalizingVoice && (
                    <span className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                      Preparing…
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void toggleVoiceRecording()}
                    disabled={isFinalizingVoice}
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:cursor-wait disabled:opacity-60',
                      isRecordingVoice
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white'
                    )}
                    title={isRecordingVoice ? 'Stop voice note recording' : 'Record voice note'}
                    aria-label={
                      isRecordingVoice ? 'Stop voice note recording' : 'Record voice note'
                    }
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Emoji Button & Smooth Animated Picker */}
              <div className="relative" ref={emojiPickerRef}>
                <button
                  type="button"
                  onClick={() => setEmojiOpen((o) => !o)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/60 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                  title="Add emoji"
                  aria-label="Add emoji"
                >
                  <Smile className="h-4 w-4" />
                </button>

                {emojiOpen && (
                  <div className="absolute bottom-[calc(100%+8px)] left-0 z-50 grid w-52 grid-cols-4 gap-1.5 rounded-2xl border border-slate-200 bg-white/95 p-2.5 shadow-2xl backdrop-blur-md animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800/95">
                    {REACTION_CHOICES.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleAddEmoji(emoji)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-xl transition-all duration-150 hover:scale-115 hover:bg-slate-100 active:scale-95 dark:hover:bg-white/10"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* @ Mention Button & Popover */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMentionOpen((o) => !o)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/60 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                  title="Mention someone (@)"
                  aria-label="Mention someone"
                >
                  <AtSign className="h-4 w-4" />
                </button>

                <MentionPopover
                  open={mentionOpen}
                  people={people}
                  groups={mentionGroups}
                  search={mentionSearch}
                  selectedIndex={mentionSelectedIndex}
                  onSelect={handleMentionSelect}
                  onSelectGroup={handleGroupMentionSelect}
                  onClose={() => setMentionOpen(false)}
                />
              </div>
            </div>

            {/* Right Submit Button */}
            <Button
              type="submit"
              size="sm"
              className="h-7 w-7 rounded-lg p-0"
              disabled={readOnly || isRecordingVoice || isFinalizingVoice}
              aria-label="Send message"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Insert Link Modal */}
      {linkModalOpen && (
        <InsertLinkModal
          open={linkModalOpen}
          initialText={linkInitialText}
          onClose={() => setLinkModalOpen(false)}
          onInsert={handleLinkInsert}
        />
      )}

      {/* Create Poll Modal */}
      <CreatePollModal
        open={pollModalOpen}
        onClose={() => setPollModalOpen(false)}
        onCreated={handleCreatePoll}
      />
    </form>
  );
}

// ─── Sidebar Channel Item ─────────────────────────────────────────────────────
interface SidebarChannelItemProps {
  channel: ChannelData;
  isSelected: boolean;
  isPinned?: boolean;
  dmPerson?: ChatUser | null;
  /** One-to-one DMs only: the other person's online status. */
  presence?: PresenceStatus;
  onClick: () => void;
  onTogglePin?: (channel: ChannelData) => void;
  onLeave?: (channel: ChannelData) => void;
  onToggleMute?: (channel: ChannelData) => void;
  onCopyLink?: (channel: ChannelData) => void;
  onCloseDm?: (channel: ChannelData) => void;
}

function SidebarChannelItem({
  channel,
  isSelected,
  isPinned,
  dmPerson,
  presence,
  onClick,
  onTogglePin,
  onLeave,
  onToggleMute,
  onCopyLink,
  onCloseDm,
}: SidebarChannelItemProps): React.ReactElement {
  const isDM = channel.kind === 'direct' || channel.kind === 'group_dm';
  const hasUnread = channel.unreadCount > 0;
  const isMuted = channel.notificationLevel === 'muted';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  return (
    <div
      className={cn(
        'group/channel relative flex h-8 w-full items-center rounded-md text-left text-[12px] transition-colors',
        menuOpen ? 'z-30' : 'z-0',
        isSelected
          ? 'bg-primary/15 font-medium text-primary dark:bg-primary-light/15 dark:text-primary-light'
          : hasUnread
            ? 'font-semibold text-slate-900 hover:bg-slate-200/70 dark:text-gray-100 dark:hover:bg-white/[0.06]'
            : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-100'
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1 text-left"
      >
        {/* Icon */}
        <span className="relative shrink-0">
          {isDM ? (
            <>
              <Avatar
                name={channel.name}
                src={dmPerson?.avatar}
                size="xs"
                className="h-5 w-5 text-[9px]"
              />
              <PresenceDot status={presence} ringClassName="ring-slate-50 dark:ring-gray-950" />
            </>
          ) : channel.kind === 'announcement' ? (
            <Bell className="h-3.5 w-3.5 shrink-0" />
          ) : channel.visibility === 'private' ? (
            <Lock className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <Hash className="h-3.5 w-3.5 shrink-0" />
          )}
        </span>

        <span className="min-w-0 flex-1 truncate">{channel.name}</span>

        {/* Muted icon indicator if muted and no unread */}
        {isMuted && !hasUnread && (
          <VolumeX className="h-3 w-3 shrink-0 text-slate-400 dark:text-gray-500 group-hover/channel:hidden" />
        )}

        {/* Unread badge */}
        {hasUnread && !isSelected && (
          <span
            className={cn(
              'ml-auto shrink-0 min-w-4 rounded-full px-1.5 py-0.5 text-center text-[9px] font-bold leading-none text-white transition-opacity group-hover/channel:hidden',
              isMuted ? 'bg-slate-400' : 'bg-primary'
            )}
          >
            {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
          </span>
        )}
      </button>

      {/* Hover Quick Action Buttons (Discord/Slack style) */}
      <div
        className={cn(
          'absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5 transition-opacity duration-150',
          menuOpen ? 'opacity-100' : 'opacity-0 group-hover/channel:opacity-100'
        )}
      >
        {/* 3-dots Menu trigger */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            className={cn(
              'flex h-5.5 w-5.5 items-center justify-center rounded text-slate-400 hover:bg-slate-300/80 hover:text-slate-800 dark:hover:bg-white/20 dark:hover:text-white',
              menuOpen && 'bg-slate-300/80 text-slate-800 dark:bg-white/20 dark:text-white'
            )}
            title="Channel options"
            aria-label="Channel options"
            aria-expanded={menuOpen}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+4px)] z-50 w-48 rounded-xl border border-slate-200 bg-white p-1.5 shadow-2xl ring-1 ring-black/5 dark:border-white/10 dark:bg-gray-900 dark:ring-white/5"
            >
              {onTogglePin && (
                <MenuItem
                  onClick={() => {
                    onTogglePin(channel);
                    setMenuOpen(false);
                  }}
                >
                  <span>{isPinned ? 'Unpin channel' : 'Pin to top'}</span>
                </MenuItem>
              )}

              {onToggleMute && (
                <MenuItem
                  onClick={() => {
                    onToggleMute(channel);
                    setMenuOpen(false);
                  }}
                >
                  {isMuted ? (
                    <>
                      <Volume2 className="h-3.5 w-3.5" /> Unmute channel
                    </>
                  ) : (
                    <>
                      <VolumeX className="h-3.5 w-3.5" /> Mute notifications
                    </>
                  )}
                </MenuItem>
              )}

              {onCopyLink && (
                <MenuItem
                  onClick={() => {
                    onCopyLink(channel);
                    setMenuOpen(false);
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> Copy name
                </MenuItem>
              )}

              {!isDM && onLeave && (
                <>
                  <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
                  <MenuItem
                    danger
                    onClick={() => {
                      onLeave(channel);
                      setMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-3.5 w-3.5" /> Leave channel
                  </MenuItem>
                </>
              )}

              {isDM && onCloseDm && (
                <>
                  <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
                  <MenuItem
                    danger
                    onClick={() => {
                      onCloseDm(channel);
                      setMenuOpen(false);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete conversation
                  </MenuItem>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Collapsible Sidebar Section ──────────────────────────────────────────────
function SidebarSection({
  label,
  icon,
  channels,
  people,
  selectedId,
  pinnedIds = [],
  onSelect,
  onTogglePin,
  onLeave,
  onToggleMute,
  onCopyLink,
  onCloseDm,
  presence,
  onAdd,
  addLabel,
  emptyText,
}: {
  label: string;
  icon?: React.ReactNode;
  /** Shows a + next to the heading, e.g. to start a direct message. */
  onAdd?: () => void;
  addLabel?: string;
  /** Shown when the section has no channels yet. */
  emptyText?: string;
  channels: ChannelData[];
  people?: ChatUser[];
  selectedId: string | null;
  pinnedIds?: string[];
  onSelect: (id: string) => void;
  onTogglePin?: (channel: ChannelData) => void;
  onLeave?: (channel: ChannelData) => void;
  onToggleMute?: (channel: ChannelData) => void;
  onCopyLink?: (channel: ChannelData) => void;
  onCloseDm?: (channel: ChannelData) => void;
  presence?: Map<string, PresenceStatus>;
}): React.ReactElement {
  const hasUnread = channels.some((c) => c.unreadCount > 0);
  const [collapsed, setCollapsed] = useState(false);

  const findPerson = (channelName: string) => {
    if (!people) return null;
    const norm = channelName.trim().toLowerCase();
    return (
      people.find((p) => {
        const full = `${p.firstName} ${p.lastName}`.trim().toLowerCase();
        return full === norm || p.email.toLowerCase() === norm || norm.includes(full);
      }) ?? null
    );
  };

  return (
    <div className="mb-1.5">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="group flex min-w-0 flex-1 items-center gap-1 rounded px-1.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 transition-colors hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3 transition-transform" />
          ) : (
            <ChevronDown className="h-3 w-3 transition-transform" />
          )}
          {icon && <span className="shrink-0">{icon}</span>}
          <span className="flex-1 text-left truncate">{label}</span>
          {collapsed && hasUnread && <span className="h-2 w-2 rounded-full bg-primary" />}
        </button>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="mr-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200 hover:text-primary dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label={addLabel ?? `Add to ${label}`}
            title={addLabel ?? `Add to ${label}`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {!collapsed && channels.length === 0 && emptyText && (
        <p className="px-2 py-1.5 text-[11px] text-slate-400 dark:text-gray-500">{emptyText}</p>
      )}
      {!collapsed && (
        <div className="mt-0.5 space-y-0.5">
          {channels.map((ch) => {
            const dmPerson = findPerson(ch.name);
            return (
              <SidebarChannelItem
                key={ch._id}
                channel={ch}
                isPinned={pinnedIds.includes(ch._id)}
                dmPerson={dmPerson}
                presence={
                  ch.kind === 'direct' && dmPerson ? presence?.get(dmPerson._id) : undefined
                }
                isSelected={ch._id === selectedId}
                onClick={() => onSelect(ch._id)}
                onTogglePin={onTogglePin}
                onLeave={onLeave}
                onToggleMute={onToggleMute}
                onCopyLink={onCopyLink}
                onCloseDm={onCloseDm}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────

interface CreateChannelModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (ch: ChannelData) => void;
}

function CreateChannelModal({
  open,
  onClose,
  onCreated,
}: CreateChannelModalProps): React.ReactElement {
  const create = useCreateChannel();
  const { data: me } = useMe();
  const { data: organization } = useCurrentOrganization();
  const isOrganizationOwner = organization?.createdBy === me?._id;
  const role = typeof me?.roleId === 'string' ? undefined : me?.roleId;
  const isOrganizationAdmin = role?.slug === 'admin';
  const canSelectAnyCompany = isOrganizationOwner || isOrganizationAdmin;
  const { data: companies = [] } = useCompanies(
    canSelectAnyCompany ? undefined : { memberOf: 'me' },
    { enabled: open }
  );
  const [name, setName] = useState('');
  const [audience, setAudience] = useState<'all_members' | 'company'>(
    canSelectAnyCompany ? 'all_members' : 'company'
  );
  const [companyId, setCompanyId] = useState('');
  // Within a company: everyone, one department, or one manager's team.
  const [companyScope, setCompanyScope] = useState<'company' | 'department' | 'team'>('company');
  const [audienceValue, setAudienceValue] = useState('');
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [announcement, setAnnouncement] = useState(false);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [messagePermission, setMessagePermission] = useState<'everyone' | 'owner_only'>('everyone');
  const companyPickerRef = useRef<HTMLDivElement>(null);
  const memberPickerRef = useRef<HTMLDivElement>(null);
  const activeCompanies = useMemo(
    () => companies.filter((company) => company.status === 'active'),
    [companies]
  );
  const canChooseCompany = canSelectAnyCompany || activeCompanies.length > 1;
  const selectedCompanyId =
    audience === 'company'
      ? companyId || (!canChooseCompany ? (activeCompanies[0]?._id ?? '') : '')
      : '';
  const selectedCompany = activeCompanies.find((company) => company._id === selectedCompanyId);
  const isAudienceScope = audience === 'company' && companyScope !== 'company';
  const { data: audiences, isLoading: audiencesLoading } = useChannelAudiences(
    selectedCompanyId || null,
    open && isAudienceScope
  );
  const audienceOptions =
    companyScope === 'department'
      ? (audiences?.departments ?? []).map((item) => ({
          value: item.name,
          label: item.name,
          count: item.memberCount,
        }))
      : (audiences?.teams ?? []).map((item) => ({
          value: item.managerId,
          label: `${item.managerName}'s team`,
          count: item.memberCount,
        }));
  const matchingCompanies = activeCompanies.filter((company) => {
    const search = companySearch.trim().toLowerCase();
    return !search || `${company.name} ${company.code ?? ''}`.toLowerCase().includes(search);
  });
  const { data: selectableMembers = [] } = useChannelCreationMembers(
    selectedCompanyId || null,
    open && visibility === 'private'
  );
  const matchingMembers = selectableMembers.filter((member) => {
    const search = memberSearch.trim().toLowerCase();
    const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''} ${member.email ?? ''}`;
    return !search || memberName.toLowerCase().includes(search);
  });
  const allMembersSelected =
    selectableMembers.length > 0 &&
    selectableMembers.every((member) => memberIds.includes(member._id));

  useEffect(() => {
    if (!companyPickerOpen && !memberPickerOpen) return;
    const closePickers = (event: MouseEvent) => {
      const target = event.target as Node;
      if (companyPickerOpen && !companyPickerRef.current?.contains(target)) {
        setCompanyPickerOpen(false);
        setCompanySearch('');
      }
      if (memberPickerOpen && !memberPickerRef.current?.contains(target)) {
        setMemberPickerOpen(false);
        setMemberSearch('');
      }
    };
    document.addEventListener('mousedown', closePickers);
    return () => document.removeEventListener('mousedown', closePickers);
  }, [companyPickerOpen, memberPickerOpen]);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const channel = await create.mutateAsync({
        name: name.trim().replace(/\s+/g, ' '),
        ...(audience === 'company' ? { companyId: selectedCompanyId } : {}),
        // Department/team channels are always public: membership follows profiles.
        ...(isAudienceScope ? { groupType: companyScope, groupValue: audienceValue } : {}),
        description: description || undefined,
        visibility: isAudienceScope ? 'public' : visibility,
        kind: announcement ? 'announcement' : 'standard',
        isReadOnly: announcement,
        messagePermission,
        memberIds: isAudienceScope ? [] : memberIds,
      });
      setName('');
      setAudience(canSelectAnyCompany ? 'all_members' : 'company');
      setCompanyId('');
      setCompanyScope('company');
      setAudienceValue('');
      setCompanyPickerOpen(false);
      setCompanySearch('');
      setDescription('');
      setMemberIds([]);
      setMemberPickerOpen(false);
      setMemberSearch('');
      setMessagePermission('everyone');
      onCreated(channel);
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create channel"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-channel"
            disabled={
              !name.trim() ||
              (audience === 'company' && !selectedCompanyId) ||
              (isAudienceScope && !audienceValue)
            }
            loading={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create channel'}
          </Button>
        </>
      }
    >
      <form id="create-channel" onSubmit={submit} className="space-y-4">
        <label className="block text-xs font-medium text-slate-700 dark:text-gray-200">
          Channel name
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="e.g. Albatross Cars Team"
            className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </label>
        {canSelectAnyCompany && (
          <div>
            <p className="text-xs font-medium text-slate-700 dark:text-gray-200">
              Channel audience
            </p>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setAudience('all_members');
                  setMemberIds([]);
                  setMemberPickerOpen(false);
                }}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-left transition-colors',
                  audience === 'all_members'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20'
                )}
              >
                <span className="block text-xs font-semibold text-slate-800 dark:text-gray-100">
                  All Members
                </span>
                <span className="mt-0.5 block text-[10px] text-slate-500">
                  Organization-wide channel
                </span>
              </button>
              <button
                type="button"
                onClick={() => setAudience('company')}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-left transition-colors',
                  audience === 'company'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20'
                )}
              >
                <span className="block text-xs font-semibold text-slate-800 dark:text-gray-100">
                  Select Company
                </span>
                <span className="mt-0.5 block text-[10px] text-slate-500">
                  Limit the channel to one company
                </span>
              </button>
            </div>
          </div>
        )}
        {audience === 'company' && (
          <label className="block text-xs font-medium text-slate-700 dark:text-gray-200">
            Company
            {canChooseCompany ? (
              <div ref={companyPickerRef} className="relative mt-1.5">
                <button
                  type="button"
                  onClick={() => setCompanyPickerOpen((current) => !current)}
                  className="flex h-10 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 text-left text-sm text-slate-800 outline-none transition-colors hover:border-primary/50 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04] dark:text-gray-100"
                  aria-expanded={companyPickerOpen}
                  aria-haspopup="listbox"
                >
                  <span className={cn('truncate', !selectedCompany && 'text-slate-400')}>
                    {selectedCompany?.name ?? 'Select a company'}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-slate-400 transition-transform',
                      companyPickerOpen && 'rotate-180'
                    )}
                  />
                </button>
                {companyPickerOpen && (
                  <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-gray-800">
                    <div className="relative mb-1.5">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input
                        autoFocus
                        value={companySearch}
                        onChange={(event) => setCompanySearch(event.target.value)}
                        placeholder="Search companies…"
                        className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 pl-8 pr-2 text-xs text-slate-800 outline-none focus:border-primary/50 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-100"
                      />
                    </div>
                    <div className="max-h-44 overflow-y-auto chat-scroll" role="listbox">
                      {matchingCompanies.length ? (
                        matchingCompanies.map((company) => (
                          <button
                            key={company._id}
                            type="button"
                            role="option"
                            aria-selected={company._id === selectedCompanyId}
                            onClick={() => {
                              setCompanyId(company._id);
                              setAudienceValue('');
                              setMemberIds([]);
                              setMemberPickerOpen(false);
                              setMemberSearch('');
                              setCompanyPickerOpen(false);
                              setCompanySearch('');
                            }}
                            className={cn(
                              'flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-xs transition-colors hover:bg-slate-100 dark:hover:bg-white/10',
                              company._id === selectedCompanyId &&
                                'bg-primary/10 text-primary dark:bg-primary/20'
                            )}
                          >
                            <span className="truncate">{company.name}</span>
                            {company._id === selectedCompanyId && (
                              <Check className="h-3.5 w-3.5 shrink-0" />
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="px-2 py-3 text-center text-[11px] text-slate-400">
                          No companies found.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-1.5 flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-200">
                {selectedCompany?.name ?? 'No active company assigned'}
              </div>
            )}
            <span className="mt-1 block text-[10px] font-normal text-slate-500">
              {canChooseCompany
                ? 'Choose the company this channel belongs to.'
                : 'Channels you create belong to your current company.'}
            </span>
          </label>
        )}
        {audience === 'company' && selectedCompanyId && (
          <div>
            <p className="text-xs font-medium text-slate-700 dark:text-gray-200">
              Who&apos;s in it
            </p>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {(
                [
                  { value: 'company', label: 'Whole company', hint: 'Every member' },
                  { value: 'department', label: 'A department', hint: 'From profiles' },
                  { value: 'team', label: 'A team', hint: 'Manager + reports' },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setCompanyScope(option.value);
                    setAudienceValue('');
                    if (option.value !== 'company') {
                      // Audience channels take everyone in it; no invite list.
                      setVisibility('public');
                      setMemberIds([]);
                      setMemberPickerOpen(false);
                    }
                  }}
                  className={cn(
                    'rounded-lg border px-2.5 py-2 text-left transition-colors',
                    companyScope === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20'
                  )}
                >
                  <span className="block text-xs font-semibold text-slate-800 dark:text-gray-100">
                    {option.label}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">{option.hint}</span>
                </button>
              ))}
            </div>
            {isAudienceScope && (
              <div className="mt-2">
                <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-1 chat-scroll dark:border-white/10">
                  {audiencesLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    </div>
                  ) : audienceOptions.length === 0 ? (
                    <p className="px-2 py-3 text-center text-[11px] text-slate-500">
                      {companyScope === 'department'
                        ? 'No departments are set on member profiles in this company.'
                        : 'Nobody in this company has a "Reports to" manager yet.'}
                    </p>
                  ) : (
                    audienceOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setAudienceValue(option.value)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors',
                          audienceValue === option.value
                            ? 'bg-primary/10 text-primary dark:bg-primary/20'
                            : 'text-slate-700 hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/10'
                        )}
                      >
                        <span className="truncate">{option.label}</span>
                        <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-slate-400">
                          {option.count} {option.count === 1 ? 'person' : 'people'}
                          {audienceValue === option.value && (
                            <Check className="h-3.5 w-3.5 text-primary" />
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
                <p className="mt-1 text-[10px] text-slate-500">
                  {companyScope === 'department'
                    ? 'Uses the Department on each member’s profile. People who move in or out are updated automatically.'
                    : 'The manager plus everyone whose “Reports to” is that manager. Changes in reporting update membership automatically.'}
                </p>
              </div>
            )}
          </div>
        )}
        {audience === 'all_members' && (
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-300">
            This channel will include every active member in the organization. No company selection
            is required.
          </p>
        )}
        {visibility === 'private' && selectedCompanyId && (
          <div ref={memberPickerRef} className="relative">
            <p className="mb-1.5 text-xs font-medium text-slate-700 dark:text-gray-200">
              Members <span className="font-normal text-slate-400">(optional)</span>
            </p>
            <button
              type="button"
              onClick={() => setMemberPickerOpen((current) => !current)}
              className="flex h-10 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 text-left text-sm text-slate-700 transition-colors hover:border-primary/50 dark:border-white/15 dark:bg-white/[0.04] dark:text-gray-200"
              aria-expanded={memberPickerOpen}
              aria-haspopup="listbox"
            >
              <span>
                {memberIds.length === 0
                  ? 'Select members'
                  : `${memberIds.length} member${memberIds.length === 1 ? '' : 's'} selected`}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-slate-400 transition-transform',
                  memberPickerOpen && 'rotate-180'
                )}
              />
            </button>
            {memberPickerOpen && (
              <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-gray-800">
                <div className="relative mb-1.5">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                    placeholder="Search members…"
                    className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 pl-8 pr-2 text-xs text-slate-800 outline-none focus:border-primary/50 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-100"
                  />
                </div>
                <div className="max-h-44 space-y-0.5 overflow-y-auto chat-scroll" role="listbox">
                  {selectableMembers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setMemberIds((current) =>
                          allMembersSelected
                            ? current.filter(
                                (id) => !selectableMembers.some((member) => member._id === id)
                              )
                            : Array.from(
                                new Set([
                                  ...current,
                                  ...selectableMembers.map((member) => member._id),
                                ])
                              )
                        );
                        setMemberPickerOpen(false);
                        setMemberSearch('');
                      }}
                      className="mb-1 flex w-full items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-left text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200 dark:hover:bg-white/[0.08]"
                    >
                      <span
                        className={cn(
                          'flex h-3.5 w-3.5 items-center justify-center rounded border',
                          allMembersSelected
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-300 bg-white dark:border-white/25 dark:bg-transparent'
                        )}
                      >
                        {allMembersSelected && <Check className="h-3 w-3 stroke-[3]" />}
                      </span>
                      {allMembersSelected ? 'Clear all members' : 'Select all members'}
                    </button>
                  )}
                  {matchingMembers.length === 0 ? (
                    <p className="px-2 py-3 text-center text-[11px] text-slate-400">
                      No active members found.
                    </p>
                  ) : (
                    matchingMembers.map((member) => {
                      const memberId = member._id;
                      const memberName =
                        `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
                        member.email ||
                        'Member';
                      const selected = memberIds.includes(memberId);
                      return (
                        <label
                          key={memberId}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.05]"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              setMemberIds((current) =>
                                selected
                                  ? current.filter((id) => id !== memberId)
                                  : [...current, memberId]
                              );
                              setMemberPickerOpen(false);
                              setMemberSearch('');
                            }}
                            className="h-3.5 w-3.5 accent-primary"
                          />
                          <Avatar name={memberName} size="sm" />
                          <span className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-gray-200">
                            {memberName}
                          </span>
                          {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            <p className="mt-1 text-[10px] text-slate-500">
              Active members of the selected company, plus managers, are available.
            </p>
          </div>
        )}
        <label className="block text-xs font-medium text-slate-700 dark:text-gray-200">
          Description <span className="font-normal text-slate-400">(optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What should this channel be used for?"
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </label>
        {isAudienceScope ? (
          <p className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-300">
            <Hash className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            Public within the {companyScope === 'department' ? 'department' : 'team'}: everyone in
            it is added automatically, and nobody outside it can join.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                setVisibility('public');
                setMemberIds([]);
                setMemberPickerOpen(false);
                setMemberSearch('');
              }}
              className={cn(
                'rounded-lg border p-3 text-left',
                visibility === 'public'
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 dark:border-white/10'
              )}
            >
              <Hash className="mb-1 h-4 w-4 text-primary" />
              <span className="block text-xs font-semibold">Public</span>
              <span className="text-[10px] text-slate-500">
                Automatically includes everyone in the selected audience
              </span>
            </button>
            <button
              type="button"
              onClick={() => setVisibility('private')}
              className={cn(
                'rounded-lg border p-3 text-left',
                visibility === 'private'
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 dark:border-white/10'
              )}
            >
              <Lock className="mb-1 h-4 w-4 text-primary" />
              <span className="block text-xs font-semibold">Private</span>
              <span className="text-[10px] text-slate-500">Invite members after creating</span>
            </button>
          </div>
        )}
        <div>
          <p className="mb-2 text-xs font-medium text-slate-700 dark:text-gray-200">
            Message permissions
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMessagePermission('everyone')}
              className={cn(
                'rounded-lg border p-3 text-left',
                messagePermission === 'everyone'
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 dark:border-white/10'
              )}
            >
              <span className="block text-xs font-semibold">Everyone can send messages</span>
              <span className="mt-0.5 block text-[10px] text-slate-500">
                All channel members can read and post.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMessagePermission('owner_only')}
              className={cn(
                'rounded-lg border p-3 text-left',
                messagePermission === 'owner_only'
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 dark:border-white/10'
              )}
            >
              <span className="block text-xs font-semibold">Only channel owner can send</span>
              <span className="mt-0.5 block text-[10px] text-slate-500">
                Other members can read messages only.
              </span>
            </button>
          </div>
        </div>
        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 p-3 text-xs dark:border-white/10">
          <span>
            <span className="block font-semibold">Announcement channel</span>
            <span className="text-slate-500">Members can read; owners and moderators post.</span>
          </span>
          <input
            type="checkbox"
            checked={announcement}
            onChange={(e) => setAnnouncement(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
        </label>
      </form>
    </Modal>
  );
}

interface DirectMessageModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (ch: ChannelData) => void;
}

/** Matches the page size of GET /channels/people. */
const CHAT_PEOPLE_PAGE_SIZE = 50;

function DirectMessageModal({
  open,
  onClose,
  onCreated,
}: DirectMessageModalProps): React.ReactElement {
  const create = useCreateDirectChannel();
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  // The API returns a capped page, so searching must go to the server to reach everyone.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  const { data: people = [], isFetching } = useChatPeople(open, debouncedQuery);
  const hasMore = !query.trim() && people.length >= CHAT_PEOPLE_PAGE_SIZE;
  const shown = people.filter((p) =>
    `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(query.toLowerCase())
  );
  const presence = usePresence(
    shown.map((person) => person._id),
    open
  );
  const submit = async () => {
    if (selected.length === 0) return;
    try {
      const channel = await create.mutateAsync({ userIds: selected });
      setSelected([]);
      onCreated(channel);
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New message"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={selected.length === 0}
            loading={create.isPending}
          >
            {create.isPending ? 'Opening…' : 'Open conversation'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500 dark:text-gray-400">
          Select one person for a private direct chat. Select multiple people only when you need a
          group conversation.
        </p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people…"
            className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-primary dark:border-white/15 dark:bg-white/[0.04]"
          />
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {shown.length === 0 && !isFetching && (
            <p className="px-2 py-8 text-center text-xs text-slate-500 dark:text-gray-400">
              {query.trim()
                ? `No one you can message matches “${query.trim()}”.`
                : 'No members are currently available for a direct chat.'}
            </p>
          )}
          {shown.map((person) => {
            const checked = selected.includes(person._id);
            const name = `${person.firstName} ${person.lastName}`.trim() || person.email;
            return (
              <button
                key={person._id}
                type="button"
                onClick={() =>
                  setSelected((cur) =>
                    checked ? cur.filter((id) => id !== person._id) : [...cur, person._id]
                  )
                }
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.05]',
                  checked && 'bg-primary/5'
                )}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={checked}
                  className="h-4 w-4 accent-primary"
                />
                <span className="relative shrink-0">
                  <Avatar name={name} src={person.avatar} size="sm" />
                  <PresenceDot
                    status={presence.get(person._id)}
                    ringClassName="ring-white dark:ring-gray-800"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-slate-800 dark:text-gray-100">
                    {name}
                  </span>
                  <span className="block truncate text-[10px] text-slate-500">
                    {person.employment?.department ?? person.email}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {hasMore && (
          <p className="text-[11px] text-slate-500 dark:text-gray-400">
            Showing the first {CHAT_PEOPLE_PAGE_SIZE} people. Search by name or email to find anyone
            else.
          </p>
        )}
      </div>
    </Modal>
  );
}

interface MembersModalProps {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
}

const ROLE_OPTIONS: Array<{
  value: 'member' | 'moderator';
  label: string;
  description: string;
}> = [
  { value: 'member', label: 'Member', description: 'Can read and post messages' },
  {
    value: 'moderator',
    label: 'Moderator',
    description: 'Can add or remove members, pin and delete messages',
  },
];

const ROLE_MENU_WIDTH = 240;

/**
 * Role pill for a member row. With management rights it opens a small menu
 * (role choices and "Remove from channel"); otherwise it is a plain label.
 * The menu is portalled so the scrolling member list cannot clip it.
 */
function MemberRoleMenu({
  role,
  name,
  canChangeRole,
  canRemove,
  reserveActionSpace = false,
  onChangeRole,
  onRemove,
}: {
  role: ChannelMemberData['role'];
  name: string;
  canChangeRole: boolean;
  canRemove: boolean;
  /** True when other rows show a ⋯ button, so this row keeps the same width. */
  reserveActionSpace?: boolean;
  onChangeRole: (role: 'member' | 'moderator') => void;
  onRemove: () => void;
}): React.ReactElement {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const label = role === 'owner' ? 'Owner' : role === 'moderator' ? 'Moderator' : 'Member';

  useEffect(() => {
    if (!position) return;
    const close = () => setPosition(null);
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close();
    };
    // Escape closes only the menu, not the members modal behind it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', close);
    // A scrolled list would leave the fixed menu floating away from its row.
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [position]);

  const roleLabel = (
    <span
      className={cn(
        'shrink-0 px-1 text-[11px]',
        role === 'owner'
          ? 'font-medium text-primary dark:text-primary-light'
          : 'text-slate-400 dark:text-gray-500'
      )}
    >
      {label}
    </span>
  );

  if (!canChangeRole && !canRemove) {
    return (
      <>
        {roleLabel}
        {/* Keeps role labels aligned with rows that have a ⋯ button. */}
        {reserveActionSpace && <span aria-hidden className="w-7 shrink-0" />}
      </>
    );
  }

  const toggle = () => {
    if (position) {
      setPosition(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const estimatedHeight = (canChangeRole ? 112 : 0) + (canRemove ? 44 : 0);
    const fitsBelow = rect.bottom + 4 + estimatedHeight < window.innerHeight - 8;
    setPosition({
      top: fitsBelow ? rect.bottom + 4 : Math.max(8, rect.top - 4 - estimatedHeight),
      left: Math.max(
        8,
        Math.min(rect.right - ROLE_MENU_WIDTH, window.innerWidth - ROLE_MENU_WIDTH - 8)
      ),
    });
  };

  const choose = (action: () => void) => {
    setPosition(null);
    action();
  };

  return (
    <>
      {roleLabel}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={Boolean(position)}
        aria-label={`Options for ${name}`}
        title="Member options"
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-800 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-white',
          position && 'bg-slate-200/70 text-slate-800 dark:bg-white/10 dark:text-white'
        )}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: position.top, left: position.left, width: ROLE_MENU_WIDTH }}
            className="fixed z-[60] rounded-xl border border-slate-200 bg-white p-1 shadow-xl animate-in fade-in zoom-in-95 duration-100 dark:border-white/10 dark:bg-gray-800"
          >
            {canChangeRole &&
              ROLE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={role === option.value}
                  onClick={() => choose(() => onChangeRole(option.value))}
                  className="flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-slate-100 dark:hover:bg-white/[0.06]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-medium text-slate-800 dark:text-gray-100">
                      {option.label}
                    </span>
                    <span className="block text-[10px] leading-4 text-slate-500 dark:text-gray-400">
                      {option.description}
                    </span>
                  </span>
                  {role === option.value && (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary dark:text-primary-light" />
                  )}
                </button>
              ))}
            {canChangeRole && canRemove && (
              <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
            )}
            {canRemove && (
              <MenuItem danger onClick={() => choose(onRemove)}>
                <UserMinus className="h-3.5 w-3.5" /> Remove from channel
              </MenuItem>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

function MembersModal({ channel, open, onClose }: MembersModalProps): React.ReactElement {
  const addMember = useAddChannelMember();
  const { data: members = [] } = useChannelMembers(channel?._id ?? null);
  const canInvite =
    channel?.currentUserRole === 'owner' || channel?.currentUserRole === 'moderator';
  const { data: people = [] } = useEligibleChannelMembers(channel?._id ?? null, open && canInvite);
  const [query, setQuery] = useState('');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const memberIds = new Set(
    members.map((m) => (typeof m.userId === 'string' ? m.userId : m.userId._id))
  );
  const presence = usePresence([...memberIds], open);
  const presenceRank = (status?: PresenceStatus) =>
    status === 'online' ? 0 : status === 'idle' ? 1 : 2;
  const roleRank = { owner: 0, moderator: 1, member: 2 } as const;
  const [memberQuery, setMemberQuery] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const { data: me } = useMe();
  const changeRole = useChangeMemberRole();
  const removeFromChannel = useRemoveMemberFromChannel();
  // Management follows the API's rules; the API enforces them regardless.
  const myRole = channel?.currentUserRole ?? null;
  const isManageable =
    Boolean(channel) &&
    channel?.kind !== 'direct' &&
    channel?.kind !== 'group_dm' &&
    !channel?.archivedAt;
  const canChangeRoles = isManageable && myRole === 'owner';
  const canRemove = (member: ChannelMemberData, userId: string) =>
    isManageable &&
    userId !== me?._id &&
    member.role !== 'owner' &&
    (myRole === 'owner' || (myRole === 'moderator' && member.role === 'member'));
  // Owner, then moderators, then members; present people first within each.
  const sortedMembers = members
    .map((member, index) => {
      const userId = typeof member.userId === 'string' ? member.userId : member.userId._id;
      return { member, index, userId, status: presence.get(userId) };
    })
    .sort(
      (a, b) =>
        roleRank[a.member.role] - roleRank[b.member.role] ||
        presenceRank(a.status) - presenceRank(b.status) ||
        a.index - b.index
    );
  const onlineCount = sortedMembers.filter(({ status }) => status === 'online').length;
  const visibleMembers = sortedMembers.filter(({ member }) => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return true;
    const person = typeof member.userId === 'string' ? null : member.userId;
    return person
      ? `${person.firstName} ${person.lastName} ${person.email}`.toLowerCase().includes(q)
      : false;
  });
  const memberName = (member: ChannelMemberData) => {
    const person = typeof member.userId === 'string' ? null : member.userId;
    return person
      ? `${person.firstName} ${person.lastName}`.trim() || person.email
      : 'Former member';
  };
  const handleRoleChange = (
    member: ChannelMemberData,
    userId: string,
    role: 'moderator' | 'member'
  ) => {
    if (!channel || role === member.role) return;
    changeRole.mutate(
      { channelId: channel._id, memberId: userId, role },
      {
        onSuccess: () =>
          toast.success(
            role === 'moderator'
              ? `${memberName(member)} is now a moderator`
              : `${memberName(member)} is now a member`
          ),
        onError: (error) => toast.error(getErrorMessage(error)),
      }
    );
  };
  const handleRemove = (member: ChannelMemberData, userId: string) => {
    if (!channel) return;
    setConfirmRemoveId(null);
    removeFromChannel.mutate(
      { channelId: channel._id, memberId: userId },
      {
        onSuccess: () => toast.success(`Removed ${memberName(member)} from #${channel.name}`),
        onError: (error) => toast.error(getErrorMessage(error)),
      }
    );
  };
  const candidates = people.filter(
    (p) =>
      !memberIds.has(p._id) &&
      `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(query.toLowerCase())
  );
  // Optimistic: the person joins the member list (and leaves the candidates)
  // at once; the hook takes them out again if the API refuses.
  const invite = (person: ChatUser) => {
    if (!channel) return;
    toast.success('Member invited');
    addMember.mutate(
      { channelId: channel._id, person },
      { onError: (error) => toast.error(getErrorMessage(error)) }
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Members · ${channel?.name ?? ''}`}
      className="max-w-lg"
    >
      <div className="space-y-4">
        {canInvite && channel?.kind !== 'direct' && (
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-slate-700 dark:text-gray-200">Add members</p>
                <p className="mt-0.5 text-[10px] text-slate-500">
                  Active company users and managers are available.
                </p>
              </div>
              <Button size="sm" onClick={() => setAddMemberOpen((current) => !current)}>
                <Plus className="h-3.5 w-3.5" />
                Add member
              </Button>
            </div>
            {addMemberOpen && (
              <>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search company users…"
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-primary dark:border-white/15 dark:bg-white/[0.04]"
                  />
                </div>
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-1 dark:border-white/10">
                  {candidates.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-slate-500">
                      No matching company users available.
                    </p>
                  ) : (
                    candidates.map((p) => {
                      const name = `${p.firstName} ${p.lastName}`.trim() || p.email;
                      return (
                        <div
                          key={p._id}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                        >
                          <Avatar name={name} src={p.avatar} size="sm" />
                          <span className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-gray-200">
                            {name}
                          </span>
                          <Button size="sm" onClick={() => invite(p)}>
                            Add
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-slate-700 dark:text-gray-200">Channel members</p>
            {members.length > 6 && (
              <div className="relative w-40">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                <input
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Find a member…"
                  aria-label="Find a member"
                  className="h-7 w-full rounded-md border border-slate-300 bg-white pl-6 pr-2 text-[11px] text-slate-700 placeholder:text-slate-400 outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200"
                />
              </div>
            )}
          </div>
          <div className="-mx-1 max-h-72 overflow-y-auto">
            {visibleMembers.length === 0 && (
              <p className="px-2 py-4 text-center text-xs text-slate-500">
                No members match &ldquo;{memberQuery.trim()}&rdquo;
              </p>
            )}
            {visibleMembers.map(({ member: m, status, userId }) => {
              const person = typeof m.userId === 'string' ? null : m.userId;
              const name = memberName(m);
              const isMe = userId === me?._id;
              const confirming = confirmRemoveId === userId;
              return (
                <div
                  key={m._id}
                  className={cn(
                    'flex min-h-11 items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors',
                    confirming
                      ? 'bg-red-50 dark:bg-red-500/10'
                      : 'hover:bg-slate-50 dark:hover:bg-white/[0.03]'
                  )}
                >
                  <span className="relative shrink-0">
                    <Avatar name={name} src={person?.avatar} size="sm" />
                    <PresenceDot status={status} ringClassName="ring-white dark:ring-gray-800" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-medium text-slate-800 dark:text-gray-100">
                      {name}
                      {isMe && (
                        <span className="font-normal text-slate-400 dark:text-gray-500">
                          {' '}
                          · you
                        </span>
                      )}
                    </span>
                    {person?.employment?.department && (
                      <span className="block truncate text-[11px] text-slate-500 dark:text-gray-400">
                        {person.employment.department}
                      </span>
                    )}
                  </span>
                  {confirming ? (
                    <span className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveId(null)}
                        className="h-7 rounded-md px-2.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-white dark:text-gray-300 dark:hover:bg-white/10"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(m, userId)}
                        aria-label={`Yes, remove ${name}`}
                        className="h-7 rounded-md bg-danger px-2.5 text-[11px] font-medium text-white transition-colors hover:bg-danger/90"
                      >
                        Yes
                      </button>
                    </span>
                  ) : (
                    <MemberRoleMenu
                      role={m.role}
                      name={name}
                      canChangeRole={canChangeRoles && m.role !== 'owner'}
                      canRemove={canRemove(m, userId)}
                      reserveActionSpace={
                        isManageable && (myRole === 'owner' || myRole === 'moderator')
                      }
                      onChangeRole={(role) => handleRoleChange(m, userId, role)}
                      onRemove={() => setConfirmRemoveId(userId)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-2 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500 dark:border-white/10 dark:text-gray-400">
            Total members: {members.length}
            {onlineCount > 0 && (
              <>
                <span aria-hidden className="text-slate-300 dark:text-gray-600">
                  ·
                </span>
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {onlineCount} online
              </>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}

interface ChannelModalProps {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
}

function ChannelDetailsModal({ channel, open, onClose }: ChannelModalProps): React.ReactElement {
  const { data: members = [] } = useChannelMembers(open ? (channel?._id ?? null) : null);
  if (!channel)
    return (
      <Modal open={open} onClose={onClose} title="Channel details">
        <p className="text-xs text-slate-500">No channel selected.</p>
      </Modal>
    );
  const visibility =
    channel.visibility === 'private'
      ? 'Private · invite only'
      : channel.companyId
        ? 'Public · company members'
        : 'Public · organization members';
  const group = displayChannelGroupLabel(channel);
  const memberCount = members.length || channel.memberCount || 0;
  const creator = channel.createdByUser;
  const creatorName = creator
    ? `${creator.firstName} ${creator.lastName}`.trim() || creator.email
    : 'Unknown';
  return (
    <Modal open={open} onClose={onClose} title="Channel details" className="max-w-md">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/[0.035]">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Hash className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-slate-900 dark:text-white">
              {channel.name}
            </p>
            <p className="text-[10px] text-slate-500">{memberCount} members</p>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <dt className="text-[10px] text-slate-400">Access</dt>
            <dd className="mt-0.5 font-medium text-slate-700 dark:text-gray-200">{visibility}</dd>
          </div>
          <div>
            <dt className="text-[10px] text-slate-400">Channel group</dt>
            <dd className="mt-0.5 font-medium text-slate-700 dark:text-gray-200">{group}</dd>
          </div>
          <div>
            <dt className="text-[10px] text-slate-400">Posting</dt>
            <dd className="mt-0.5 font-medium text-slate-700 dark:text-gray-200">
              {channel.messagePermission === 'owner_only'
                ? 'Channel owner only'
                : channel.isReadOnly || channel.kind === 'announcement'
                  ? 'Owners and moderators'
                  : 'All members'}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-slate-400">Your role</dt>
            <dd className="mt-0.5 font-medium capitalize text-slate-700 dark:text-gray-200">
              {channel.currentUserRole}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-[10px] text-slate-400">Created by</dt>
            <dd className="mt-0.5 flex items-center gap-2 font-medium text-slate-700 dark:text-gray-200">
              <Avatar name={creatorName} src={creator?.avatar} size="sm" />
              <span className="truncate">{creatorName}</span>
            </dd>
          </div>
        </dl>
        <section>
          <p className="mb-1.5 text-xs font-medium text-slate-700 dark:text-gray-200">
            Members · {memberCount}
          </p>
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-1 dark:border-white/10">
            {members.length === 0 ? (
              <p className="px-2 py-3 text-xs text-slate-500">No active members found.</p>
            ) : (
              members.map((member) => {
                const person = typeof member.userId === 'string' ? null : member.userId;
                const name = person
                  ? `${person.firstName} ${person.lastName}`.trim() || person.email
                  : 'Member';
                return (
                  <div key={member._id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                    <Avatar name={name} src={person?.avatar} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-800 dark:text-gray-100">
                      {name}
                    </span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold capitalize text-slate-500 dark:bg-white/10 dark:text-gray-400">
                      {member.role}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

interface ChannelSettingsModalProps {
  channel: ChannelData;
  onClose: () => void;
}

/** Owner-only editor for a channel's name, description, access, and posting rules. */
function ChannelSettingsModal({ channel, onClose }: ChannelSettingsModalProps): React.ReactElement {
  const update = useUpdateChannel();
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description ?? '');
  const [visibility, setVisibility] = useState(channel.visibility);
  const [messagePermission, setMessagePermission] = useState(channel.messagePermission);
  const [isReadOnly, setIsReadOnly] = useState(channel.isReadOnly);
  const isAnnouncement = channel.kind === 'announcement';
  const audience = channel.companyId ? 'company' : 'organization';
  const optionClass = (active: boolean) =>
    cn(
      'rounded-lg border p-3 text-left',
      active ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-white/10'
    );

  // Optimistic: the new settings show at once and the modal closes; if the API
  // refuses them the channel reverts and the error explains why.
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    update.mutate(
      {
        channelId: channel._id,
        payload: {
          name: name.trim().replace(/\s+/g, ' '),
          description: description.trim(),
          visibility,
          messagePermission,
          ...(isAnnouncement ? {} : { isReadOnly }),
        },
      },
      { onError: (error) => toast.error(getErrorMessage(error)) }
    );
    toast.success('Channel settings saved');
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Channel settings"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="channel-settings"
            disabled={!name.trim()}
            loading={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="channel-settings" onSubmit={submit} className="space-y-4">
        <label className="block text-xs font-medium text-slate-700 dark:text-gray-200">
          Channel name
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </label>
        <label className="block text-xs font-medium text-slate-700 dark:text-gray-200">
          Description <span className="font-normal text-slate-400">(optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="What should this channel be used for?"
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-white/15 dark:bg-white/[0.04]"
          />
        </label>
        <div>
          <p className="mb-2 text-xs font-medium text-slate-700 dark:text-gray-200">Access</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setVisibility('public')}
              className={optionClass(visibility === 'public')}
            >
              <Hash className="mb-1 h-4 w-4 text-primary" />
              <span className="block text-xs font-semibold">Public</span>
              <span className="text-[10px] text-slate-500">
                {audience === 'company'
                  ? 'Everyone in this company can see and join'
                  : 'Everyone in the organization can see and join'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setVisibility('private')}
              className={optionClass(visibility === 'private')}
            >
              <Lock className="mb-1 h-4 w-4 text-primary" />
              <span className="block text-xs font-semibold">Private</span>
              <span className="text-[10px] text-slate-500">Invite only</span>
            </button>
          </div>
          {channel.visibility === 'public' && visibility === 'private' && (
            <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400">
              Current members keep access. Remove anyone who should not stay from View members.
            </p>
          )}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium text-slate-700 dark:text-gray-200">
            Message permissions
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMessagePermission('everyone')}
              className={optionClass(messagePermission === 'everyone')}
            >
              <span className="block text-xs font-semibold">Everyone can send messages</span>
              <span className="mt-0.5 block text-[10px] text-slate-500">
                All channel members can read and post.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMessagePermission('owner_only')}
              className={optionClass(messagePermission === 'owner_only')}
            >
              <span className="block text-xs font-semibold">Only channel owner can send</span>
              <span className="mt-0.5 block text-[10px] text-slate-500">
                Other members can read messages only.
              </span>
            </button>
          </div>
        </div>
        <label
          className={cn(
            'flex items-center justify-between rounded-lg border border-slate-200 p-3 text-xs dark:border-white/10',
            isAnnouncement ? 'opacity-60' : 'cursor-pointer'
          )}
        >
          <span>
            <span className="block font-semibold">Read-only for members</span>
            <span className="text-slate-500">
              {isAnnouncement
                ? 'Announcement channels are always read-only for members.'
                : 'Members can read; owners and moderators post.'}
            </span>
          </span>
          <input
            type="checkbox"
            checked={isAnnouncement || isReadOnly}
            disabled={isAnnouncement}
            onChange={(e) => setIsReadOnly(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
        </label>
      </form>
    </Modal>
  );
}

function PinnedMessagesModal({ channel, open, onClose }: ChannelModalProps): React.ReactElement {
  const { data: pinned = [], isLoading } = useQuery({
    queryKey: ['channels', 'pins', channel?._id],
    queryFn: () => getPinnedMessages(channel!._id),
    enabled: open && Boolean(channel),
    staleTime: 30_000,
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Pinned messages · ${channel?.name ?? ''}`}
      className="max-w-lg"
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </div>
      ) : pinned.length === 0 ? (
        <p className="py-8 text-center text-xs text-slate-500">
          No pinned messages in this channel.
        </p>
      ) : (
        <div className="max-h-[25rem] space-y-2 overflow-y-auto pr-1">
          {pinned.map((msg) => (
            <article
              key={msg._id}
              className="rounded-lg border border-slate-200 p-3 dark:border-white/10"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-slate-800 dark:text-gray-100">
                  {personName(msg.senderId)}
                </span>
                <time className="shrink-0 text-[10px] text-slate-400">
                  {formatTime(msg.createdAt)}
                </time>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600 dark:text-gray-300">
                {msg.content || 'Attachment'}
              </p>
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}

interface ConfirmLeaveModalProps {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

function ConfirmLeaveModal({
  channel,
  open,
  onClose,
  onConfirm,
  isLoading,
}: ConfirmLeaveModalProps): React.ReactElement {
  if (!channel) return <></>;
  const isDM = channel.kind === 'direct' || channel.kind === 'group_dm';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isDM ? 'Close conversation' : `Leave #${channel.name}`}
      className="max-w-md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={isLoading} className="gap-1.5">
            <LogOut className="h-4 w-4" />
            {isDM ? 'Close conversation' : 'Leave channel'}
          </Button>
        </div>
      }
    >
      <div className="space-y-2 py-1">
        <p className="text-[13px] leading-relaxed text-slate-600 dark:text-gray-300">
          {isDM ? (
            <>
              Are you sure you want to close this direct conversation with{' '}
              <strong className="text-slate-800 dark:text-white">{channel.name}</strong>?
            </>
          ) : (
            <>
              Are you sure you want to leave{' '}
              <strong className="text-slate-800 dark:text-white">#{channel.name}</strong>? You
              won&apos;t receive new messages from this channel unless you rejoin.
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}

interface ConfirmArchiveModalProps {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

function ConfirmArchiveModal({
  channel,
  open,
  onClose,
  onConfirm,
  isLoading,
}: ConfirmArchiveModalProps): React.ReactElement {
  if (!channel) return <></>;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Archive #${channel.name}`}
      className="max-w-md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isLoading} className="gap-1.5">
            <Archive className="h-4 w-4" />
            Archive channel
          </Button>
        </div>
      }
    >
      <div className="space-y-2 py-1 text-[13px] leading-relaxed text-slate-600 dark:text-gray-300">
        <p>
          <strong className="text-slate-800 dark:text-white">#{channel.name}</strong> will be hidden
          from the sidebar and become read-only for everyone.
        </p>
        <p>
          Messages, members, pins and settings are kept. You can restore it anytime from Archived
          channels.
        </p>
      </div>
    </Modal>
  );
}

function formatArchivedDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function archivedByName(channel: ChannelData): string {
  const user = channel.archivedByUser;
  return user ? `${user.firstName} ${user.lastName}`.trim() || user.email : 'a former member';
}

function ArchivedChannelItem({
  channel,
  isSelected,
  onClick,
}: {
  channel: ChannelData;
  isSelected: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition-colors',
        isSelected
          ? 'bg-primary/15 text-primary dark:bg-primary-light/15 dark:text-primary-light'
          : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-100'
      )}
    >
      <Archive className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate', isSelected && 'font-medium')}>{channel.name}</span>
        {channel.archivedAt && (
          <span className="block truncate text-[10px] text-slate-400 dark:text-gray-500">
            {formatArchivedDate(channel.archivedAt)} · {archivedByName(channel)}
          </span>
        )}
      </span>
    </button>
  );
}

interface ConfirmDeleteMessageModalProps {
  messageId: string | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

function ConfirmDeleteMessageModal({
  messageId,
  open,
  onClose,
  onConfirm,
  isLoading,
}: ConfirmDeleteMessageModalProps): React.ReactElement {
  return (
    <Modal
      open={open && Boolean(messageId)}
      onClose={onClose}
      title="Delete message"
      className="max-w-sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={isLoading} className="gap-1.5">
            <Trash2 className="h-4 w-4" />
            Delete message
          </Button>
        </div>
      }
    >
      <p className="text-[13px] text-slate-600 dark:text-gray-300">
        Are you sure you want to delete this message? This action cannot be undone.
      </p>
    </Modal>
  );
}

// ─── Thread Panel ─────────────────────────────────────────────────────────────
interface ThreadPanelProps {
  channel: ChannelData;
  root: ChannelMessageData;
  people?: ChatUser[];
  /** Who can be @mentioned in replies: the channel's current members. */
  mentionPeople?: ChatUser[];
  myId?: string;
  isMember?: boolean;
  canPin: boolean;
  canModerate: boolean;
  /** Reply to scroll to and highlight once replies load (from message search). */
  focusMessageId?: string | null;
  onFocusHandled?: () => void;
  onSave?: (message: ChannelMessageData) => void;
  onClose: () => void;
  onReact: (messageId: string, emoji: string) => void;
  onPin: (messageId: string) => void;
  onEdit: (messageId: string, content: string, mentions?: EditMentions) => void;
  onDelete: (messageId: string) => void;
  onVote?: (messageId: string, optionId: string, poll: PollData) => void;
  allowVoiceNotes?: boolean;
}

function ThreadRootPreview({
  message,
  people,
  myId,
  onVote,
}: Pick<ThreadPanelProps, 'people' | 'myId' | 'onVote'> & {
  message: ChannelMessageData;
}): React.ReactElement {
  const sender = personOf(message.senderId);
  const isDeleted = Boolean(message.deletedAt);
  return (
    <article className="py-0.5">
      <div className="flex items-center gap-2.5">
        <Avatar name={personName(message.senderId)} src={sender?.avatar} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold text-slate-900 dark:text-gray-100">
            {personName(message.senderId)}
          </p>
          <p className="text-[10px] text-slate-400 dark:text-gray-500">
            {formatTime(message.createdAt)}
            {message.editedAt && !isDeleted ? ' · edited' : ''}
          </p>
        </div>
      </div>
      <div className="mt-2 pl-[34px] text-[12px]">
        {isDeleted ? (
          <p className="text-[12px] italic text-slate-400">This message was deleted.</p>
        ) : (
          <>
            {message.content && (
              <MessageMarkdownContent
                content={message.content}
                myId={myId}
                people={people}
                onVote={(optionId, poll) => onVote?.(message._id, optionId, poll)}
              />
            )}
            {message.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {message.attachments.map((att) => (
                  <MessageAttachment key={att.key} attachment={att} compact />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}

function ThreadPanel({
  channel,
  root,
  people = [],
  mentionPeople = people,
  myId,
  isMember = true,
  canPin,
  canModerate,
  focusMessageId,
  onFocusHandled,
  onSave,
  onClose,
  onReact,
  onPin,
  onEdit,
  onDelete,
  onVote,
  allowVoiceNotes = false,
}: ThreadPanelProps): React.ReactElement {
  const { data: replies = [], isLoading: repliesLoading } = useChannelMessages(
    channel._id,
    root._id
  );
  const { mutateAsync: markChannelRead } = useMarkChannelRead();
  const threadScrollRef = useRef<HTMLDivElement>(null);
  const lastMarkedReadRef = useRef<string | null>(null);
  const scrolledRepliesRef = useRef<ChannelMessageData[] | null>(null);

  useLayoutEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;
    if (focusMessageId) {
      if (repliesLoading) return;
      scrolledRepliesRef.current = replies;
      const revealed = revealMessage(el, focusMessageId);
      onFocusHandled?.();
      // Not among the loaded replies (e.g. deleted): fall back to the latest.
      if (revealed) return;
    } else {
      // Clearing the focus must not undo the jump; only new replies scroll.
      if (scrolledRepliesRef.current === replies) return;
      scrolledRepliesRef.current = replies;
    }
    el.scrollTop = el.scrollHeight;
    const timer = setTimeout(() => {
      if (threadScrollRef.current)
        threadScrollRef.current.scrollTop = threadScrollRef.current.scrollHeight;
    }, 50);
    return () => clearTimeout(timer);
  }, [focusMessageId, onFocusHandled, replies, repliesLoading]);

  useEffect(() => {
    const latest = replies[replies.length - 1] ?? root;
    const key = `${channel._id}:${latest.sequence}`;
    if (lastMarkedReadRef.current === key) return;
    lastMarkedReadRef.current = key;
    void markChannelRead({ channelId: channel._id, sequence: latest.sequence }).catch(() => {
      lastMarkedReadRef.current = null;
    });
  }, [channel._id, markChannelRead, replies, root]);

  return (
    <aside
      data-chat-drop-zone
      className="relative flex w-[22rem] shrink-0 flex-col border-l border-slate-300 bg-white dark:border-white/10 dark:bg-gray-900"
    >
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-300 px-4 dark:border-white/10">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">Thread</h3>
          <p className="truncate text-[10px] text-slate-500">
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'} · #{channel.name}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white"
          aria-label="Close thread"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div ref={threadScrollRef} className="min-h-0 flex-1 overflow-y-auto chat-scroll">
        <div className="sticky top-0 z-10 border-b border-slate-300 bg-white px-4 py-3 dark:border-white/10 dark:bg-gray-900">
          <ThreadRootPreview message={root} people={people} myId={myId} onVote={onVote} />
        </div>
        <div className="flex items-center gap-2 px-4 py-3">
          <div className="h-px flex-1 bg-slate-300 dark:bg-white/10" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
            Replies
          </span>
          <div className="h-px flex-1 bg-slate-300 dark:bg-white/10" />
        </div>
        {replies.length === 0 ? (
          <p className="px-4 py-5 text-center text-[12px] text-slate-400 dark:text-gray-500">
            No replies yet. Start the thread!
          </p>
        ) : (
          <div className="pb-3">
            <MessageList
              messages={replies}
              myId={myId}
              people={people}
              mentionPeople={mentionPeople}
              canPin={canPin}
              canModerate={canModerate}
              onSave={onSave}
              onThread={() => undefined}
              onReact={onReact}
              onPin={onPin}
              onEdit={onEdit}
              onDelete={onDelete}
              onVote={onVote}
            />
          </div>
        )}
      </div>
      {isMember ? (
        <Composer
          channel={channel}
          people={mentionPeople}
          threadRootId={root._id}
          allowVoiceNotes={allowVoiceNotes}
        />
      ) : (
        <div className="border-t border-slate-300 bg-slate-50 p-3 text-center text-[12px] text-slate-500 dark:border-white/10 dark:bg-gray-950/70">
          Only active channel members can reply in this thread.
        </div>
      )}
    </aside>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function ChannelsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();
  const { data: me } = useMe();
  const { can } = usePermissions();
  const { data: organization } = useCurrentOrganization();
  const chatSettings = organization?.settings?.chatSettings;
  const role = typeof me?.roleId === 'object' ? me.roleId : undefined;
  const roleLevel = role?.hierarchyLevel ?? 0;
  const isOrganizationOwner = organization?.createdBy === me?._id;
  const isOrganizationAdmin = isOrganizationOwner || role?.slug === 'admin';
  const directMessagesEnabled =
    isOrganizationAdmin || (chatSettings?.directMessagesEnabled ?? true);
  const allowVoiceNotes = isOrganizationAdmin || (chatSettings?.allowVoiceNotes ?? false);
  const canCreateChannels =
    isOrganizationAdmin ||
    chatSettings?.allowAllRolesToCreateChannels === true ||
    (can('channels', 'create') && roleLevel >= 3);
  const { data: channels = [], isLoading } = useChannels();
  const { data: people = [] } = useChatPeople(directMessagesEnabled);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [directOpen, setDirectOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [channelMenuOpen, setChannelMenuOpen] = useState(false);
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const sidebarMenuRef = useRef<HTMLDivElement>(null);
  const [leaveTarget, setLeaveTarget] = useState<ChannelData | null>(null);
  const [leftChannelIds, setLeftChannelIds] = useState<Set<string>>(() => new Set());
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [deleteTargetMessageId, setDeleteTargetMessageId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [threadRoot, setThreadRoot] = useState<ChannelMessageData | null>(null);
  const [typingByChannel, setTypingByChannel] = useState<Record<string, Set<string>>>({});
  const [recordingByChannel, setRecordingByChannel] = useState<Record<string, Set<string>>>({});
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [channelSearch, setChannelSearch] = useState('');
  const [isChannelsFullscreen, setIsChannelsFullscreen] = useState(false);
  const [messageSearchOpen, setMessageSearchOpen] = useState(false);
  // A search result waiting to be scrolled to once its conversation renders.
  const [jumpTarget, setJumpTarget] = useState<{
    channelId: string;
    messageId: string;
    sequence: number;
  } | null>(null);
  // Set while the timeline shows a window of older messages instead of the latest.
  const [historyAnchor, setHistoryAnchor] = useState<{
    channelId: string;
    sequence: number;
  } | null>(null);
  const [threadFocusId, setThreadFocusId] = useState<string | null>(null);
  const searchShortcutLabel = useMemo(() => getSearchShortcutLabel(), []);
  const [archivedView, setArchivedView] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ChannelData | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  // Saved messages can live in archived channels, so label those too.
  const { data: archivedChannels = [], isLoading: archivedLoading } = useChannels(
    true,
    archivedView || savedOpen
  );
  const toggleSaved = useToggleSaved();
  const allChannelsForLabels = useMemo(
    () => [...channels, ...archivedChannels],
    [archivedChannels, channels]
  );
  const archiveChannel = useArchiveChannel();
  const restoreChannel = useRestoreChannel();

  useEffect(() => {
    const requestedChannelId = searchParams.get('channelId');
    if (!requestedChannelId || !channels.some((channel) => channel._id === requestedChannelId))
      return;
    setSelectedId(requestedChannelId);
    // Preserve no stale deep-link state after it has opened the notification.
    setSearchParams({}, { replace: true });
  }, [channels, searchParams, setSearchParams]);

  const lastMarkedReadRef = useRef<string | null>(null);
  const channelMenuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const channelsRootRef = useRef<HTMLDivElement>(null);
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scrollToOwnMessage = useCallback(() => {
    setNewMessageCount(0);
    setShowScrollBtn(false);
    const scroll = () => {
      const element = scrollRef.current;
      if (element) element.scrollTop = element.scrollHeight;
    };
    requestAnimationFrame(scroll);
    window.setTimeout(scroll, 60);
  }, []);

  useFullscreenShortcut({
    isFullscreen: isChannelsFullscreen,
    setIsFullscreen: setIsChannelsFullscreen,
    canEnter: true,
  });

  const selected = useMemo(() => {
    if (!selectedId) return null;
    const active = channels.find((c) => c._id === selectedId);
    if (active) return active;
    return archivedView ? (archivedChannels.find((c) => c._id === selectedId) ?? null) : null;
  }, [archivedChannels, archivedView, channels, selectedId]);
  const selectedChannelId = selected?._id ?? null;
  // Archived channels are history only: no live room, no read receipts, no composer.
  const isArchivedChannel = Boolean(selected?.archivedAt);
  const liveChannelId = isArchivedChannel ? null : selectedChannelId;

  const { data: latestMessages = [], isLoading: latestLoading } =
    useChannelMessages(selectedChannelId);
  const activeHistoryAnchor =
    historyAnchor && historyAnchor.channelId === selectedChannelId ? historyAnchor : null;
  const { data: historyMessages = [], isLoading: historyLoading } = useChannelMessagesAround(
    activeHistoryAnchor?.channelId ?? null,
    activeHistoryAnchor?.sequence ?? null
  );
  const isViewingHistory = Boolean(activeHistoryAnchor);
  const messages = isViewingHistory ? historyMessages : latestMessages;
  const messagesLoading = isViewingHistory ? historyLoading : latestLoading;
  // Read by the scroll layout effect below; layout effects run in order, so
  // this is current before that effect decides where to scroll.
  const jumpTargetRef = useRef(jumpTarget);
  useLayoutEffect(() => {
    jumpTargetRef.current = jumpTarget;
  }, [jumpTarget]);
  const { data: members = [] } = useChannelMembers(selectedChannelId);
  // Poll voters and @mentions belong to the selected channel. The DM directory
  // can legitimately omit admins or out-of-scope members, so it must not be
  // the only source used to resolve their display names.
  const channelPeople = useMemo<ChatUser[]>(() => {
    const byId = new Map<string, ChatUser>(people.map((person) => [person._id, person]));
    for (const member of members) {
      if (typeof member.userId !== 'string') {
        byId.set(member.userId._id, member.userId);
      }
    }
    if (me?._id) {
      byId.set(me._id, {
        _id: me._id,
        firstName: me.firstName,
        lastName: me.lastName,
        email: me.email,
        avatar: me.avatar ?? undefined,
        employment: me.employment,
      });
    }
    return [...byId.values()];
  }, [me, members, people]);
  // Only the channel's current members can be @mentioned; channelPeople is
  // wider so names of former members and the directory still resolve.
  const channelMemberPeople = useMemo<ChatUser[]>(
    () => members.flatMap((member) => (typeof member.userId === 'string' ? [] : [member.userId])),
    [members]
  );
  const { mutateAsync: markChannelRead } = useMarkChannelRead();
  const toggleReaction = useToggleReaction();
  const togglePin = useTogglePin();
  const editMessage = useEditChannelMessage();
  const votePoll = useVoteChannelPoll();
  const deleteMessage = useDeleteChannelMessage();
  const removeMember = useRemoveChannelMember();
  const updateMember = useUpdateChannelMember();

  const isMember = useMemo(() => {
    if (!selected || leftChannelIds.has(selected._id) || !me?._id) return false;
    return members.some((m) => {
      const uid = typeof m.userId === 'string' ? m.userId : m.userId?._id;
      return uid === me._id;
    });
  }, [selected, members, me?._id, leftChannelIds]);
  const canPinMessages = Boolean(
    selected &&
    (selected.kind === 'direct' ||
      selected.kind === 'group_dm' ||
      selected.currentUserRole === 'owner' ||
      selected.currentUserRole === 'moderator')
  );
  const canModerateMessages = Boolean(
    selected &&
    selected.kind !== 'direct' &&
    selected.kind !== 'group_dm' &&
    (selected.currentUserRole === 'owner' || selected.currentUserRole === 'moderator')
  );

  // ── Pinned Channels State (Optimistic instant updates with localStorage) ──
  const pinnedStorageKey = useMemo(
    () => (me?._id ? `kaamco_pinned_channels_${me._id}` : 'kaamco_pinned_channels'),
    [me?._id]
  );
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('kaamco_pinned_channels') || '[]');
    } catch {
      return [];
    }
  });

  // Sync state when user profile is loaded
  useEffect(() => {
    try {
      const stored = localStorage.getItem(pinnedStorageKey);
      if (stored) setPinnedIds(JSON.parse(stored));
    } catch {
      // Local storage can be unavailable in private browsing or restricted contexts.
    }
  }, [pinnedStorageKey]);

  const handleTogglePinChannel = useCallback(
    (ch: ChannelData) => {
      setPinnedIds((prev) => {
        const isCurrentlyPinned = prev.includes(ch._id);
        const next = isCurrentlyPinned ? prev.filter((id) => id !== ch._id) : [...prev, ch._id];
        try {
          localStorage.setItem(pinnedStorageKey, JSON.stringify(next));
        } catch {
          // Pinning still works for this session when local storage is unavailable.
        }
        toast.success(isCurrentlyPinned ? `Unpinned #${ch.name}` : `Pinned #${ch.name} to top`);
        return next;
      });
    },
    [pinnedStorageKey]
  );

  // ── Filtered channels for sidebar search ──
  const filteredChannels = useMemo(() => {
    const q = channelSearch.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter((c) => c.name.toLowerCase().includes(q));
  }, [channels, channelSearch]);

  const filteredArchivedChannels = useMemo(() => {
    const q = channelSearch.trim().toLowerCase();
    return q ? archivedChannels.filter((c) => c.name.toLowerCase().includes(q)) : archivedChannels;
  }, [archivedChannels, channelSearch]);

  // Pinned vs Unpinned separation
  const pinnedChannels = useMemo(
    () => filteredChannels.filter((c) => pinnedIds.includes(c._id)),
    [filteredChannels, pinnedIds]
  );
  const unpinnedChannels = useMemo(
    () => filteredChannels.filter((c) => !pinnedIds.includes(c._id)),
    [filteredChannels, pinnedIds]
  );

  const groupedChannels = useMemo(
    () =>
      unpinnedChannels
        .filter((c) => !['direct', 'group_dm'].includes(c.kind))
        .reduce<Record<string, ChannelData[]>>((acc, ch) => {
          const label = displayChannelGroupLabel(ch);
          (acc[label] ??= []).push(ch);
          return acc;
        }, {}),
    [unpinnedChannels]
  );
  const directChannels = useMemo(
    () => unpinnedChannels.filter((c) => c.kind === 'direct' || c.kind === 'group_dm'),
    [unpinnedChannels]
  );

  const selectedDmPerson = useMemo(() => {
    if (!selected || (selected.kind !== 'direct' && selected.kind !== 'group_dm')) return null;
    const norm = selected.name.trim().toLowerCase();
    return (
      people.find((p) => {
        const full = `${p.firstName} ${p.lastName}`.trim().toLowerCase();
        return full === norm || p.email.toLowerCase() === norm || norm.includes(full);
      }) ?? null
    );
  }, [selected, people]);

  // One-to-one DMs carry the other person's name, matched to the directory
  // the same way `selectedDmPerson` does, to show their online status.
  const dmPeopleIds = useMemo(() => {
    const ids: string[] = [];
    for (const channel of channels) {
      if (channel.kind !== 'direct') continue;
      const norm = channel.name.trim().toLowerCase();
      const person = people.find((p) => {
        const full = `${p.firstName} ${p.lastName}`.trim().toLowerCase();
        return full === norm || p.email.toLowerCase() === norm || norm.includes(full);
      });
      if (person) ids.push(person._id);
    }
    return ids;
  }, [channels, people]);
  const dmPresence = usePresence(dmPeopleIds, directMessagesEnabled);
  const selectedDmPresence =
    selected?.kind === 'direct' && selectedDmPerson
      ? (dmPresence.get(selectedDmPerson._id) ?? 'offline')
      : null;

  const typingUsers = typingByChannel[selectedChannelId ?? ''] ?? new Set<string>();
  const typingNames = [...typingUsers]
    .map((uid) => {
      const m = members.find((x) => typeof x.userId !== 'string' && x.userId._id === uid);
      return m && typeof m.userId !== 'string'
        ? `${m.userId.firstName} ${m.userId.lastName}`.trim()
        : 'Someone';
    })
    .filter(Boolean);
  const recordingUsers = recordingByChannel[selectedChannelId ?? ''] ?? new Set<string>();
  const recordingNames = [...recordingUsers]
    .map((uid) => {
      const m = members.find((x) => typeof x.userId !== 'string' && x.userId._id === uid);
      return m && typeof m.userId !== 'string'
        ? `${m.userId.firstName} ${m.userId.lastName}`.trim()
        : 'Someone';
    })
    .filter(Boolean);

  const totalUnread = channels.reduce((sum, ch) => sum + ch.unreadCount, 0);

  useEffect(() => {
    if (!channelMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (!channelMenuRef.current?.contains(e.target as Node)) setChannelMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChannelMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [channelMenuOpen]);

  useEffect(() => {
    if (!sidebarMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (!sidebarMenuRef.current?.contains(e.target as Node)) setSidebarMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [sidebarMenuOpen]);

  const lastChannelRef = useRef<string | null>(null);

  useEffect(() => {
    setNewMessageCount(0);
    setShowScrollBtn(false);
    // Older-message windows are per visit; coming back opens at the latest.
    setHistoryAnchor((current) => (current?.channelId === selectedChannelId ? current : null));
  }, [selectedChannelId]);

  const markLatestReadIfAtBottom = useCallback(() => {
    const el = scrollRef.current;
    const last = messages[messages.length - 1];
    // The bottom of an older window is not the latest message.
    if (!el || !liveChannelId || !last || isViewingHistory) return;
    // Browsers can leave a small fractional gap after programmatic scrolling.
    // Treat the final visual line as read instead of requiring an exact zero.
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 80) return;
    const key = `${liveChannelId}:${last.sequence}`;
    if (lastMarkedReadRef.current === key) return;
    lastMarkedReadRef.current = key;
    void markChannelRead({ channelId: liveChannelId, sequence: last.sequence }).catch(() => {
      lastMarkedReadRef.current = null;
    });
  }, [isViewingHistory, liveChannelId, markChannelRead, messages]);

  const scrollToUnreadOrLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Open where the user stopped reading. A channel that has no unread
    // messages retains the existing today/latest behavior.
    const firstUnreadSequence =
      selected && selected.unreadCount > 0
        ? selected.lastMessageSequence - selected.unreadCount + 1
        : null;
    const firstUnreadMessage = firstUnreadSequence
      ? el.querySelector<HTMLElement>(`[data-message-sequence="${firstUnreadSequence}"]`)
      : null;
    const todayDivider = el.querySelector<HTMLElement>('#channel-today-divider');
    const firstTodayMsg = el.querySelector<HTMLElement>('[data-is-today="true"]');
    const targetEl = firstUnreadMessage || todayDivider || firstTodayMsg;
    if (targetEl) {
      // Calculate position relative to container
      const containerTop = el.getBoundingClientRect().top;
      const targetTop = targetEl.getBoundingClientRect().top;
      const relativeTop = targetTop - containerTop + el.scrollTop;

      el.scrollTop = Math.max(0, relativeTop - 12);
    } else {
      // If no messages from today, scroll to bottom (latest message)
      el.scrollTop = el.scrollHeight;
    }
  }, [selected]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || messagesLoading || messages.length === 0) return;

    const isChannelSwitch = lastChannelRef.current !== selectedChannelId;
    if (isChannelSwitch) {
      lastChannelRef.current = selectedChannelId;
      setNewMessageCount(0);
      // A search jump positions the view itself.
      if (jumpTargetRef.current?.channelId === selectedChannelId) return;
      scrollToUnreadOrLatest();
      const timer = setTimeout(() => {
        scrollToUnreadOrLatest();
        markLatestReadIfAtBottom();
      }, 60);
      return () => clearTimeout(timer);
    }

    // An older window never receives live messages, so there is nothing to follow.
    if (isViewingHistory) return;

    // For real-time new incoming messages: if user is already near bottom (< 350px), auto-scroll down
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 350;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
      markLatestReadIfAtBottom();
    }
  }, [
    isViewingHistory,
    markLatestReadIfAtBottom,
    messages,
    messagesLoading,
    scrollToUnreadOrLatest,
    selectedChannelId,
  ]);

  // Resolve a pending search jump: reveal the message if it is rendered,
  // otherwise load a window of history around it and try again.
  useLayoutEffect(() => {
    if (!jumpTarget || jumpTarget.channelId !== selectedChannelId || messagesLoading) return;
    if (messages.some((message) => message._id === jumpTarget.messageId)) {
      const el = scrollRef.current;
      if (el && revealMessage(el, jumpTarget.messageId)) setJumpTarget(null);
      return;
    }
    if (isViewingHistory && latestMessages.some((m) => m._id === jumpTarget.messageId)) {
      setHistoryAnchor(null);
      return;
    }
    if (activeHistoryAnchor?.sequence !== jumpTarget.sequence) {
      setHistoryAnchor({ channelId: jumpTarget.channelId, sequence: jumpTarget.sequence });
      return;
    }
    toast.error('That message is no longer available');
    setJumpTarget(null);
  }, [
    activeHistoryAnchor?.sequence,
    isViewingHistory,
    jumpTarget,
    latestMessages,
    messages,
    messagesLoading,
    selectedChannelId,
  ]);

  const handleSearchResultSelect = useCallback(
    (result: MessageSearchResult) => {
      setMessageSearchOpen(false);
      if (result.channelId !== selectedChannelId) setThreadRoot(null);
      setSelectedId(result.channelId);
      if (result.threadRootId) {
        if (!result.threadRoot) {
          toast.error('That thread is no longer available');
          return;
        }
        setThreadRoot(result.threadRoot);
        setThreadFocusId(result._id);
        return;
      }
      setJumpTarget({
        channelId: result.channelId,
        messageId: result._id,
        sequence: result.sequence,
      });
    },
    [selectedChannelId]
  );

  const handleThreadFocusHandled = useCallback(() => setThreadFocusId(null), []);

  // Stable so memoised message rows don't re-render on every page update.
  const toggleSavedMutate = toggleSaved.mutate;
  const handleToggleSaved = useCallback(
    (message: ChannelMessageData) => {
      const wasSaved = Boolean(message.saved);
      toggleSavedMutate(
        { channelId: message.channelId, messageId: message._id, saved: wasSaved },
        {
          onSuccess: (result) =>
            toast.success(result.saved ? 'Saved for later' : 'Removed from saved'),
          onError: (error) => toast.error(getErrorMessage(error)),
        }
      );
    },
    [toggleSavedMutate]
  );

  const handleSavedSelect = (message: SavedMessage) => {
    setSavedOpen(false);
    // A save can sit in an archived channel, which only opens from that view.
    if (archivedChannels.some((channel) => channel._id === message.channelId)) {
      setArchivedView(true);
    }
    handleSearchResultSelect(message);
  };

  const jumpToLatest = useCallback(() => {
    setJumpTarget(null);
    setHistoryAnchor(null);
    scrollToOwnMessage();
  }, [scrollToOwnMessage]);

  // Cmd/Ctrl+K opens message search on this page, ahead of the global command
  // palette (which skips events that were already handled).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      if (event.key.toLowerCase() !== 'k') return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      // The composer keeps Cmd/Ctrl+K for inserting a link.
      if (target?.isContentEditable) return;
      // Leave other open dialogs (including the palette) to their own handling.
      if (document.querySelector('[role="dialog"]:not([data-message-search])')) return;
      event.preventDefault();
      setSavedOpen(false);
      setMessageSearchOpen((current) => !current);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distanceFromBottom > 200);
    if (distanceFromBottom < 80) {
      setNewMessageCount(0);
      markLatestReadIfAtBottom();
    }
  }, [markLatestReadIfAtBottom]);

  useEffect(() => {
    // Archived channels have no live room; the server would refuse the join.
    if (!socket || !isConnected || !liveChannelId) return;
    const channelId = liveChannelId;
    const typingTimeouts = typingTimeoutsRef.current;
    const forCurrent = (e: { channelId?: string }) => e.channelId === channelId;

    const onMsgCreated = (e: ChannelMessageData & { channelId?: string }) => {
      if (!forCurrent(e as { channelId?: string })) return;
      const messageKey = channelKeys.messages(channelId, e.threadRootId);
      const currentMessages = queryClient.getQueryData<ChannelMessageData[]>(messageKey) ?? [];
      const matchingOptimisticMessage = e.clientMessageId
        ? currentMessages.find((message) => message.clientMessageId === e.clientMessageId)
        : undefined;
      const alreadyPresent = Boolean(
        currentMessages.some((message) => message._id === e._id) || matchingOptimisticMessage
      );
      queryClient.setQueryData<ChannelMessageData[]>(messageKey, (prev = []) => {
        if (matchingOptimisticMessage) {
          return prev.map((message) =>
            message._id === matchingOptimisticMessage._id ? e : message
          );
        }
        return prev.some((message) => message._id === e._id) ? prev : [...prev, e];
      });
      if (e.threadRootId && !alreadyPresent) {
        queryClient.setQueryData<ChannelMessageData[]>(channelKeys.messages(channelId), (prev) =>
          prev?.map((message) =>
            message._id === e.threadRootId
              ? { ...message, replyCount: message.replyCount + 1 }
              : message
          )
        );
      }
      const senderId = typeof e.senderId !== 'string' ? e.senderId._id : e.senderId;
      const el = scrollRef.current;
      const isNearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 350;
      if (senderId && senderId !== me?._id && !isNearBottom)
        setNewMessageCount((current) => current + 1);
      if (senderId)
        setTypingByChannel((prev) => {
          const next = { ...prev };
          const set = new Set(next[channelId] ?? []);
          set.delete(senderId);
          next[channelId] = set;
          return next;
        });
      if (senderId)
        setRecordingByChannel((prev) => {
          const next = { ...prev };
          const set = new Set(next[channelId] ?? []);
          set.delete(senderId);
          next[channelId] = set;
          return next;
        });
    };
    const onMsgChange = (e: ChannelMessageData & { channelId?: string; messageId?: string }) => {
      if (!forCurrent(e)) return;
      if (e._id) {
        queryClient.setQueriesData<ChannelMessageData[]>(
          { queryKey: channelKeys.messagesRoot(channelId) },
          (current) => current?.map((message) => (message._id === e._id ? e : message))
        );
        return;
      }
      if (e.messageId && e.deletedAt) {
        queryClient.setQueriesData<ChannelMessageData[]>(
          { queryKey: channelKeys.messagesRoot(channelId) },
          (current) =>
            current?.map((message) =>
              message._id === e.messageId
                ? { ...message, content: '', attachments: [], deletedAt: e.deletedAt }
                : message
            )
        );
      }
    };
    const onMsgReactions = (e: {
      channelId?: string;
      messageId?: string;
      reactions?: MessageReactionData[];
      actorId?: string;
      emoji?: string;
      reacted?: boolean;
    }) => {
      if (!forCurrent(e)) return;
      if (e.messageId && e.reactions) {
        queryClient.setQueriesData<ChannelMessageData[]>(
          { queryKey: channelKeys.messagesRoot(channelId) },
          (list) => {
            if (!list) return list;
            return list.map((msg) => {
              if (msg._id !== e.messageId) return msg;
              const previousByEmoji = new Map(
                msg.reactions.map((reaction) => [reaction.emoji, reaction])
              );
              return {
                ...msg,
                reactions: e.reactions!.map((reaction) => ({
                  ...reaction,
                  reacted:
                    reaction.emoji === e.emoji && e.actorId === me?._id
                      ? Boolean(e.reacted)
                      : (previousByEmoji.get(reaction.emoji)?.reacted ?? false),
                })),
              };
            });
          }
        );
      }
    };
    const onChannelChange = (e: { channelId?: string }) => {
      if (forCurrent(e)) void queryClient.invalidateQueries({ queryKey: channelKeys.list() });
    };
    const onTyping = (e: {
      channelId?: string;
      userId?: string;
      typing?: boolean;
      activity?: 'typing' | 'recording';
    }) => {
      if (!forCurrent(e) || !e.userId) return;
      const isRecording = e.activity === 'recording';
      const updateActivity = (
        setter: React.Dispatch<React.SetStateAction<Record<string, Set<string>>>>,
        active: boolean
      ) =>
        setter((prev) => {
          const next = { ...prev };
          const set = new Set(next[channelId] ?? []);
          if (active) set.add(e.userId!);
          else set.delete(e.userId!);
          next[channelId] = set;
          return next;
        });
      updateActivity(isRecording ? setRecordingByChannel : setTypingByChannel, e.typing !== false);
      if (e.typing !== false) {
        updateActivity(isRecording ? setTypingByChannel : setRecordingByChannel, false);
      }
      const activity = isRecording ? 'recording' : 'typing';
      const timeoutKey = `${channelId}:${e.userId}:${activity}`;
      const existingTimeout = typingTimeouts.get(timeoutKey);
      if (existingTimeout) clearTimeout(existingTimeout);
      if (e.typing === false) {
        typingTimeouts.delete(timeoutKey);
        return;
      }
      const timeout = setTimeout(() => {
        updateActivity(isRecording ? setRecordingByChannel : setTypingByChannel, false);
        typingTimeouts.delete(timeoutKey);
      }, 5_000);
      typingTimeouts.set(timeoutKey, timeout);
    };
    const onReadUpdated = (e: { channelId?: string; lastReadSequence?: number }) => {
      if (e.channelId !== channelId) return;
      queryClient.setQueriesData<ChannelData[]>({ queryKey: ['channels', 'list'] }, (cur) =>
        cur?.map((ch) =>
          ch._id === e.channelId
            ? {
                ...ch,
                unreadCount: Math.max(0, ch.lastMessageSequence - (e.lastReadSequence ?? 0)),
              }
            : ch
        )
      );
    };
    const onAccessRevoked = (e: { channelId?: string }) => {
      if (!e.channelId) return;
      setLeftChannelIds((current) => new Set(current).add(e.channelId!));
      if (e.channelId === channelId) setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: channelKeys.members(e.channelId) });
      void queryClient.invalidateQueries({ queryKey: channelKeys.list() });
    };

    socket.on('channel.message.created', onMsgCreated);
    socket.on('channel.message.updated', onMsgChange);
    socket.on('channel.message.deleted', onMsgChange);
    socket.on('channel.message.reactions', onMsgReactions);
    socket.on('channel.pins.changed', onChannelChange);
    socket.on('channel.updated', onChannelChange);
    socket.on('channel.typing', onTyping);
    socket.on('channel.read.updated', onReadUpdated);
    socket.on('channel.access.revoked', onAccessRevoked);

    // Socket.io room membership is lost on every reconnect. Re-subscribe only
    // after listeners are installed, then re-fetch once to fill any messages
    // that were sent during the network gap.
    socket.emit('channel.subscribe', { channelId });
    // Gateway authentication and the client `connect` event are asynchronous.
    // A second, idempotent join closes that narrow race after a reconnect.
    const subscribeRetry = window.setTimeout(() => {
      if (socket.connected) socket.emit('channel.subscribe', { channelId });
    }, 750);
    void queryClient.invalidateQueries({ queryKey: channelKeys.messagesRoot(channelId) });
    void queryClient.invalidateQueries({ queryKey: channelKeys.list() });

    return () => {
      window.clearTimeout(subscribeRetry);
      for (const [timeoutKey, timeout] of typingTimeouts) {
        if (timeoutKey.startsWith(`${channelId}:`)) {
          clearTimeout(timeout);
          typingTimeouts.delete(timeoutKey);
        }
      }
      setTypingByChannel((prev) => ({ ...prev, [channelId]: new Set() }));
      setRecordingByChannel((prev) => ({ ...prev, [channelId]: new Set() }));
      socket.emit('channel.unsubscribe', { channelId });
      socket.off('channel.message.created', onMsgCreated);
      socket.off('channel.message.updated', onMsgChange);
      socket.off('channel.message.deleted', onMsgChange);
      socket.off('channel.message.reactions', onMsgReactions);
      socket.off('channel.pins.changed', onChannelChange);
      socket.off('channel.updated', onChannelChange);
      socket.off('channel.typing', onTyping);
      socket.off('channel.read.updated', onReadUpdated);
      socket.off('channel.access.revoked', onAccessRevoked);
    };
  }, [isConnected, liveChannelId, me?._id, queryClient, socket]);

  // Archive/restore arrive as per-user events so every open tab updates its
  // sidebar, including tabs that are not viewing the channel.
  useEffect(() => {
    if (!socket) return;
    const onArchived = (e: { channelId?: string; name?: string; archivedBy?: string }) => {
      if (!e.channelId) return;
      queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), (current) =>
        current?.filter((channel) => channel._id !== e.channelId)
      );
      void queryClient.invalidateQueries({ queryKey: channelKeys.list(true) });
      if (e.channelId !== selectedId || archivedView) return;
      setSelectedId(null);
      setThreadRoot(null);
      if (e.archivedBy !== me?._id) toast.info(`#${e.name ?? 'channel'} was archived`);
    };
    const onRestored = () => {
      void queryClient.invalidateQueries({ queryKey: channelKeys.list(false) });
      void queryClient.invalidateQueries({ queryKey: channelKeys.list(true) });
    };
    // Roles or membership changed (including our own role): refresh the member
    // list and our channel entry, whose `currentUserRole` drives permissions.
    const onMembersChanged = (e: { channelId?: string }) => {
      if (!e.channelId) return;
      void queryClient.invalidateQueries({ queryKey: channelKeys.members(e.channelId) });
      void queryClient.invalidateQueries({ queryKey: channelKeys.list(false) });
    };
    // We were removed: drop the channel from the sidebar even when not viewing it.
    const onAccessRevoked = (e: { channelId?: string }) => {
      if (!e.channelId) return;
      queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), (current) =>
        current?.filter((channel) => channel._id !== e.channelId)
      );
      void queryClient.invalidateQueries({ queryKey: channelKeys.list(false) });
    };
    // Another tab saved or unsaved a message.
    const onSavedChanged = (e: { messageId?: string; saved?: boolean }) => {
      if (!e.messageId || e.saved === undefined) return;
      applySavedChange(queryClient, e.messageId, e.saved);
      if (e.saved) void queryClient.invalidateQueries({ queryKey: channelKeys.saved });
    };
    const onPresence = (e: { userId?: string; status?: PresenceStatus }) => {
      if (e.userId && e.status)
        applyPresenceUpdate(queryClient, { userId: e.userId, status: e.status });
    };
    socket.on('channel.archived', onArchived);
    socket.on('channel.restored', onRestored);
    socket.on('chat.presence', onPresence);
    socket.on('channel.saved.changed', onSavedChanged);
    socket.on('channel.members.changed', onMembersChanged);
    socket.on('channel.access.revoked', onAccessRevoked);
    return () => {
      socket.off('channel.saved.changed', onSavedChanged);
      socket.off('channel.members.changed', onMembersChanged);
      socket.off('channel.access.revoked', onAccessRevoked);
      socket.off('channel.archived', onArchived);
      socket.off('channel.restored', onRestored);
      socket.off('chat.presence', onPresence);
    };
  }, [archivedView, me?._id, queryClient, selectedId, socket]);

  const handleCreated = (channel: ChannelData) => {
    setSelectedId(channel._id);
    void queryClient.invalidateQueries({ queryKey: channelKeys.list() });
  };

  const handleReaction = (messageId: string, emoji: string) => {
    if (isArchivedChannel) {
      toast.error('Archived channels are read-only');
      return;
    }
    if (!isMember) {
      toast.error('Only channel members can add reactions');
      return;
    }
    void toggleReaction
      .mutateAsync({ channelId: selected!._id, messageId, emoji })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const handlePin = (messageId: string) => {
    if (isArchivedChannel) {
      toast.error('Archived channels are read-only');
      return;
    }
    if (!isMember) {
      toast.error('Only channel members can pin messages');
      return;
    }
    const channelId = selected!._id;
    // Timeline or open thread: whichever holds it, for the optimistic pin list.
    const message =
      messages.find((item) => item._id === messageId) ??
      (threadRoot
        ? queryClient
            .getQueryData<ChannelMessageData[]>(channelKeys.messages(channelId, threadRoot._id))
            ?.find((item) => item._id === messageId)
        : undefined);
    const pinnedNow = queryClient
      .getQueryData<ChannelMessageData[]>(channelKeys.pins(channelId))
      ?.some((item) => item._id === messageId);
    // When the pin list is loaded the outcome is known, so confirm at once.
    if (pinnedNow !== undefined) toast.success(pinnedNow ? 'Unpinned' : 'Message pinned');
    togglePin.mutate(
      { channelId, messageId, message },
      {
        onSuccess: (result) => {
          if (pinnedNow === undefined) toast.success(result.pinned ? 'Message pinned' : 'Unpinned');
        },
        onError: (error) => toast.error(getErrorMessage(error)),
      }
    );
  };

  const handleEdit = (messageId: string, content: string, mentions?: EditMentions) =>
    void editMessage
      .mutateAsync({ channelId: selected!._id, messageId, content, ...mentions })
      .catch((e) => toast.error(getErrorMessage(e)));

  const handleDelete = (messageId: string) => {
    setDeleteTargetMessageId(messageId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!deleteTargetMessageId || !selected?._id) return;
    try {
      await deleteMessage.mutateAsync({
        channelId: selected._id,
        messageId: deleteTargetMessageId,
      });
      toast.success('Message deleted');
      setDeleteModalOpen(false);
      setDeleteTargetMessageId(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleVotePoll = (messageId: string, optionId: string, updatedPoll: PollData) => {
    if (isArchivedChannel) {
      toast.error('Archived channels are read-only');
      return;
    }
    if (!isMember) {
      toast.error('Only channel members can vote in polls');
      return;
    }
    const newContent = `[POLL:${JSON.stringify(updatedPoll)}]`;
    void votePoll
      .mutateAsync({
        channelId: selected!._id,
        messageId,
        optionId,
        optimisticContent: newContent,
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const handleLeaveChannel = (ch: ChannelData) => {
    setLeaveTarget(ch);
    setLeaveModalOpen(true);
  };

  const handleCloseDm = (ch: ChannelData) => {
    setLeaveTarget(ch);
    setLeaveModalOpen(true);
  };

  // Optimistic: the channel leaves the sidebar and the view moves on at once
  // (the hook updates the caches); a failure puts everything back.
  const handleConfirmLeave = async () => {
    if (!leaveTarget || !me?._id) return;
    const target = leaveTarget;
    const leftChannelId = target._id;
    const wasSelected = selectedChannelId === leftChannelId;
    const isDM = target.kind === 'direct' || target.kind === 'group_dm';
    setLeaveModalOpen(false);
    setLeaveTarget(null);
    setLeftChannelIds((current) => new Set(current).add(leftChannelId));
    socket?.emit('channel.unsubscribe', { channelId: leftChannelId });
    if (wasSelected) {
      setSelectedId(channels.find((channel) => channel._id !== leftChannelId)?._id ?? null);
    }
    toast.success(isDM ? `Closed conversation with ${target.name}` : `You left #${target.name}`);
    removeMember.mutate(
      { channelId: leftChannelId, memberId: me._id },
      {
        onError: (error) => {
          toast.error(getErrorMessage(error));
          setLeftChannelIds((current) => {
            const next = new Set(current);
            next.delete(leftChannelId);
            return next;
          });
          // Reopening re-subscribes the live room through the socket effect.
          if (wasSelected) setSelectedId(leftChannelId);
        },
      }
    );
  };

  // Optimistic: the mute icon and menu label flip at once.
  const handleToggleMuteChannel = (ch: ChannelData) => {
    if (!me?._id) return;
    const newLevel = ch.notificationLevel === 'muted' ? 'all' : 'muted';
    toast.success(newLevel === 'muted' ? `Muted #${ch.name}` : `Unmuted #${ch.name}`);
    updateMember.mutate(
      { channelId: ch._id, memberId: me._id, payload: { notificationLevel: newLevel } },
      { onError: (error) => toast.error(getErrorMessage(error)) }
    );
  };

  const handleCopyChannelName = (ch: ChannelData) => {
    void navigator.clipboard.writeText(ch.name).then(() => toast.success(`Copied #${ch.name}`));
  };

  // Archive and restore are optimistic: the lists move at once (see the
  // hooks), and a failure rolls them back and returns the view to where it was.
  const handleConfirmArchive = async () => {
    const target = archiveTarget;
    if (!target) return;
    const wasOpen = selectedChannelId === target._id;
    setArchiveTarget(null);
    if (wasOpen) {
      setSelectedId(null);
      setThreadRoot(null);
    }
    archiveChannel.mutate(target._id, {
      onSuccess: () => toast.success(`Archived #${target.name}`),
      onError: (error) => {
        toast.error(getErrorMessage(error));
        if (wasOpen) setSelectedId(target._id);
      },
    });
  };

  const handleRestoreChannel = (ch: ChannelData) => {
    setArchivedView(false);
    setSelectedId(ch._id);
    restoreChannel.mutate(ch._id, {
      onSuccess: () => {
        toast.success(`Restored #${ch.name}`);
        // The live room was joined before the server finished restoring and
        // may have been refused; joining again is idempotent.
        socket?.emit('channel.subscribe', { channelId: ch._id });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
        setArchivedView(true);
        setSelectedId(ch._id);
      },
    });
  };

  const toggleArchivedView = () => {
    // Leaving the archive must not keep an archived channel open.
    if (archivedView && selected?.archivedAt) setSelectedId(null);
    setArchivedView(!archivedView);
    setThreadRoot(null);
  };

  return (
    <div
      ref={channelsRootRef}
      className={cn(
        'flex w-full overflow-hidden bg-white dark:bg-gray-900',
        isChannelsFullscreen ? 'fixed inset-0 z-50 h-screen' : 'h-full'
      )}
    >
      {/* ── Sidebar ────────────────────────────────────────── */}
      <aside className="flex w-64 shrink-0 flex-col border-r border-slate-300 bg-slate-50 dark:border-white/10 dark:bg-gray-950/60">
        {/* Sidebar header */}
        <div className="flex h-14 items-center justify-between border-b border-slate-300 px-3 dark:border-white/10">
          <div className="min-w-0">
            <h1 className="text-[13px] font-semibold text-slate-900 dark:text-white">Channels</h1>
            {totalUnread > 0 && (
              <p className="text-[10px] text-slate-500 dark:text-gray-500">{totalUnread} unread</p>
            )}
          </div>
          <div className="flex gap-1">
            {canCreateChannels && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-primary dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Create channel"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <div ref={sidebarMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setSidebarMenuOpen((open) => !open)}
                className={cn(
                  'rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-primary dark:hover:bg-white/10 dark:hover:text-white',
                  sidebarMenuOpen && 'bg-slate-200 text-primary dark:bg-white/10 dark:text-white'
                )}
                aria-label="More options"
                title="More options"
                aria-haspopup="menu"
                aria-expanded={sidebarMenuOpen}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {sidebarMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-[calc(100%+6px)] z-50 w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-xl animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800"
                >
                  <MenuItem
                    onClick={() => {
                      setSavedOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                  >
                    <Bookmark className="h-3.5 w-3.5" /> Saved messages
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      toggleArchivedView();
                      setSidebarMenuOpen(false);
                    }}
                  >
                    {archivedView ? (
                      <>
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to channels
                      </>
                    ) : (
                      <>
                        <Archive className="h-3.5 w-3.5" /> Archived channels
                      </>
                    )}
                  </MenuItem>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search – filters channels in real time */}
        <div className="px-2 pt-2.5 pb-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={channelSearch}
              onChange={(e) => setChannelSearch(e.target.value)}
              placeholder="Find a channel…"
              className="h-7 w-full rounded-md border border-slate-300 bg-white pl-8 pr-2.5 text-[12px] text-slate-700 placeholder:text-slate-400 outline-none transition-colors focus:border-primary/50 focus:ring-1 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200 dark:placeholder:text-gray-500"
            />
            {channelSearch && (
              <button
                type="button"
                onClick={() => setChannelSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Channels nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 chat-scroll">
          {archivedView ? (
            <div>
              <div className="flex items-center justify-between gap-2 px-1.5 py-1">
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 dark:text-gray-500">
                  <Archive className="h-3 w-3" />
                  Archived channels
                </span>
                <button
                  type="button"
                  onClick={toggleArchivedView}
                  className="inline-flex items-center gap-1 rounded px-1 text-[11px] text-slate-500 transition-colors hover:text-slate-900 dark:text-gray-400 dark:hover:text-gray-100"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back
                </button>
              </div>
              {archivedLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
              ) : filteredArchivedChannels.length === 0 ? (
                <p className="px-2 py-6 text-center text-[11px] leading-5 text-slate-400 dark:text-gray-500">
                  {channelSearch.trim()
                    ? `No archived channels match “${channelSearch}”`
                    : 'No archived channels. Archived channels you can access appear here.'}
                </p>
              ) : (
                <div className="mt-0.5 space-y-0.5">
                  {filteredArchivedChannels.map((channel) => (
                    <ArchivedChannelItem
                      key={channel._id}
                      channel={channel}
                      isSelected={channel._id === selectedChannelId}
                      onClick={() => setSelectedId(channel._id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Pinned Channels Section */}
              {pinnedChannels.length > 0 && (
                <SidebarSection
                  label="Pinned Channels"
                  channels={pinnedChannels}
                  people={people}
                  presence={dmPresence}
                  selectedId={selectedChannelId}
                  pinnedIds={pinnedIds}
                  onSelect={setSelectedId}
                  onTogglePin={handleTogglePinChannel}
                  onLeave={handleLeaveChannel}
                  onToggleMute={handleToggleMuteChannel}
                  onCopyLink={handleCopyChannelName}
                  onCloseDm={handleCloseDm}
                />
              )}

              {/* Grouped Standard / Custom Channels */}
              {Object.entries(groupedChannels).map(([group, entries]) => (
                <SidebarSection
                  key={group}
                  label={group}
                  channels={entries}
                  people={people}
                  selectedId={selectedChannelId}
                  pinnedIds={pinnedIds}
                  onSelect={setSelectedId}
                  onTogglePin={handleTogglePinChannel}
                  onLeave={handleLeaveChannel}
                  onToggleMute={handleToggleMuteChannel}
                  onCopyLink={handleCopyChannelName}
                />
              ))}

              {/* Direct Messages: always shown when DMs are on, so + can start one. */}
              {(directChannels.length > 0 || (directMessagesEnabled && !channelSearch.trim())) && (
                <SidebarSection
                  label="Direct Messages"
                  channels={directChannels}
                  people={people}
                  presence={dmPresence}
                  onAdd={directMessagesEnabled ? () => setDirectOpen(true) : undefined}
                  addLabel="New direct message"
                  emptyText="No conversations yet"
                  selectedId={selectedChannelId}
                  pinnedIds={pinnedIds}
                  onSelect={setSelectedId}
                  onTogglePin={handleTogglePinChannel}
                  onToggleMute={handleToggleMuteChannel}
                  onCopyLink={handleCopyChannelName}
                  onCloseDm={handleCloseDm}
                />
              )}

              {channelSearch.trim() &&
                pinnedChannels.length === 0 &&
                Object.keys(groupedChannels).length === 0 &&
                directChannels.length === 0 && (
                  <p className="px-2 py-6 text-center text-[11px] text-slate-400 dark:text-gray-500">
                    No channels match &ldquo;{channelSearch}&rdquo;
                  </p>
                )}
            </>
          )}
        </nav>
      </aside>

      {/* ── Main message area ─────────────────────────────────── */}
      {selected ? (
        <main
          // Archived channels have no composer, so nothing can be dropped in.
          data-chat-drop-zone={isArchivedChannel ? undefined : true}
          className="relative flex min-w-0 flex-1 flex-col bg-white dark:bg-gray-900"
        >
          {/* Channel header */}
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-300 px-5 dark:border-white/10">
            <div className="flex min-w-0 items-center gap-2">
              {isArchivedChannel ? (
                <Archive className="h-4 w-4 shrink-0 text-slate-500" />
              ) : selected.kind === 'announcement' ? (
                <Bell className="h-4 w-4 shrink-0 text-slate-500" />
              ) : selected.kind === 'direct' || selected.kind === 'group_dm' ? (
                <Avatar
                  name={selected.name}
                  src={selectedDmPerson?.avatar}
                  size="xs"
                  className="h-6 w-6 text-[10px]"
                />
              ) : selected.visibility === 'private' ? (
                <Lock className="h-4 w-4 shrink-0 text-slate-500" />
              ) : (
                <Hash className="h-4 w-4 shrink-0 text-slate-500" />
              )}
              <h2 className="truncate text-[14px] font-semibold text-slate-900 dark:text-gray-100">
                {selected.name}
              </h2>
              {selected.kind === 'announcement' && (
                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                  Announcement
                </span>
              )}
              {isArchivedChannel && (
                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-600 dark:bg-white/10 dark:text-gray-300">
                  Archived
                </span>
              )}
              {selectedDmPresence && (
                <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-slate-500 dark:text-gray-400">
                  <span
                    aria-hidden
                    className={cn(
                      'h-2 w-2 rounded-full',
                      selectedDmPresence === 'online'
                        ? 'bg-emerald-500'
                        : selectedDmPresence === 'idle'
                          ? 'bg-amber-400'
                          : 'border border-slate-300 dark:border-gray-600'
                    )}
                  />
                  {PRESENCE_LABEL[selectedDmPresence]}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setMessageSearchOpen(true)}
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Search messages"
                title={`Search messages (${searchShortcutLabel})`}
              >
                <Search className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsChannelsFullscreen((current) => !current)}
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label={isChannelsFullscreen ? 'Exit full screen' : 'Enter full screen'}
                title={isChannelsFullscreen ? 'Exit full screen (F or Esc)' : 'Full screen (F)'}
              >
                {isChannelsFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
              {selected.kind !== 'direct' && (
                <button
                  type="button"
                  onClick={() => setMembersOpen(true)}
                  className="hidden items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-white/10 dark:hover:text-white sm:inline-flex"
                  aria-label="View channel members"
                >
                  <Users className="h-3.5 w-3.5" />
                  {members.length > 0 ? members.length : (selected.memberCount ?? 1)}
                </button>
              )}
              <div ref={channelMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setChannelMenuOpen((o) => !o)}
                  className={cn(
                    'rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-white/10 dark:hover:text-white',
                    channelMenuOpen &&
                      'bg-slate-100 text-slate-800 dark:bg-white/10 dark:text-white'
                  )}
                  aria-label="Channel menu"
                  aria-expanded={channelMenuOpen}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {channelMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+6px)] z-50 w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-xl animate-in fade-in zoom-in-95 duration-150 dark:border-white/10 dark:bg-gray-800"
                  >
                    {isArchivedChannel ? (
                      <>
                        <MenuItem
                          onClick={() => {
                            setDetailsOpen(true);
                            setChannelMenuOpen(false);
                          }}
                        >
                          <Info className="h-3.5 w-3.5" /> Channel details
                        </MenuItem>
                        {selected.currentUserRole === 'owner' && (
                          <MenuItem
                            onClick={() => {
                              handleRestoreChannel(selected);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" /> Restore channel
                          </MenuItem>
                        )}
                      </>
                    ) : selected.kind === 'direct' || selected.kind === 'group_dm' ? (
                      <MenuItem
                        danger
                        onClick={() => {
                          handleCloseDm(selected);
                          setChannelMenuOpen(false);
                        }}
                      >
                        <LogOut className="h-3.5 w-3.5" /> Close conversation
                      </MenuItem>
                    ) : (
                      <>
                        <MenuItem
                          onClick={() => {
                            handleTogglePinChannel(selected);
                            setChannelMenuOpen(false);
                          }}
                        >
                          <span>
                            {pinnedIds.includes(selected._id) ? 'Unpin channel' : 'Pin to top'}
                          </span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setDetailsOpen(true);
                            setChannelMenuOpen(false);
                          }}
                        >
                          <Info className="h-3.5 w-3.5" /> Channel details
                        </MenuItem>
                        {selected.currentUserRole === 'owner' && (
                          <MenuItem
                            onClick={() => {
                              setSettingsOpen(true);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <Settings className="h-3.5 w-3.5" /> Channel settings
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            setMembersOpen(true);
                            setChannelMenuOpen(false);
                          }}
                        >
                          <Users className="h-3.5 w-3.5" />{' '}
                          {selected.currentUserRole === 'owner' ||
                          selected.currentUserRole === 'moderator'
                            ? 'Manage members'
                            : 'View members'}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setPinnedOpen(true);
                            setChannelMenuOpen(false);
                          }}
                        >
                          <Bookmark className="h-3.5 w-3.5" /> Pinned messages
                        </MenuItem>
                        {selected.notificationLevel === 'muted' ? (
                          <MenuItem
                            onClick={() => {
                              handleToggleMuteChannel(selected);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <Volume2 className="h-3.5 w-3.5" /> Unmute channel
                          </MenuItem>
                        ) : (
                          <MenuItem
                            onClick={() => {
                              handleToggleMuteChannel(selected);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <VolumeX className="h-3.5 w-3.5" /> Mute channel
                          </MenuItem>
                        )}
                        {selected.currentUserRole === 'owner' && (
                          <MenuItem
                            onClick={() => {
                              setArchiveTarget(selected);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <Archive className="h-3.5 w-3.5" /> Archive channel
                          </MenuItem>
                        )}
                        <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
                        {isMember ? (
                          <MenuItem
                            danger
                            onClick={() => {
                              handleLeaveChannel(selected);
                              setChannelMenuOpen(false);
                            }}
                          >
                            <LogOut className="h-3.5 w-3.5" /> Leave channel
                          </MenuItem>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Messages */}
          <div className="relative min-h-0 flex-1">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-full overflow-y-auto py-3 chat-scroll"
            >
              {messagesLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary-light/15">
                    <Hash className="h-6 w-6" />
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-800 dark:text-gray-100">
                    Welcome to #{selected.name}!
                  </h3>
                  <p className="mt-1 text-[12px] text-slate-500">
                    This is the beginning of the{' '}
                    <span className="font-semibold text-slate-700 dark:text-gray-300">
                      #{selected.name}
                    </span>{' '}
                    channel.
                  </p>
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  myId={me?._id}
                  people={channelPeople}
                  mentionPeople={channelMemberPeople}
                  canPin={canPinMessages}
                  canModerate={canModerateMessages}
                  readOnly={isArchivedChannel}
                  onSave={handleToggleSaved}
                  onThread={setThreadRoot}
                  onReact={handleReaction}
                  onPin={handlePin}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onVote={handleVotePoll}
                />
              )}
            </div>

            {/* Older-message window opened from search */}
            {isViewingHistory && (
              <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center px-4">
                <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-3 pr-1 text-[11px] text-slate-500 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-150 dark:border-white/10 dark:bg-gray-800 dark:text-gray-400">
                  <span>Viewing older messages</span>
                  <button
                    type="button"
                    onClick={jumpToLatest}
                    className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Jump to latest
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Scroll-to-bottom FAB */}
            {!isViewingHistory && (showScrollBtn || newMessageCount > 0) && (
              <button
                type="button"
                onClick={() => {
                  const el = scrollRef.current;
                  if (el) el.scrollTop = el.scrollHeight;
                  setNewMessageCount(0);
                  setShowScrollBtn(false);
                }}
                className="absolute bottom-4 right-4 flex h-8 min-w-8 items-center justify-center gap-1 rounded-full bg-primary px-2 text-white shadow-lg transition-all hover:opacity-90 animate-in fade-in zoom-in-95 duration-150"
                aria-label={
                  newMessageCount > 0
                    ? `${newMessageCount} new messages. Scroll to bottom`
                    : 'Scroll to bottom'
                }
              >
                <ArrowDown className="h-4 w-4" />
                {newMessageCount > 0 && (
                  <span className="text-[10px] font-bold leading-none">
                    {newMessageCount > 99 ? '99+' : newMessageCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Typing indicator */}
          {isMember && !isArchivedChannel && (
            <TypingIndicator names={typingNames} recordingNames={recordingNames} />
          )}

          {/* Composer is available only to active channel members. */}
          {isArchivedChannel ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-300 bg-slate-50 px-6 py-3.5 dark:border-white/10 dark:bg-gray-950/70">
              <div className="flex min-w-0 items-start gap-2.5">
                <Archive className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">
                    This channel is archived
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-gray-400">
                    {selected.archivedAt
                      ? `Archived on ${formatArchivedDate(selected.archivedAt)} by ${archivedByName(selected)}. `
                      : ''}
                    Messages are read-only.
                  </p>
                </div>
              </div>
              {selected.currentUserRole === 'owner' && (
                <Button
                  onClick={() => handleRestoreChannel(selected)}
                  className="h-8 gap-1.5 px-3 text-[12px]"
                >
                  <ArchiveRestore className="h-3.5 w-3.5" />
                  Restore channel
                </Button>
              )}
            </div>
          ) : isMember ? (
            <Composer
              channel={selected}
              people={channelMemberPeople}
              onSent={isViewingHistory ? jumpToLatest : scrollToOwnMessage}
              allowVoiceNotes={allowVoiceNotes}
            />
          ) : (
            <div className="flex items-center justify-between border-t border-slate-300 bg-slate-50 px-6 py-3.5 dark:border-white/10 dark:bg-gray-950/70">
              <div>
                <p className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">
                  Read-only view of #{selected.name}
                </p>
                <p className="text-[11px] text-slate-500 dark:text-gray-400">
                  Only the channel creator or a moderator can add you back as a member.
                </p>
              </div>
            </div>
          )}
        </main>
      ) : (
        <main className="flex flex-1 items-center justify-center bg-white dark:bg-gray-900">
          <div className="flex flex-col items-center text-center px-8 max-w-sm">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary-light/15">
              <MessageCircle className="h-8 w-8" />
            </div>
            <h2 className="text-[16px] font-bold text-slate-800 dark:text-gray-100">
              {channels.length === 0 ? 'No channels yet' : 'Select a channel'}
            </h2>
            <p className="mt-2 text-[12px] leading-5 text-slate-500 dark:text-gray-400">
              {channels.length === 0
                ? 'Create your first channel to start team communication.'
                : 'Choose a channel from the sidebar to start reading and sending messages.'}
            </p>
            {channels.length === 0 && canCreateChannels && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
              >
                <Plus className="h-4 w-4" /> Create a channel
              </button>
            )}
          </div>
        </main>
      )}

      {/* ── Thread Panel ───────────────────────────────────────── */}
      {threadRoot && selected && (
        <ThreadPanel
          channel={selected}
          root={threadRoot}
          people={channelPeople}
          mentionPeople={channelMemberPeople}
          myId={me?._id}
          isMember={isMember && !isArchivedChannel}
          canPin={canPinMessages}
          canModerate={canModerateMessages}
          focusMessageId={threadFocusId}
          onFocusHandled={handleThreadFocusHandled}
          onSave={isArchivedChannel ? undefined : handleToggleSaved}
          onClose={() => {
            setThreadRoot(null);
            setThreadFocusId(null);
          }}
          onReact={handleReaction}
          onPin={handlePin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onVote={handleVotePoll}
          allowVoiceNotes={allowVoiceNotes}
        />
      )}

      {/* ── Modals ─────────────────────────────────────────────── */}
      <MessageSearchDialog
        open={messageSearchOpen}
        onClose={() => setMessageSearchOpen(false)}
        channels={channels}
        people={channelPeople}
        userId={me?._id}
        onSelect={handleSearchResultSelect}
      />
      <CreateChannelModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <DirectMessageModal
        open={directOpen}
        onClose={() => setDirectOpen(false)}
        onCreated={handleCreated}
      />
      <MembersModal channel={selected} open={membersOpen} onClose={() => setMembersOpen(false)} />
      <ChannelDetailsModal
        channel={selected}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
      {settingsOpen && selected && (
        <ChannelSettingsModal channel={selected} onClose={() => setSettingsOpen(false)} />
      )}
      <PinnedMessagesModal
        channel={selected}
        open={pinnedOpen}
        onClose={() => setPinnedOpen(false)}
      />
      <SavedMessagesDialog
        open={savedOpen}
        onClose={() => setSavedOpen(false)}
        channels={allChannelsForLabels}
        onSelect={handleSavedSelect}
        onUnsave={handleToggleSaved}
      />
      <ConfirmArchiveModal
        channel={archiveTarget}
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleConfirmArchive}
      />
      <ConfirmLeaveModal
        channel={leaveTarget}
        open={leaveModalOpen}
        onClose={() => {
          setLeaveModalOpen(false);
          setLeaveTarget(null);
        }}
        onConfirm={handleConfirmLeave}
        isLoading={removeMember.isPending}
      />
      <ConfirmDeleteMessageModal
        messageId={deleteTargetMessageId}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTargetMessageId(null);
        }}
        onConfirm={handleConfirmDeleteMessage}
        isLoading={deleteMessage.isPending}
      />
    </div>
  );
}

ChannelsPage.displayName = 'ChannelsPage';
